import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, InputGroup, Badge, ProgressBar, Modal, ListGroup, Tabs, Tab } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const InteractivePlanner = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const sectionsRef = useRef([]);
  const parallaxRef = useRef(null);
  const [messages, setMessages] = useState([
    { sender: 'bot', text: 'Hi! I\'m here to help plan your dream Bali wedding. Tell me about your vision and I\'ll create a personalized estimate for you.' },
    /*{ sender: 'bot', text: 'You can try saying things like:\n• "We want a beachfront wedding for 80 guests with premium catering"\n• "Planning an intimate garden ceremony for 30 people"\n• "Looking for a luxury cliff-top venue with high-end photography"' }*/
  ]);
  const [input, setInput] = useState('');
  const [storyStep] = useState(1);
  const [mood] = useState('timeless-elegance');
  const [loading, setLoading] = useState(false);
  // Suggestion chips under chatbot reduced to only core budget/optimization actions (rendered inline below)
  const [isTyping, setIsTyping] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [pulseAnimation, setPulseAnimation] = useState(false);
  // Lightweight action history to avoid undefined errors when logging quick actions
  const [history, setHistory] = useState([]);
  // Proposal modal state
  const [showProposal, setShowProposal] = useState(false);
  const [proposalText, setProposalText] = useState('');
  const [showPackages, setShowPackages] = useState(false);
  // Detailed Plan modal state
  const [showDetailedPlan, setShowDetailedPlan] = useState(false);
  // Flow/progress states
  const [confirmedSelections, setConfirmedSelections] = useState(false);
  
  // ===== SMART BUDGET OPTIMIZER STATE =====
  const [showBudgetOptimizer, setShowBudgetOptimizer] = useState(false);
  const [budgetInput, setBudgetInput] = useState('25000'); // Default $25k
  const [guestCountInput, setGuestCountInput] = useState(80);
  const [venuePreference, setVenuePreference] = useState('any'); // 'beachfront', 'garden', 'villa', 'cliff', 'any'
  const [numberOfDays, setNumberOfDays] = useState(1); // Multi-day wedding support
  const [weddingTheme, setWeddingTheme] = useState('Western'); // Cultural theme
  const [guestsConfirmed, setGuestsConfirmed] = useState(false); // Has user explicitly set guests?
  const [budgetConfirmed, setBudgetConfirmed] = useState(false); // Has user explicitly set budget?
  const [venueConfirmed, setVenueConfirmed] = useState(false); // Has user explicitly set venue?
  const [daysConfirmed, setDaysConfirmed] = useState(false); // Has user explicitly set days?
  const [themeConfirmed, setThemeConfirmed] = useState(false); // Has user explicitly set theme?
  const [dreamDescription, setDreamDescription] = useState(''); // User's dream wedding description for LLM parsing
  const [isParsingVision, setIsParsingVision] = useState(false); // Loading state for LLM parsing
  const [visionParsed, setVisionParsed] = useState(false); // Has vision been analyzed
  const [aiSuggestions, setAiSuggestions] = useState(null); // AI-extracted preferences
  const [optimizerStep, setOptimizerStep] = useState(0); // 0: package selection, 1: chat, 2: analyzing, 3: results
  const [selectedStartingPackage, setSelectedStartingPackage] = useState(null); // Track which package user started with
  const [selectedVenueId, setSelectedVenueId] = useState(null); // Track specific venue chosen by user in chat
  const [optimizedPlan, setOptimizedPlan] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [optimizerPriorities, setOptimizerPriorities] = useState({
    venue: 3,
    catering: 3,
    photography: 3,
    decoration: 2,
    entertainment: 2
  });
  // Chat-first AI state
  const [optimizerChat, setOptimizerChat] = useState([
    { role: 'assistant', content: "Hi! I'm your AI wedding planner ✨ Tell me about your dream Bali wedding — the vibe, guest count, budget, anything that excites you!" }
  ]);
  const [optimizerChatInput, setOptimizerChatInput] = useState('');
  const [isOptimizerChatLoading, setIsOptimizerChatLoading] = useState(false);
  const optimizerChatRef = useRef(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingPreferences, setPendingPreferences] = useState(null);
  const [isTypewriterActive, setIsTypewriterActive] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const [typewriterFullText, setTypewriterFullText] = useState('');
  const [chatFadeIn, setChatFadeIn] = useState(false);
  
  // Typewriter effect for package descriptions
  useEffect(() => {
    if (!isTypewriterActive || !typewriterFullText) return;
    
    let currentIndex = 0;
    const speed = 12; // ms per character - fast but readable
    
    const typeInterval = setInterval(() => {
      if (currentIndex < typewriterFullText.length) {
        setTypewriterText(typewriterFullText.substring(0, currentIndex + 1));
        currentIndex++;
        // Auto-scroll while typing
        if (optimizerChatRef.current) {
          optimizerChatRef.current.scrollTop = optimizerChatRef.current.scrollHeight;
        }
      } else {
        clearInterval(typeInterval);
        setIsTypewriterActive(false);
        // Append the full message to existing chat (not replace)
        setOptimizerChat(prev => {
          // If the last message is already this exact text, don't duplicate
          if (prev.length > 0 && prev[prev.length - 1].content === typewriterFullText) return prev;
          return [...prev, { role: 'assistant', content: typewriterFullText }];
        });
        setTypewriterText('');
        setTypewriterFullText('');
      }
    }, speed);
    
    return () => clearInterval(typeInterval);
  }, [isTypewriterActive, typewriterFullText]);
  
  // Auto-scroll optimizer chat when messages change
  useEffect(() => {
    if (optimizerChatRef.current) {
      optimizerChatRef.current.scrollTop = optimizerChatRef.current.scrollHeight;
    }
  }, [optimizerChat, isOptimizerChatLoading, showConfirmation]);
  
  const [proposalGenerated, setProposalGenerated] = useState(false);
  const [contacted, setContacted] = useState(false);
  // Venue catalog & compare
  const [showVenueCatalog, setShowVenueCatalog] = useState(false);
  const [venueFilters, setVenueFilters] = useState({ vibe: 'any', minGuests: 20, maxGuests: 300, tier: 'any', rainPlan: false, accessible: false });
  const venues = [
    { id: 'villa-aurora', name: 'Villa Aurora', vibe: 'villa', capacity: 120, basePrice: 2800, labels: ['intimate','sunset'], availability: 'High', img: '/images/venue.jpeg', notes: ['Noise curfew 10pm','Service charge 10%'] },
    { id: 'cliff-sanctuary', name: 'Cliff Sanctuary', vibe: 'cliff', capacity: 200, basePrice: 3200, labels: ['grand','sunset'], availability: 'Medium', img: '/images/hawaii.jpg', notes: ['Windy in July','Minimum spend on decor'] },
    { id: 'garden-serenity', name: 'Garden Serenity', vibe: 'garden', capacity: 80, basePrice: 1800, labels: ['intimate','rainy season friendly'], availability: 'Low', img: '/images/flowers.jpeg', notes: ['Backup tent available','ADA friendly paths'] },
    { id: 'beach-luna', name: 'Beach Luna', vibe: 'beach', capacity: 150, basePrice: 2500, labels: ['sunset','grand'], availability: 'Medium', img: '/images/baliExperience.jpg', notes: ['Sand access','Generator backup'] }
  ];
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  
  const [estimate, setEstimate] = useState({
    guests: 50,
    venue: 'beachfront',
    catering: 'standard',
    decoration: 'standard',
    photography: 'standard',
    entertainment: 'standard'
  });
  
  const [totalCost, setTotalCost] = useState(0);
  const [tier, setTier] = useState('signature');
  // CTA visibility when reaching end of Detailed Plan
  const [showBottomCTA, setShowBottomCTA] = useState(false);
  // CTA visibility for main page scroll
  const [showPageBottomCTA, setShowPageBottomCTA] = useState(false);
  // Contact modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '' });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  // Pre-chat must-have picks
  const [mustHave, setMustHave] = useState({ budget: null, venue: null, guests: null, catering: null, tier: null });
  const mustHaveComplete = Boolean(mustHave.budget && mustHave.venue && mustHave.guests && mustHave.catering && mustHave.tier);
  // Prevent multiple auto-sends
  const [mustHaveSent, setMustHaveSent] = useState(false);
  // Lightbox state for testimony images
  const [lightboxImg, setLightboxImg] = useState(null);
  const [lightboxInfo, setLightboxInfo] = useState({ name: '', quote: '' });
  // Video lightbox state
  const [videoModal, setVideoModal] = useState(null);
  const [videoInfo, setVideoInfo] = useState({ name: '', quote: '' });
  const hasStarted = Boolean(mustHave.budget || mustHave.venue || mustHave.guests || mustHave.catering || mustHave.tier);
  // Compute progress across 8 steps
  const steps = [
    { key: 'budget', label: 'Select budget', done: Boolean(mustHave.budget) },
    { key: 'venue', label: 'Select venue', done: Boolean(mustHave.venue) },
    { key: 'guests', label: 'Select guests', done: Boolean(mustHave.guests) },
    { key: 'catering', label: 'Select catering', done: Boolean(mustHave.catering) },
    { key: 'tier', label: 'Select tier', done: Boolean(mustHave.tier) },
  { key: 'confirm', label: 'Generate detailed plan', done: confirmedSelections },
    { key: 'contact', label: 'Contact team', done: contacted }
  ];
  const stepsCompleted = steps.filter(s => s.done).length;
  const progressPct = Math.round((stepsCompleted / steps.length) * 100);
  const nextIncomplete = steps.find(s => !s.done);
  const nextStep = nextIncomplete?.label || 'Contact us';
  const nextStepKey = nextIncomplete?.key || 'contact';

  const scrollToStep = (key) => {
    if (key === 'confirm') {
      setConfirmedSelections(true);
      setShowDetailedPlan(true);
      return;
    }
    if (key === 'contact') {
      // Open the contact modal with options
      setShowContactModal(true);
      return;
    }
    const idMap = {
      budget: 'step-budget',
      venue: 'step-venue',
      guests: 'step-guests',
      catering: 'step-catering',
      tier: 'step-tier',
      contact: 'page-cta-anchor'
    };
    const targetId = idMap[key] || 'planner-root';
    const el = document.getElementById(targetId);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Observe end-of-page to reveal global CTA
  useEffect(() => {
    const anchor = document.getElementById('page-cta-anchor');
    if (!anchor) return;
    const io = new IntersectionObserver((entries) => {
      const entry = entries[0];
      setShowPageBottomCTA(entry.isIntersecting);
    }, { root: null, threshold: 0.1 });
    io.observe(anchor);
    return () => io.disconnect();
  }, []);

  // Check for edit mode - restore chat history if coming from wedding plan
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit') === 'true') {
      const savedPlan = localStorage.getItem('weddingPlan');
      if (savedPlan) {
        try {
          const planData = JSON.parse(savedPlan);
          // Restore chat history
          if (planData.optimizerChat && planData.optimizerChat.length > 0) {
            setOptimizerChat(planData.optimizerChat);
          }
          // Restore settings
          if (planData.venuePreference) { setVenuePreference(planData.venuePreference); setVenueConfirmed(true); }
          if (planData.budgetInput) { setBudgetInput(planData.budgetInput); setBudgetConfirmed(true); }
          if (planData.guestCountInput) { setGuestCountInput(planData.guestCountInput); setGuestsConfirmed(true); }
          if (planData.optimizerPriorities) setOptimizerPriorities(planData.optimizerPriorities);
          if (planData.dreamDescription) setDreamDescription(planData.dreamDescription);
          if (planData.aiSuggestions) setAiSuggestions(planData.aiSuggestions);
          if (planData.numberOfDays) { setNumberOfDays(planData.numberOfDays); setDaysConfirmed(true); }
          if (planData.weddingTheme) { setWeddingTheme(planData.weddingTheme); setThemeConfirmed(true); }
          
          // Restore confirmation state so user sees their choices
          setPendingPreferences({
            venue: planData.venuePreference || 'any',
            guests: planData.guests || planData.guestCountInput || 50,
            budget: planData.budget || planData.budgetInput || 25000,
            priorities: planData.optimizerPriorities || {},
            summary: planData.dreamDescription || '',
            days: planData.numberOfDays || 1,
            theme: planData.weddingTheme || 'Western'
          });
          setShowConfirmation(true);
          
          // Skip package selection, go directly to chat with confirmation
          setOptimizerStep(1);
          
          // Open the optimizer modal
          setShowBudgetOptimizer(true);
        } catch (e) {
          console.error('Error restoring plan data:', e);
        }
      }
    }
  }, []);

  // ===== SMART BUDGET OPTIMIZER - VENDOR DATABASE =====
  // ===== REAL VENDOR DATABASE — Sourced from BALIVIP Vendor Sheets (May 2026) =====
  const vendorDatabase = {
    venue: [
      // Essential — Anantara Uluwatu (cliff front, Uluwatu)
      { id: 'anantara-intimate', name: 'Anantara Uluwatu – Intimate Celebration', venueType: 'resort', tier: 'essential', price: 2148, additionalDayRate: 0.8, includesDays: 1, capacity: 20, image: '/images/venues/anantara/1.jpg', gallery: ['/images/venues/anantara/2.webp', '/images/venues/anantara/3.jpg', '/images/venues/anantara/4.jpg'], features: ['Dewa Dewi Chapel', 'Floral decor', 'Celebrant & certificate', 'Sound system', 'Welcome drinks for 20'], vibe: 'cliff', location: 'Uluwatu', website: 'https://www.anantara.com/en/uluwatu-bali' },
      { id: 'anantara-dewadewi', name: 'Anantara Uluwatu – Dewa Dewi Celebration', venueType: 'resort', tier: 'essential', price: 3483, additionalDayRate: 0.8, includesDays: 1, capacity: 20, image: '/images/venues/anantara/5.jpg', gallery: ['/images/venues/anantara/6.webp', '/images/venues/anantara/7.webp'], features: ['Chapel ceremony', 'Honeymoon suite 2 nights', 'Spa treatment', 'Candlelit dinner', 'Champagne'], vibe: 'cliff', location: 'Uluwatu', website: 'https://www.anantara.com/en/uluwatu-bali' },
      // Signature — The Laguna Resort (beachfront, Nusa Dua)
      { id: 'laguna-intimate', name: 'The Laguna – Timeless Intimate Wedding', venueType: 'resort', tier: 'signature', price: 9855, additionalDayRate: 0.5, includesDays: 3, capacity: 30, image: '/images/venues/laguna/1.webp', gallery: ['/images/venues/laguna/2.webp', '/images/venues/laguna/3.webp', '/images/venues/laguna/4.webp'], features: ['Beach or garden ceremony', 'Violin or acoustic trio', 'Buffet dinner', '3-night stay', 'Wedding cake', 'Rindik music'], vibe: 'beachfront', location: 'Nusa Dua' },
      { id: 'laguna-ceremony-dinner', name: 'The Laguna – Ceremony & Dinner Reception', venueType: 'resort', tier: 'signature', price: 12306, additionalDayRate: 0.5, includesDays: 3, capacity: 50, image: '/images/venues/laguna/5.jpg', gallery: ['/images/venues/laguna/6.webp', '/images/venues/laguna/7.webp'], features: ['Beach or garden venue', 'Acoustic trio', '4-course dinner or buffet', '3-night stay', 'Sparkling wine', 'Full decoration'], vibe: 'beachfront', location: 'Nusa Dua' },
      // Signature — Pandawa Cliff Estate (cliff villa, Uluwatu)
      { id: 'pandawa-pala', name: 'Pandawa Cliff Estate – Villa Pala', venueType: 'villa', tier: 'signature', price: 1795, additionalDayRate: 1.0, includesDays: 2, capacity: 50, image: '/images/venues/pandawa/1.jpg', gallery: ['/images/venues/pandawa/2.jpg', '/images/venues/pandawa/3.jpg', '/images/venues/pandawa/4.jpg'], features: ['Cliff-top villa', 'Ocean panorama', 'Private estate', 'Min 2 nights'], vibe: 'cliff', location: 'Uluwatu' },
      { id: 'pandawa-combined', name: 'Pandawa Cliff Estate – Combined Villas', venueType: 'villa', tier: 'signature', price: 6813, additionalDayRate: 1.0, includesDays: 3, capacity: 250, image: '/images/venues/pandawa/5.jpg', gallery: ['/images/venues/pandawa/6.jpg', '/images/venues/pandawa/7.webp', '/images/venues/pandawa/8.jpg'], features: ['Whole estate buyout', '250 guest capacity', 'Multiple venue areas', 'Min 3 nights'], vibe: 'cliff', location: 'Uluwatu' },
      // Signature — Jeeva Saba (rice field villa, Gianyar)
      { id: 'jeeva-saba', name: 'Jeeva Saba – Rice Field Villa', venueType: 'villa', tier: 'signature', price: 6129, additionalDayRate: 0.5, includesDays: 2, capacity: 16, image: '/images/venues/jeeva-saba/1.jpg', gallery: ['/images/venues/jeeva-saba/2.webp', '/images/venues/jeeva-saba/3.jpg', '/images/venues/jeeva-saba/4.jpg'], features: ['8-bedroom villa', 'Rice field views', 'Balinese massage for couple', 'Breakfast included', 'Honeymoon setup'], vibe: 'garden', location: 'Gianyar' },
      // Prestige — AYANA Resort (cliff front, Jimbaran)
      { id: 'ayana-kisik', name: 'AYANA – Sweet Escape at Kisik Pier', venueType: 'resort', tier: 'prestige', price: 11896, additionalDayRate: 0.5, includesDays: 2, capacity: 30, image: '/images/venues/ayana/1.jpg', gallery: ['/images/venues/ayana/2.jpg', '/images/venues/ayana/3.jpg', '/images/venues/ayana/4.jpg'], features: ['Kisik Pier ceremony', 'Seafood dinner', '2-night stay (10 rooms)', 'Cocktail reception', 'Wedding consultant'], vibe: 'cliff', location: 'Jimbaran' },
      { id: 'ayana-tresna', name: 'AYANA – Tresna Chapel Dream Wedding', venueType: 'resort', tier: 'prestige', price: 16854, additionalDayRate: 0.5, includesDays: 2, capacity: 50, image: '/images/venues/ayana/5.jpg', gallery: ['/images/venues/ayana/6.jpg', '/images/venues/ayana/7.jpg', '/images/venues/ayana/8.jpg'], features: ['Tresna Chapel', 'Acapella choralist', '2-night stay (15 rooms)', 'Buffet or plated dinner', 'Fairy lights'], vibe: 'cliff', location: 'Jimbaran' },
      { id: 'ayana-sky', name: 'AYANA – Sky Is The Limit', venueType: 'resort', tier: 'prestige', price: 29713, additionalDayRate: 0.5, includesDays: 2, capacity: 100, image: '/images/venues/ayana/9.jpg', gallery: ['/images/venues/ayana/10.jpg', '/images/venues/ayana/11.jpg'], features: ['Sky venue', 'Ocean views', 'Large capacity', '2-night accommodation', 'Full coordination'], vibe: 'cliff', location: 'Jimbaran' },
      // Prestige — The Mulia Resort (ocean view, Nusa Dua)
      { id: 'mulia-glasshouse', name: 'The Mulia – Glass House Ocean View', venueType: 'resort', tier: 'prestige', price: 21671, additionalDayRate: 0.5, includesDays: 2, capacity: 100, image: '/images/venues/mulia/1.webp', gallery: ['/images/venues/mulia/2.jpg', '/images/venues/mulia/3.jpg'], features: ['Mulia Glass House', 'Ocean view', 'Earl Suite 2 nights', 'Afternoon tea & cocktails'], vibe: 'beachfront', location: 'Nusa Dua', website: 'https://themulia.com' },
      { id: 'mulia-unity', name: 'The Mulia – Unity Garden Ocean View', venueType: 'resort', tier: 'prestige', price: 40516, additionalDayRate: 0.5, includesDays: 2, capacity: 100, image: '/images/venues/mulia/4.jpg', gallery: ['/images/venues/mulia/5.jpg'], features: ['Eternity glass venue', '6 Royal Suites', '10 Grandeur rooms', 'Food credit included'], vibe: 'beachfront', location: 'Nusa Dua', website: 'https://themulia.com' },
      // Prestige — The Stones Villas (cliff/ocean, Uluwatu)
      { id: 'stones-queen', name: 'The Stones Villas – Queen Package', venueType: 'villa', tier: 'prestige', price: 17273, additionalDayRate: 0.5, includesDays: 2, capacity: 50, image: '/images/venues/stones/1.jpg', gallery: ['/images/venues/stones/2.jpg', '/images/venues/stones/3.jpg', '/images/venues/stones/4.jpg'], features: ['3 days 2 nights', 'Cliff/ocean view', 'Venue usage included', 'Eco-luxury'], vibe: 'cliff', location: 'Uluwatu', themes: ['Balinese', 'Bohemian'] },
      // Ultra — Amankila (ocean view, Karangasem)
      { id: 'amankila-intimate', name: 'Amankila – Intimate Suite Wedding', venueType: 'resort', tier: 'ultra', price: 58000, additionalDayRate: 0.5, includesDays: 2, capacity: 50, image: '/images/venues/amankila/1.jpg', gallery: ['/images/venues/amankila/2.jpg', '/images/venues/amankila/3.jpg', '/images/venues/amankila/4.avif'], features: ['Amankila Suite', '10 suites 2 nights', '4-course dinner', '$1,000 spa credit', 'Water sports', 'Airport transfers'], vibe: 'beachfront', location: 'Karangasem' },
      { id: 'amankila-exclusive', name: 'Amankila – Two-Night Exclusive Wedding', venueType: 'resort', tier: 'ultra', price: 163500, additionalDayRate: 0.5, includesDays: 2, capacity: 50, image: '/images/venues/amankila/5.jpeg', gallery: ['/images/venues/amankila/6.jpg', '/images/venues/amankila/7.jpg'], features: ['Exclusive resort buyout', '31 suites', '$30,000 F&B credit', '$2,060 spa credit', 'Full privacy'], vibe: 'beachfront', location: 'Karangasem' },
      // Ultra — The Apurva Kempinski (cliff front, Nusa Dua)
      { id: 'kempinski-50', name: 'The Apurva Kempinski – 50 Guests', venueType: 'resort', tier: 'ultra', price: 31220, additionalDayRate: 0.5, includesDays: 2, capacity: 50, image: '/images/venues/kempinski/1.JPG', gallery: ['/images/venues/kempinski/2.JPG', '/images/venues/kempinski/3.JPG', '/images/venues/kempinski/4.JPG'], features: ['Ocean front chapel', 'Tea Pai ceremony room', 'Violin & cello', 'Spa treatment', '14 rooms 2 nights'], vibe: 'cliff', location: 'Nusa Dua' },
      { id: 'kempinski-100', name: 'The Apurva Kempinski – 100 Guests', venueType: 'resort', tier: 'ultra', price: 38806, additionalDayRate: 0.5, includesDays: 2, capacity: 100, image: '/images/venues/kempinski/5.jpg', gallery: ['/images/venues/kempinski/6.webp', '/images/venues/kempinski/7.jpg', '/images/venues/kempinski/8.jpg'], features: ['Ocean front chapel', 'Buffet or 3-course dinner', 'Cocktail reception', '14 rooms 2 nights'], vibe: 'cliff', location: 'Nusa Dua' },
      { id: 'kempinski-200', name: 'The Apurva Kempinski – 200 Guests', venueType: 'resort', tier: 'ultra', price: 53978, additionalDayRate: 0.5, includesDays: 2, capacity: 200, image: '/images/venues/kempinski/9.jpg', gallery: ['/images/venues/kempinski/10.jpg', '/images/venues/kempinski/11.jpg'], features: ['Ocean front chapel & ballroom', 'Grand celebration', '14 rooms 2 nights'], vibe: 'cliff', location: 'Nusa Dua' },
      // Ultra — The Ungasan (clifftop villas, Uluwatu)
      { id: 'ungasan-everlasting', name: 'The Ungasan – Everlasting Love', venueType: 'villa', tier: 'ultra', price: 29936, additionalDayRate: 0.5, includesDays: 2, capacity: 30, image: '/images/venues/ungasan/1.jpg', gallery: ['/images/venues/ungasan/2.jpg', '/images/venues/ungasan/3.jpg', '/images/venues/ungasan/4.jpg'], features: ['3 private villas', 'Butler service', 'Spa for couple', 'Sundays Beach Club access', 'Airport transfers'], vibe: 'cliff', location: 'Uluwatu', themes: ['Western', 'Bohemian'] },
      { id: 'ungasan-newbeginning', name: 'The Ungasan – The New Beginning', venueType: 'villa', tier: 'ultra', price: 44933, additionalDayRate: 0.5, includesDays: 2, capacity: 50, image: '/images/venues/ungasan/5.jpg', gallery: ['/images/venues/ungasan/6.jpg', '/images/venues/ungasan/7.jpg', '/images/venues/ungasan/8.webp'], features: ['5 five-bedroom villas', 'Butler service', 'Cliff-top ceremony', 'Beach club access'], vibe: 'cliff', location: 'Uluwatu', themes: ['Western', 'Bohemian'] },
      // Ultra — Alila Villas Uluwatu (cliff top)
      { id: 'alila-cliffedge', name: 'Alila Villas Uluwatu – Cliff Edge Wedding', venueType: 'villa', tier: 'ultra', price: 10600, additionalDayRate: 0.5, includesDays: 2, capacity: 20, image: '/images/venues/alila/1.jpg', gallery: ['/images/venues/alila/2.jpg', '/images/venues/alila/3.jpg', '/images/venues/alila/4.jpg'], features: ['Cliff Temple Terrace', '3-course set menu', 'One-bedroom Pool Villa 2 nights', 'Spa Alila treatment'], vibe: 'cliff', location: 'Uluwatu', themes: ['Western', 'Indian', 'Balinese', 'Chinese'] },
      { id: 'alila-villalaw', name: 'Alila Villas Uluwatu – Cliff Villa & Cabana', venueType: 'villa', tier: 'ultra', price: 28031, additionalDayRate: 0.5, includesDays: 2, capacity: 50, image: '/images/venues/alila/5.jpg', gallery: ['/images/venues/alila/6.jpg', '/images/venues/alila/7.jpg'], features: ['Cliff-top cabana', '3-course dinner', '3-bedroom villa 2 nights', 'Trio musicians'], vibe: 'cliff', location: 'Uluwatu' },
      // Ultra — Khayangan Estate (beachfront, Uluwatu)
      { id: 'khayangan-estate', name: 'Khayangan Estate', venueType: 'villa', tier: 'ultra', price: 28962, additionalDayRate: 0.5, includesDays: 2, capacity: 250, image: '/images/venues/khayangan/1.jpg', gallery: ['/images/venues/khayangan/2.avif', '/images/venues/khayangan/3.jpg', '/images/venues/khayangan/4.jpg'], features: ['Private peninsula', 'Multiple venues', 'Helicopter access', 'Celebrity favorite'], vibe: 'beachfront', location: 'Uluwatu', themes: ['any'] }
    ],
    catering: [
      // Essential — Bali Indian Food
      { id: 'bali-indian-veg-a', name: 'Bali Indian Food – Vegetarian Menu A', tier: 'essential', pricePerHead: 11.76, additionalDayRate: 1.0, includesDays: 1, image: '/images/food.jpeg', features: ['Vegetarian', 'Tandoori', 'Curry spread'], style: 'Indian Vegetarian', themes: ['Indian', 'Middle Eastern'] },
      { id: 'bali-indian-nonveg-c', name: 'Bali Indian Food – Non-Veg Menu C', tier: 'essential', pricePerHead: 20.57, additionalDayRate: 1.0, includesDays: 1, image: '/images/food.jpeg', features: ['Non-vegetarian', 'Biryani', 'Tandoori', 'Full spread'], style: 'Indian Non-Veg', themes: ['Indian', 'Middle Eastern'] },
      // Signature — Bali Catering Company (BCC)
      { id: 'bcc-set3', name: 'Bali Catering Company – 3-Course Set Menu', tier: 'signature', pricePerHead: 37.91, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/bcc/1.jpg', gallery: ['/images/vendors/bcc/2.jpg', '/images/vendors/bcc/3.jpg', '/images/vendors/bcc/4.jpg'], features: ['Starter + main + dessert', 'Standard or Asian option', 'Professional service'], style: 'Set Menu' },
      { id: 'bcc-set4', name: 'Bali Catering Company – 4-Course Set Menu', tier: 'signature', pricePerHead: 43.74, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/bcc/5.jpg', gallery: ['/images/vendors/bcc/6.jpg', '/images/vendors/bcc/7.jpg'], features: ['Cold starter + hot starter + main + dessert', 'Premium plating'], style: 'Fine Dining' },
      { id: 'bcc-buffet-intl', name: 'Bali Catering Company – International Buffet', tier: 'signature', pricePerHead: 43.74, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/bcc/8.jpg', gallery: ['/images/vendors/bcc/9.jpg', '/images/vendors/bcc/10.png'], features: ['Western, BBQ, or international', 'Live stations', 'Full service'], style: 'Buffet' },
      { id: 'bcc-buffet-indo', name: 'Bali Catering Company – Indonesian Buffet', tier: 'signature', pricePerHead: 40.82, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/bcc/11.png', features: ['Indonesian style buffet', 'Local favorites'], style: 'Indonesian Buffet' },
      { id: 'bcc-family', name: 'Bali Catering Company – Family Style', tier: 'signature', pricePerHead: 40.82, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/bcc/12.png', features: ['Shared platters', 'Communal dining', 'Warm atmosphere'], style: 'Family Style' },
      // Prestige — Amazing Bali Catering (ABC)
      { id: 'abc-set5', name: 'Amazing Bali Catering – 5-Course Set Menu', tier: 'prestige', pricePerHead: 46.65, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/abc-catering/1.jpg', gallery: ['/images/vendors/abc-catering/2.jpg', '/images/vendors/abc-catering/3.jpg'], features: ['5-course degustation', 'Premium ingredients', 'Sommelier service'], style: 'Fine Dining' },
      { id: 'abc-bbq', name: 'Amazing Bali Catering – BBQ Buffet', tier: 'prestige', pricePerHead: 46.65, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/abc-catering/4.jpg', gallery: ['/images/vendors/abc-catering/5.jpg', '/images/vendors/abc-catering/6.jpg'], features: ['BBQ buffet', 'Live grilling', 'Premium meats'], style: 'BBQ Luxury' },
      // Prestige — Lumbung Catering
      { id: 'lumbung-signature', name: 'Lumbung Catering – Signature Buffet', tier: 'prestige', pricePerHead: 49.57, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/lumbung/1.jpeg', gallery: ['/images/vendors/lumbung/2.jpeg', '/images/vendors/lumbung/3.jpeg'], features: ['Signature buffet', 'East meets West', 'Premium selection'], style: 'Signature Buffet' },
      { id: 'lumbung-set4', name: 'Lumbung Catering – 4-Course Set Menu', tier: 'prestige', pricePerHead: 44.00, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/lumbung/4.jpeg', gallery: ['/images/vendors/lumbung/5.jpeg', '/images/vendors/lumbung/6.jpeg'], features: ['Appetizer + soup + main + dessert', 'Elegant plating'], style: 'Fine Dining' },
      // Ultra — Bali Miracle
      { id: 'miracle-set4', name: 'Bali Miracle – 4-Course Set Menu', tier: 'ultra', pricePerHead: 46.54, additionalDayRate: 0.7, includesDays: 1, image: '/images/food.jpeg', features: ['4-course set', 'Free-flow non-alcohol beverages', 'Premium presentation'], style: 'Ultra Dining' },
      { id: 'miracle-platinum', name: 'Bali Miracle – Platinum Buffet', tier: 'ultra', pricePerHead: 41.99, additionalDayRate: 0.7, includesDays: 1, image: '/images/food.jpeg', features: ['3 appetizers', '2 live stations', '3 mains', 'Dessert station', 'Free-flow beverages'], style: 'Platinum Buffet' }
    ],
    photography: [
      // Essential — Sriwijayastory (Photo Only)
      { id: 'sri-photo-8h', name: 'Sriwijayastory – Photo 8 Hours', tier: 'essential', price: 647, additionalDayRate: 0.8, includesDays: 1, image: '/images/pic.jpeg', features: ['2 photographers', '250 edited photos', 'All original JPG files'], deliverables: '250 photos' },
      { id: 'sri-photo-12h', name: 'Sriwijayastory – Photo 12 Hours', tier: 'essential', price: 883, additionalDayRate: 0.8, includesDays: 1, image: '/images/pic.jpeg', features: ['2 photographers', '300 edited photos', 'All original JPG files'], deliverables: '300 photos' },
      // Essential — Sriwijayastory (Photo + Video Combo)
      { id: 'sri-combo-8h', name: 'Sriwijayastory – Photo & Video 8 Hours', tier: 'essential', price: 1353, additionalDayRate: 0.8, includesDays: 1, image: '/images/pic.jpeg', features: ['2 photographers + 2 videographers', '250 edited photos', 'Cinematic 3-5 min', 'Full documentary', 'Free drone'], deliverables: '250 photos + cinematic video' },
      { id: 'sri-combo-16h', name: 'Sriwijayastory – Photo & Video 16 Hours', tier: 'essential', price: 2295, additionalDayRate: 0.8, includesDays: 1, image: '/images/pic.jpeg', features: ['3 photographers + 3 videographers', '350 edited photos', 'Cinematic 3-5 min', 'Full documentary', 'Free drone'], deliverables: '350 photos + cinematic video' },
      // Signature — Venema (Photo + Video Combo)
      { id: 'venema-combo-8h', name: 'Venema – Combo 8 Hours', tier: 'signature', price: 2236, additionalDayRate: 0.7, includesDays: 1, image: '/images/pic.jpeg', features: ['2 photographers + 2 videographers', '250 edited photos', 'Teaser + extended highlights'], deliverables: '250 photos + highlight video' },
      { id: 'venema-combo-fullday', name: 'Venema – Full Day Combo', tier: 'signature', price: 3178, additionalDayRate: 0.7, includesDays: 1, image: '/images/pic.jpeg', features: ['2 photographers + 3 videographers', '450 edited photos', 'Teaser + full speeches'], deliverables: '450 photos + full video' },
      // Signature — Flipmax Photography
      { id: 'flipmax-aditya-8h', name: 'Flipmax – Photo by Aditya 8 Hours', tier: 'signature', price: 1177, additionalDayRate: 0.7, includesDays: 1, image: '/images/vendors/flipmax/1.jpg', gallery: ['/images/vendors/flipmax/2.jpg', '/images/vendors/flipmax/3.jpg', '/images/vendors/flipmax/4.jpg'], features: ['2 photographers', '300 edited images', 'Online gallery'], deliverables: '300 photos' },
      // Prestige — IMAJ (Photo + Video Combo)
      { id: 'imaj-combo-8h', name: 'IMAJ – Photo & Video 8 Hours', tier: 'prestige', price: 1854, additionalDayRate: 0.6, includesDays: 1, image: '/images/vendors/imaj/1.png', gallery: ['/images/vendors/imaj/2.png', '/images/vendors/imaj/3.png'], features: ['700 edited photos', 'Film + lab scan', 'All master files', 'Online gallery'], deliverables: '700 photos + film' },
      { id: 'imaj-combo-12h', name: 'IMAJ – Photo & Video 12 Hours', tier: 'prestige', price: 2383, additionalDayRate: 0.6, includesDays: 1, image: '/images/vendors/imaj/4.png', gallery: ['/images/vendors/imaj/5.png', '/images/vendors/imaj/6.png'], features: ['1000 edited photos', '300 film scans', 'Online gallery'], deliverables: '1000 photos + film' },
      // Prestige — Diktat Photography (Photo + Video)
      { id: 'diktat-combo-8h', name: 'Diktat – Photo & Video 8 Hours', tier: 'prestige', price: 2295, additionalDayRate: 0.6, includesDays: 1, image: '/images/vendors/diktat/1.jpeg', gallery: ['/images/vendors/diktat/2.jpeg', '/images/vendors/diktat/3.jpeg'], features: ['2 photographers + 2 videographers', '300 edited photos', 'Cinematic clip + 30-min movie', 'USB delivery'], deliverables: '300 photos + cinematic movie' },
      { id: 'diktat-combo-12h', name: 'Diktat – Photo & Video 12 Hours', tier: 'prestige', price: 2942, additionalDayRate: 0.6, includesDays: 1, image: '/images/vendors/diktat/4.jpeg', gallery: ['/images/vendors/diktat/5.jpeg', '/images/vendors/diktat/6.jpeg'], features: ['2 photographers + 2 videographers', '400 edited photos', 'Cinematic clip + 30-min movie'], deliverables: '400 photos + cinematic movie' },
      // Prestige — Perpetual
      { id: 'perpetual-photo-full', name: 'Perpetual – Full Day Photo', tier: 'prestige', price: 1765, additionalDayRate: 0.6, includesDays: 1, image: '/images/pic.jpeg', features: ['Up to 16 hours', '2 photographers + assistant', '300 edited photos'], deliverables: '300 photos' },
      { id: 'perpetual-video-full', name: 'Perpetual – Full Day Video', tier: 'prestige', price: 2177, additionalDayRate: 0.6, includesDays: 1, image: '/images/pic.jpeg', features: ['Up to 16 hours', '2 videographers + assistant', 'SDE video + 35-min full video', 'Drone'], deliverables: 'SDE + full video' },
      // Ultra — Bali Pixtura
      { id: 'pixtura-dayu-8h', name: 'Bali Pixtura – Dayu 8 Hours', tier: 'ultra', price: 3413, additionalDayRate: 0.5, includesDays: 1, image: '/images/pic.jpeg', features: ['2 photographers + 3 videographers', '800 edited photos + 150 sneak peek', '1-min IG video + 4-min highlight', '30-min documentary', 'Drone'], deliverables: '800 photos + full video package' },
      { id: 'pixtura-dayu-12h', name: 'Bali Pixtura – Dayu 12 Hours', tier: 'ultra', price: 4266, additionalDayRate: 0.5, includesDays: 1, image: '/images/pic.jpeg', features: ['2 photographers + 3 videographers', '1200 edited photos', '5-min highlight + 40-min documentary', 'Drone'], deliverables: '1200 photos + full video' },
      // Ultra — AXIOO
      { id: 'axioo-photo-adi', name: 'AXIOO – Photography by Adi', tier: 'ultra', price: 3060, additionalDayRate: 0.5, includesDays: 1, image: '/images/vendors/axioo/1.jpg', gallery: ['/images/vendors/axioo/2.jpg', '/images/vendors/axioo/3.jpg', '/images/vendors/axioo/4.jpg'], features: ['16 hours', '2 photographers', '200 edited photos', 'USB delivery'], deliverables: '200 premium photos' },
      { id: 'axioo-video-robb', name: 'AXIOO – Videography Trailer by Robb', tier: 'ultra', price: 2942, additionalDayRate: 0.5, includesDays: 1, image: '/images/vendors/axioo/5.jpg', gallery: ['/images/vendors/axioo/6.jpg', '/images/vendors/axioo/7.jpg'], features: ['16 hours', '3 videographers', 'Same-day edit (3 min)', 'Movie (15-20 min)'], deliverables: 'SDE + cinematic movie' }
    ],
    decoration: [
      // Essential — BALIVIP Styling
      { id: 'balivip-style-50-ess', name: 'BALIVIP Styling – 50 Guests', tier: 'essential', price: 4647, additionalDayRate: 0.2, includesDays: 1, image: '/images/flowers.jpeg', features: ['Full venue styling', 'Floral arrangements', 'Table setup', 'Ceremony arch'], style: 'Tropical Elegant' },
      { id: 'balivip-style-100-ess', name: 'BALIVIP Styling – 100 Guests', tier: 'essential', price: 5809, additionalDayRate: 0.2, includesDays: 1, image: '/images/flowers.jpeg', features: ['Full venue styling', 'Floral arrangements', 'Table centerpieces', 'Ceremony arch', 'Aisle decor'], style: 'Tropical Elegant' },
      { id: 'balivip-style-200-ess', name: 'BALIVIP Styling – 200 Guests', tier: 'essential', price: 8713, additionalDayRate: 0.2, includesDays: 1, image: '/images/flowers.jpeg', features: ['Grand venue styling', 'Full floral design', 'Large-scale setup'], style: 'Tropical Elegant' },
      // Signature — BALIVIP Styling
      { id: 'balivip-style-50-sig', name: 'BALIVIP Styling Signature – 50 Guests', tier: 'signature', price: 5228, additionalDayRate: 0.2, includesDays: 1, image: '/images/flowers.jpeg', features: ['Elevated design', 'Premium florals', 'Custom table settings', 'Lighting design'], style: 'Romantic Luxe' },
      { id: 'balivip-style-100-sig', name: 'BALIVIP Styling Signature – 100 Guests', tier: 'signature', price: 6390, additionalDayRate: 0.2, includesDays: 1, image: '/images/flowers.jpeg', features: ['Elevated design', 'Premium florals', 'Ceremony + reception styling'], style: 'Romantic Luxe' },
      { id: 'balivip-style-200-sig', name: 'BALIVIP Styling Signature – 200 Guests', tier: 'signature', price: 9294, additionalDayRate: 0.2, includesDays: 1, image: '/images/flowers.jpeg', features: ['Grand elevated design', 'Full venue transformation'], style: 'Romantic Luxe' },
      // Prestige — BALIVIP Styling + Bali Sewa Sewa + PRODECO
      { id: 'balivip-style-50-pre', name: 'BALIVIP Styling Prestige – 50 Guests', tier: 'prestige', price: 6390, additionalDayRate: 0.15, includesDays: 1, image: '/images/flowers.jpeg', features: ['Luxury design', 'Imported flowers', 'Custom installations', 'Full venue styling'], style: 'Grand Opulence' },
      { id: 'balivip-style-100-pre', name: 'BALIVIP Styling Prestige – 100 Guests', tier: 'prestige', price: 8132, additionalDayRate: 0.15, includesDays: 1, image: '/images/flowers.jpeg', features: ['Luxury florals', 'Chandelier rentals', 'Full installation'], style: 'Grand Opulence' },
      { id: 'balivip-style-200-pre', name: 'BALIVIP Styling Prestige – 200 Guests', tier: 'prestige', price: 11618, additionalDayRate: 0.15, includesDays: 1, image: '/images/flowers.jpeg', features: ['Grand luxury design', 'Bespoke installations', 'Premium florals'], style: 'Grand Opulence' },
      // Ultra — BALIVIP Styling + Sannari Stage/Flooring
      { id: 'balivip-style-50-ultra', name: 'BALIVIP Styling Ultra – 50 Guests', tier: 'ultra', price: 8132, additionalDayRate: 0.15, includesDays: 1, image: '/images/flowers.jpeg', features: ['Bespoke luxury design', 'Rare orchids & peonies', 'Suspended installations', 'Custom lighting'], style: 'Bespoke Luxury' },
      { id: 'balivip-style-100-ultra', name: 'BALIVIP Styling Ultra – 100 Guests', tier: 'ultra', price: 10456, additionalDayRate: 0.15, includesDays: 1, image: '/images/flowers.jpeg', features: ['Grand bespoke design', 'Imported flowers', 'Full venue transformation', 'Custom dance floor'], style: 'Bespoke Luxury' },
      { id: 'balivip-style-200-ultra', name: 'BALIVIP Styling Ultra – 200 Guests', tier: 'ultra', price: 15103, additionalDayRate: 0.15, includesDays: 1, image: '/images/flowers.jpeg', features: ['Ultimate luxury', 'Celebrity florist', 'Multiple installations', 'Custom dance floor by Sannari'], style: 'Bespoke Luxury' }
    ],
    entertainment: [
      // Essential — DJ Bian
      { id: 'dj-bian-3h', name: 'DJ Bian – 3 Hours', tier: 'essential', price: 382, additionalDayRate: 0.9, includesDays: 1, image: '/images/dj.jpeg', features: ['3-hour DJ set', 'Equipment not included'], type: 'DJ' },
      { id: 'dj-bian-equip', name: 'DJ Bian – 4 Hours with Equipment', tier: 'essential', price: 441, additionalDayRate: 0.9, includesDays: 1, image: '/images/dj.jpeg', features: ['4-hour DJ set', 'Full DJ equipment included'], type: 'DJ' },
      // Signature — DJ Arif / DJ Mike / DJ Hannah / Moana
      { id: 'dj-arif', name: 'DJ Arif – Sunset to Midnight', tier: 'signature', price: 588, additionalDayRate: 0.8, includesDays: 1, image: '/images/dj.jpeg', features: ['5-hour set', 'Funk, disco, house', 'Open requests'], type: 'DJ', themes: ['Western', 'Bohemian'] },
      { id: 'dj-mike-5h', name: 'DJ Mike – 5 Hours', tier: 'signature', price: 618, additionalDayRate: 0.8, includesDays: 1, image: '/images/dj.jpeg', features: ['5-hour performance', 'Professional sound', 'Dance lighting'], type: 'DJ', themes: ['Western', 'Bohemian'] },
      { id: 'moana-sax', name: 'Moana – Saxophone Player', tier: 'signature', price: 306, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/moana/1.png', gallery: ['/images/vendors/moana/2.png', '/images/vendors/moana/3.png'], features: ['3×30 min sets', 'Ceremony & cocktail hour'], type: 'Live Music' },
      { id: 'moana-trio', name: 'Moana – Trio Acoustic', tier: 'signature', price: 459, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/moana/4.png', features: ['Singer + guitarist + drum pad', '3×45 min sets'], type: 'Live Band' },
      { id: 'moana-5piece', name: 'Moana – 5-Player Band', tier: 'signature', price: 765, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/moana/5.png', gallery: ['/images/vendors/moana/6.png', '/images/vendors/moana/7.png'], features: ['Singer + guitarist + drums + keyboard + bass', '3×45 min sets'], type: 'Live Band', themes: ['Western', 'Bohemian'] },
      { id: 'dj-evie', name: 'DJ Evie – 3 Hours', tier: 'signature', price: 647, additionalDayRate: 0.8, includesDays: 1, image: '/images/dj.jpeg', features: ['3-hour set', 'Equipment included'], type: 'DJ' },
      // Prestige — Lucky Aces / DJ Jimmy Sax / Orange Production
      { id: 'lucky-aces-6piece', name: 'Lucky Aces – 6-Piece Band (Damaris)', tier: 'prestige', price: 941, additionalDayRate: 0.7, includesDays: 1, image: '/images/vendors/lucky-aces/1.png', gallery: ['/images/vendors/lucky-aces/2.png', '/images/vendors/lucky-aces/3.png'], features: ['Female + male singer', 'Guitar + bass + keys + drums + sax', '2×45 min sets'], type: 'Live Band', themes: ['Western'] },
      { id: 'lucky-aces-7piece', name: 'Lucky Aces – 7-Piece Band (Damaris)', tier: 'prestige', price: 1089, additionalDayRate: 0.7, includesDays: 1, image: '/images/vendors/lucky-aces/4.png', gallery: ['/images/vendors/lucky-aces/5.png', '/images/vendors/lucky-aces/6.png'], features: ['Full 7-piece band', 'Saxophone + vocalist', '2×45 min sets'], type: 'Live Band', themes: ['Western'] },
      { id: 'dj-jimmy-sax', name: 'DJ Jimmy Sax Black – 6 Hours', tier: 'prestige', price: 1400, additionalDayRate: 0.7, includesDays: 1, image: '/images/dj.jpeg', features: ['6-hour DJ set', '3000W sound system included'], type: 'DJ' },
      { id: 'fire-traditional', name: 'Orange Production – Traditional Fire Dance', tier: 'prestige', price: 318, additionalDayRate: 0, includesDays: 1, image: '/images/vendors/orange-production/1.png', gallery: ['/images/vendors/orange-production/2.png', '/images/vendors/orange-production/3.png'], features: ['5 fire dancers', '10-12 min show'], type: 'Fire Dance', themes: ['Balinese', 'any'] },
      { id: 'fire-bollywood', name: 'Orange Production – Bollywood Fire Dance', tier: 'prestige', price: 926, additionalDayRate: 0, includesDays: 1, image: '/images/vendors/orange-production/4.png', gallery: ['/images/vendors/orange-production/5.png'], features: ['15 fire dancers', '10-12 min show'], type: 'Fire Dance', themes: ['Indian'] },
      { id: 'tari-pendet', name: 'Orange Production – Tari Pendet', tier: 'prestige', price: 289, additionalDayRate: 0, includesDays: 1, image: '/images/vendors/orange-production/6.png', features: ['4 Balinese dancers', '6-min traditional show'], type: 'Traditional Dance', themes: ['Balinese'] },
      { id: 'tari-kecak-25', name: 'Orange Production – Kecak Dance (25 dancers)', tier: 'prestige', price: 1447, additionalDayRate: 0, includesDays: 1, image: '/images/vendors/orange-production/7.png', gallery: ['/images/vendors/orange-production/8.png', '/images/vendors/orange-production/9.png'], features: ['25 dancers', '15-min iconic Kecak performance'], type: 'Traditional Dance', themes: ['Balinese'] },
      // Ultra — Luxe Voir / Joshua Setiawan
      { id: 'luxevoir-fullband', name: 'Luxe Voir – La Pierre de Lune Full Band', tier: 'ultra', price: 2118, additionalDayRate: 0.6, includesDays: 1, image: '/images/dj.jpeg', features: ['2 vocalists + guitar + keys + bass + drums + sax/violin', '2×45 min sets'], type: 'Live Band', themes: ['Western'] },
      { id: 'luxevoir-almandine', name: 'Luxe Voir – Almandine Harmonized Band', tier: 'ultra', price: 2795, additionalDayRate: 0.6, includesDays: 1, image: '/images/dj.jpeg', features: ['Premium 8-piece band', 'Guitar + keys + bass + drums + sax + violin', '2×45 min sets'], type: 'Live Band', themes: ['Western'] },
      { id: 'joshua-fullband', name: 'Joshua Setiawan – Full Band', tier: 'ultra', price: 2059, additionalDayRate: 0.6, includesDays: 1, image: '/images/vendors/joshua-setiawan/1.png', gallery: ['/images/vendors/joshua-setiawan/2.png', '/images/vendors/joshua-setiawan/3.png'], features: ['Male + female singer', 'Keys + guitar + bass + drums + sax/violin', '2×45 min sets'], type: 'Live Band', themes: ['any'] },
      { id: 'joshua-iqua', name: 'Joshua Setiawan – Iqua Package', tier: 'ultra', price: 2295, additionalDayRate: 0.6, includesDays: 1, image: '/images/vendors/joshua-setiawan/4.png', gallery: ['/images/vendors/joshua-setiawan/5.png', '/images/vendors/joshua-setiawan/6.png'], features: ['Singer: Iqua', 'Full band', '2×45 min sets'], type: 'Live Band', themes: ['any'] }
    ],
    makeup: [
      // Essential — AlekSander / Glo Spa / Ranny Bronzer / Wiwit / Dolface
      { id: 'aleksander-bride', name: 'AlekSander – Bridal Makeup & Hairdo', tier: 'essential', price: 294, additionalDayRate: 1.0, includesDays: 1, image: '/images/flowers.jpeg', features: ['Makeup + hairdo', 'Professional bridal look'], type: 'Bridal' },
      { id: 'dolface-bride', name: 'Dolface Darlings – Bridal Package', tier: 'essential', price: 471, additionalDayRate: 1.0, includesDays: 1, image: '/images/vendors/dolface/1.png', gallery: ['/images/vendors/dolface/2.png', '/images/vendors/dolface/3.png'], features: ['Makeup + hairdo', 'Premium products'], type: 'Bridal' },
      { id: 'ranny-bride', name: 'Ranny Bronzer – Bridal Makeup', tier: 'essential', price: 399, additionalDayRate: 1.0, includesDays: 1, image: '/images/vendors/rany-bronzer/1.png', gallery: ['/images/vendors/rany-bronzer/2.png', '/images/vendors/rany-bronzer/3.png'], features: ['Makeup + hairdo', 'Professional artist'], type: 'Bridal' },
      // Signature — Alvina Yo / Carmelia / Natalie / Cempaka / Fikri
      { id: 'alvina-gold', name: 'Alvina Yo – Gold Package', tier: 'signature', price: 529, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/alvina-yo/1.png', gallery: ['/images/vendors/alvina-yo/2.png', '/images/vendors/alvina-yo/3.png'], features: ['Makeup + hairdo + retouch', 'Groom touch-up'], type: 'Bridal Package' },
      { id: 'alvina-platinum', name: 'Alvina Yo – Platinum Package', tier: 'signature', price: 853, additionalDayRate: 0.8, includesDays: 1, image: '/images/vendors/alvina-yo/4.png', gallery: ['/images/vendors/alvina-yo/5.png', '/images/vendors/alvina-yo/6.png'], features: ['Bridal makeup + retouch', 'Groom touch-up', '2 bridesmaids makeup'], type: 'Bridal + Party' },
      { id: 'natalie-diamond', name: 'Natalie – Diamond Package', tier: 'signature', price: 823, additionalDayRate: 0.8, includesDays: 1, image: '/images/flowers.jpeg', features: ['Bridal makeup + trial + retouch', 'Groom makeup', '2 moms makeup'], type: 'Bridal + Family' },
      { id: 'carmelia-platinum', name: 'Carmelia – Platinum Bride & Mother', tier: 'signature', price: 853, additionalDayRate: 0.8, includesDays: 1, image: '/images/flowers.jpeg', features: ['Airbrush bridal makeup', 'Retouch + trial', '2 moms makeup'], type: 'Bridal + Family' },
      // Prestige — Andriani Dewi / Sherlya / Cempaka
      { id: 'andriani-diamond', name: 'Andriani Dewi – Diamond Package', tier: 'prestige', price: 1235, additionalDayRate: 0.7, includesDays: 1, image: '/images/flowers.jpeg', features: ['Premium bridal makeup + retouch', '2 moms makeup by team'], type: 'Bridal + Family' },
      { id: 'sherlya-gorge', name: 'Sherlya – Gorge Package', tier: 'prestige', price: 1470, additionalDayRate: 0.7, includesDays: 1, image: '/images/flowers.jpeg', features: ['Bridal makeup + trial + retouch', '2 moms + 2 sisters makeup'], type: 'Full Bridal Party' },
      { id: 'cempaka-prestige', name: 'Cempaka – Bridal + Trial', tier: 'prestige', price: 1288, additionalDayRate: 0.7, includesDays: 1, image: '/images/flowers.jpeg', features: ['Premium bridal makeup + hairdo', 'Retouch + trial included'], type: 'Bridal Package' },
      // Ultra — Sheila Kho
      { id: 'sheila-gold', name: 'Sheila Kho – Gold Package', tier: 'ultra', price: 1176, additionalDayRate: 0.6, includesDays: 1, image: '/images/flowers.jpeg', features: ['Celebrity MUA', 'Premium bridal makeup + retouch'], type: 'Bridal' },
      { id: 'sheila-luxury', name: 'Sheila Kho – Luxury Package', tier: 'ultra', price: 2059, additionalDayRate: 0.6, includesDays: 1, image: '/images/flowers.jpeg', features: ['Celebrity MUA', 'Bridal + retouch', '2 moms + 1 sister by team', 'Full retouch service'], type: 'Full Bridal Party' }
    ],
    weddingCake: [
      // Signature — Sugar Legacy
      { id: 'sugar-2tier-real', name: 'Sugar Legacy – 2-Tier (1 real cake)', tier: 'signature', price: 232, additionalDayRate: 0, includesDays: 1, image: '/images/food.jpeg', features: ['22cm + 16cm tiers', '1 real cake tier', 'Serves ~50'], type: '2-Tier', capacity: 50 },
      { id: 'sugar-3tier-real', name: 'Sugar Legacy – 3-Tier (2 real cakes)', tier: 'signature', price: 319, additionalDayRate: 0, includesDays: 1, image: '/images/food.jpeg', features: ['28cm + 22cm + 16cm', '2 real cake tiers', 'Serves ~100'], type: '3-Tier', capacity: 100 },
      { id: 'sugar-5tier-big', name: 'Sugar Legacy – 5-Tier Grand', tier: 'signature', price: 784, additionalDayRate: 0, includesDays: 1, image: '/images/food.jpeg', features: ['60cm to 20cm tiers', 'Showpiece cake', 'Serves ~175'], type: '5-Tier', capacity: 175 },
      // Prestige — Butter Bali
      { id: 'butter-sugarflowers', name: 'Butter Bali – Sugar Flowers Cake', tier: 'prestige', pricePerHead: 7.55, additionalDayRate: 0, includesDays: 1, image: '/images/food.jpeg', features: ['Artisan sugar flowers', 'Custom flavors', '50-100 servings'], type: 'Custom Cake', capacity: 100 },
      { id: 'butter-handpainted', name: 'Butter Bali – Hand-Painted Design', tier: 'prestige', pricePerHead: 7.26, additionalDayRate: 0, includesDays: 1, image: '/images/food.jpeg', features: ['Hand-painted artistry', 'Custom design', '50-100 servings'], type: 'Custom Cake', capacity: 100 },
      // Ultra — Cake It On
      { id: 'cakeiton-italian', name: 'Cake It On – Italian Wedding Cake (80cm)', tier: 'ultra', price: 418, additionalDayRate: 0, includesDays: 1, image: '/images/food.jpeg', features: ['80cm diameter', 'Fresh strawberry', 'Serves ~200'], type: 'Italian Cake', capacity: 200 },
      { id: 'cakeiton-dessert-50', name: 'Cake It On – Dessert Table (50 pax)', tier: 'ultra', price: 668, additionalDayRate: 0, includesDays: 1, image: '/images/food.jpeg', features: ['2-tier dummy cake', 'Cake slices + cupcakes', 'Macarons + cake pops', 'Popcorn + fruit pies', '3-hour attendant'], type: 'Dessert Table', capacity: 50 },
      { id: 'cakeiton-full-3tier', name: 'Cake It On – Full 3-Tier Cake', tier: 'ultra', price: 499, additionalDayRate: 0, includesDays: 1, image: '/images/food.jpeg', features: ['All real cake tiers', 'Serves ~150'], type: 'Full Cake', capacity: 150 }
    ],
    transportation: [
      // Signature — Berdjaja Transport
      { id: 'berdjaja-avanza', name: 'Berdjaja – Avanza/Xpander (7 seats)', tier: 'signature', price: 64, additionalDayRate: 1.0, includesDays: 1, image: '/images/venue.jpeg', features: ['Full day (10 hours)', '7 seats', 'Driver included'], type: 'Car', capacity: 7 },
      { id: 'berdjaja-innova', name: 'Berdjaja – Innova Zenix (7 seats)', tier: 'signature', price: 102, additionalDayRate: 1.0, includesDays: 1, image: '/images/venue.jpeg', features: ['Full day (10 hours)', '7 seats', 'Premium sedan'], type: 'Car', capacity: 7 },
      { id: 'berdjaja-alphard', name: 'Berdjaja – Alphard (6 seats)', tier: 'signature', price: 183, additionalDayRate: 1.0, includesDays: 1, image: '/images/venue.jpeg', features: ['Full day (10 hours)', '6 luxury seats', 'VIP transport'], type: 'Luxury Car', capacity: 6 },
      { id: 'berdjaja-hiace', name: 'Berdjaja – Hiace Commuter (14 seats)', tier: 'signature', price: 93, additionalDayRate: 1.0, includesDays: 1, image: '/images/venue.jpeg', features: ['Full day (10 hours)', '14 seats', 'Group transport'], type: 'Van', capacity: 14 },
      { id: 'berdjaja-premio-luxury', name: 'Berdjaja – Premio Luxury (9 seats)', tier: 'signature', price: 218, additionalDayRate: 1.0, includesDays: 1, image: '/images/venue.jpeg', features: ['Full day (10 hours)', '9 luxury seats', 'Premium van'], type: 'Luxury Van', capacity: 9 }
    ],
    activities: [
      // Essential — Bali Games Hire
      { id: 'games-jenga', name: 'Giant Jenga', tier: 'essential', price: 35, additionalDayRate: 1.0, includesDays: 1, image: '/images/dj.jpeg', features: ['Giant outdoor game', 'Per day rental'], type: 'Game' },
      { id: 'games-beanbag', name: 'Bean Bag Toss', tier: 'essential', price: 30, additionalDayRate: 1.0, includesDays: 1, image: '/images/dj.jpeg', features: ['Classic party game', 'Per day rental'], type: 'Game' },
      { id: 'games-badminton', name: 'Boho Badminton', tier: 'essential', price: 52, additionalDayRate: 1.0, includesDays: 1, image: '/images/dj.jpeg', features: ['Stylish badminton set', 'Per day rental'], type: 'Game' },
      { id: 'games-foosball', name: 'Foosball Table', tier: 'essential', price: 41, additionalDayRate: 1.0, includesDays: 1, image: '/images/dj.jpeg', features: ['Table football', 'Per day rental'], type: 'Game' },
      // Signature — Gacha Corner
      { id: 'gacha-claw', name: 'LED Claw Machine', tier: 'signature', price: 174, additionalDayRate: 1.0, includesDays: 1, image: '/images/dj.jpeg', features: ['LED claw machine', 'Full day rental', 'Fun photo op'], type: 'Arcade' },
      { id: 'gacha-basketball', name: 'LED Basketball Game', tier: 'signature', price: 435, additionalDayRate: 1.0, includesDays: 1, image: '/images/dj.jpeg', features: ['LED basketball arcade', 'Full day rental', 'Competition mode'], type: 'Arcade' },
      { id: 'gacha-airhockey', name: 'Air Hockey Table', tier: 'signature', price: 87, additionalDayRate: 1.0, includesDays: 1, image: '/images/dj.jpeg', features: ['Full-size air hockey', 'Full day rental'], type: 'Arcade' }
    ],
    addons: [
      // Indian
      { id: 'addon-mehndi', name: 'Mehndi / Henna Artist', tier: 'essential', price: 800, additionalDayRate: 1.0, includesDays: 1, image: '/images/flowers.jpeg', features: ['Professional henna', 'Bridal hands & feet', 'Guest station'], themes: ['Indian', 'Middle Eastern'] },
      { id: 'addon-pandit', name: 'Pandit / Hindu Officiant', tier: 'essential', price: 500, additionalDayRate: 1.0, includesDays: 1, image: '/images/flowers.jpeg', features: ['Hindu ceremony', 'Fire ritual', 'Mantra chanting', 'Blessings'], themes: ['Indian'] },
      { id: 'addon-baraat', name: 'Baraat Horse & Band', tier: 'signature', price: 2000, additionalDayRate: 0, includesDays: 1, image: '/images/flowers.jpeg', features: ['Decorated horse', 'Dhol players', 'Flower shower', 'Procession'], themes: ['Indian'] },
      { id: 'addon-sangeet', name: 'Sangeet Night Setup', tier: 'signature', price: 3000, additionalDayRate: 0, includesDays: 1, image: '/images/flowers.jpeg', features: ['Stage', 'Lighting', 'Sound', 'Choreography coordination'], themes: ['Indian'] },
      { id: 'addon-bollywood-fire', name: 'Bollywood Fire Dance', tier: 'prestige', price: 926, additionalDayRate: 0, includesDays: 1, image: '/images/dj.jpeg', features: ['15 fire dancers', '10-12 min Bollywood themed show'], themes: ['Indian'] },
      // Balinese
      { id: 'addon-blessing', name: 'Balinese Blessing Ceremony', tier: 'essential', price: 600, additionalDayRate: 0, includesDays: 1, image: '/images/flowers.jpeg', features: ['Traditional priest', 'Offerings', 'Holy water', 'Symbolic rituals'], themes: ['Balinese'] },
      { id: 'addon-pendet', name: 'Tari Pendet – Balinese Welcome Dance', tier: 'prestige', price: 289, additionalDayRate: 0, includesDays: 1, image: '/images/dj.jpeg', features: ['4 Balinese dancers', '6-min traditional welcome dance'], themes: ['Balinese'] },
      { id: 'addon-kecak', name: 'Kecak Dance Performance (25 dancers)', tier: 'prestige', price: 1447, additionalDayRate: 0, includesDays: 1, image: '/images/dj.jpeg', features: ['25 performers', '15-min iconic Kecak', 'Fire element'], themes: ['Balinese'] },
      { id: 'addon-legong', name: 'Tari Legong Keraton', tier: 'prestige', price: 260, additionalDayRate: 0, includesDays: 1, image: '/images/dj.jpeg', features: ['3 dancers', '15-min classical Balinese dance'], themes: ['Balinese'] },
      // Chinese
      { id: 'addon-tea', name: 'Tea Ceremony Setup', tier: 'essential', price: 400, additionalDayRate: 0, includesDays: 1, image: '/images/flowers.jpeg', features: ['Tea set', 'Traditional decor', 'Ceremony coordination'], themes: ['Chinese'] },
      { id: 'addon-lion', name: 'Lion Dance Performance', tier: 'signature', price: 1800, additionalDayRate: 0, includesDays: 1, image: '/images/flowers.jpeg', features: ['Lion dance troupe', 'Drums', 'Auspicious blessing'], themes: ['Chinese'] },
      // Japanese
      { id: 'addon-sake', name: 'Sake Ceremony Setup', tier: 'essential', price: 500, additionalDayRate: 0, includesDays: 1, image: '/images/flowers.jpeg', features: ['San-san-kudo', 'Sake set', 'Ceremony coordination'], themes: ['Japanese'] },
      // Mexican
      { id: 'addon-mariachi', name: 'Mariachi Band', tier: 'signature', price: 1500, additionalDayRate: 0.8, includesDays: 1, image: '/images/flowers.jpeg', features: ['Live mariachi', 'Traditional costumes', 'Ceremony & reception'], themes: ['Mexican'] },
      // Korean
      { id: 'addon-paebaek', name: 'Korean Paebaek Setup', tier: 'essential', price: 700, additionalDayRate: 0, includesDays: 1, image: '/images/flowers.jpeg', features: ['Traditional table', 'Hanbok rental', 'Jujubes & chestnuts'], themes: ['Korean'] },
      // Universal
      { id: 'addon-fireworks', name: 'Premium Fireworks Display', tier: 'prestige', price: 5000, additionalDayRate: 0, includesDays: 1, image: '/images/flowers.jpeg', features: ['5-min choreographed', 'Beach or cliff launch', 'Permits included'], themes: ['any'] },
      { id: 'addon-drone', name: 'Drone Light Show', tier: 'ultra', price: 12000, additionalDayRate: 0, includesDays: 1, image: '/images/flowers.jpeg', features: ['100+ drones', 'Custom design', '10-minute show'], themes: ['any'] }
    ]
  };

  // ===== MULTI-DAY PRICING HELPER =====
  const calculateMultiDayPrice = (vendor, category, numGuests, numDays) => {
    let basePrice;
    if (category === 'catering' && vendor.pricePerHead) {
      basePrice = vendor.pricePerHead * numGuests;
    } else if (category === 'weddingCake' && vendor.pricePerHead) {
      basePrice = vendor.pricePerHead * numGuests;
    } else {
      basePrice = vendor.price || 0;
    }
    const includedDays = vendor.includesDays || 1;
    const additionalDayRate = vendor.additionalDayRate || 0.7;
    const extraDays = Math.max(0, numDays - includedDays);
    return Math.round(basePrice + (basePrice * additionalDayRate * extraDays));
  };

  // Pick representative vendors for a given package tier (used for package announcement)
  const categoryEmojis = {
    venue: '🏝',
    catering: '🍽',
    decoration: '💐',
    photography: '📸',
    entertainment: '🎵',
    makeup: '💄',
    weddingCake: '🎂',
    transportation: '🚗',
    activities: '🎯',
    addons: '✨'
  };

  const getRepresentativeVendors = (selectedTier) => {
    const tierMap = { essential: 'essential', classic: 'signature', luxury: 'prestige', prestige: 'prestige' };
    const tierKey = tierMap[selectedTier] || selectedTier;
    const categories = ['venue', 'catering', 'decoration', 'photography', 'entertainment', 'makeup'];
    const picks = categories.map(cat => {
      const list = vendorDatabase[cat] || [];
      let item = list.find(v => v.tier === tierKey);
      if (!item) item = list.find(v => v.tier === 'signature') || list[0];
      const emoji = categoryEmojis[cat] || '•';
      return { category: cat, emoji, name: item ? item.name : 'TBD' };
    });
    return picks;
  };

  // ===== THEME FILTER HELPER =====
  const filterByTheme = (vendors, theme) => {
    if (!theme || theme === 'Western') return vendors; // Western is default, no filtering needed
    const filtered = vendors.filter(v => 
      v.themes && (v.themes.includes('any') || v.themes.includes(theme))
    );
    return filtered.length > 0 ? filtered : vendors; // fallback to all if no match
  };

  // ===== GET RELEVANT ADD-ONS FOR THEME =====
  const getThemeAddons = (theme, tier, numDays) => {
    if (!theme || theme === 'Western') return [];
    const tierOrder = ['essential', 'signature', 'prestige', 'ultra'];
    const tierIdx = tierOrder.indexOf(tier);
    return vendorDatabase.addons.filter(addon => {
      const matchesTheme = addon.themes.includes('any') || addon.themes.includes(theme);
      const addonTierIdx = tierOrder.indexOf(addon.tier);
      return matchesTheme && addonTierIdx <= tierIdx;
    }).map(addon => ({
      ...addon,
      totalPrice: calculateMultiDayPrice(addon, 'addons', 0, numDays)
    }));
  };

  // ===== SMART BUDGET OPTIMIZER - AI ALLOCATION FUNCTION =====
  const generateOptimizedPackages = (budget, guests, priorities, venuePreferenceParam = 'any', days = 1, theme = 'Western') => {
    const numBudget = parseFloat(budget) || 15000;
    const numGuests = parseInt(guests) || 80;
    const numDays = parseInt(days) || 1;
    const weddingThemeParam = theme || 'Western';

    // Calculate priority weights
    const totalPriority = Object.values(priorities).reduce((a, b) => a + b, 0);
    const weights = {};
    Object.keys(priorities).forEach(key => {
      weights[key] = priorities[key] / totalPriority;
    });

    // Determine budget tier - what level of vendors to target
    // $0-40k = essential, $40-65k = signature, $65-100k = prestige, $100k+ = ultra
    const getBudgetTier = (budget) => {
      if (budget >= 100000) return 'ultra';
      if (budget >= 65000) return 'prestige';
      if (budget >= 40000) return 'signature';
      return 'essential';
    };
    
    const budgetTier = getBudgetTier(numBudget);
    const tierOrder = ['essential', 'signature', 'prestige', 'ultra'];
    const tierIndex = tierOrder.indexOf(budgetTier);

    // Helper to find vendor by tier with fallback
    const findVendorByTier = (category, targetTier, venueFilter = null) => {
      let options = [...vendorDatabase[category]];
      
      // Filter by theme preference
      if (weddingThemeParam && weddingThemeParam !== 'Western') {
        const themeFiltered = options.filter(v => 
          v.themes && (v.themes.includes('any') || v.themes.includes(weddingThemeParam))
        );
        if (themeFiltered.length > 0) options = themeFiltered;
      }
      
      // Filter venues by preference — map user-facing venue types to database vibe values
      if (category === 'venue' && venueFilter && venueFilter !== 'any') {
        const vibeMap = {
          'beachfront': ['beachfront'],
          'cliff front': ['cliff'],
          'cliff top': ['cliff'],
          'cliff villa': ['cliff'],  // cliff villas (Pandawa, Ungasan, Alila, Stones)
          'cliff': ['cliff'],
          'ocean view': ['beachfront', 'cliff'],
          'rice field': ['garden'],
          'garden': ['garden'],
          'villa': ['cliff', 'garden']  // villas can be cliff or garden
        };
        const matchVibes = vibeMap[venueFilter] || [venueFilter];
        const filtered = options.filter(v => matchVibes.includes(v.vibe));
        if (filtered.length > 0) options = filtered;
      }
      
      // Filter by capacity for venues
      if (category === 'venue') {
        options = options.filter(v => v.capacity >= numGuests);
        if (options.length === 0) options = [...vendorDatabase[category]]; // fallback to all
      }
      
      // If user selected a specific venue by name, use it (for venue category only)
      if (category === 'venue' && selectedVenueId) {
        const userPick = options.find(v => v.id === selectedVenueId);
        if (userPick) return userPick;
        // Also check full database in case filters excluded it
        const fallbackPick = vendorDatabase.venue.find(v => v.id === selectedVenueId);
        if (fallbackPick) return fallbackPick;
      }
      
      // Find exact tier match first
      let selected = options.find(v => v.tier === targetTier);
      
      // If not found, find closest tier
      if (!selected) {
        const targetIdx = tierOrder.indexOf(targetTier);
        // Try lower tiers first, then higher
        for (let i = targetIdx; i >= 0; i--) {
          selected = options.find(v => v.tier === tierOrder[i]);
          if (selected) break;
        }
        if (!selected) {
          for (let i = targetIdx; i < tierOrder.length; i++) {
            selected = options.find(v => v.tier === tierOrder[i]);
            if (selected) break;
          }
        }
      }
      
      return selected || options[0];
    };

    // Generate 3 package options
    const packages = [];

    // Package 1: Best Value (one tier below budget tier, maximize value)
    const bestValueTier = tierOrder[Math.max(0, tierIndex - 1)];
    const bestValue = {
      id: 'best-value',
      name: 'Best Value Package',
      tagline: 'Maximum coverage within your budget',
      icon: '💎',
      color: '#10b981',
      allocations: {},
      vendors: {},
      total: 0,
      savings: 0
    };

    // Package 2: Balanced (at budget tier)
    const balanced = {
      id: 'balanced',
      name: 'Signature Experience',
      tagline: 'Perfect balance of quality and value',
      icon: '⭐',
      color: '#f59e0b',
      allocations: {},
      vendors: {},
      total: 0,
      savings: 0
    };

    // Package 3: Premium (at or above budget tier - splurge on priorities)
    const premiumTier = tierOrder[Math.min(tierOrder.length - 1, tierIndex + 1)];
    const premium = {
      id: 'premium',
      name: 'Prestige Collection',
      tagline: 'Ultimate luxury experience',
      icon: '👑',
      color: '#8b5cf6',
      allocations: {},
      vendors: {},
      total: 0,
      savings: 0
    };

    // Core categories always included, plus optional extras
    const coreCategories = ['venue', 'catering', 'photography', 'decoration', 'entertainment', 'makeup'];
    const scalableCategories = ['venue', 'catering', 'photography', 'decoration', 'entertainment', 'makeup'];

    // Helper to allocate a package across all categories
    const allocatePackage = (pkg, targetTier) => {
      // Pick venue first to check if it's a resort (F&B included)
      const venueVendor = findVendorByTier('venue', targetTier, venuePreferenceParam);
      const isResort = venueVendor?.venueType === 'resort';

      coreCategories.forEach(category => {
        // Skip catering for resort venues — F&B is included in the venue package
        if (category === 'catering' && isResort) {
          pkg.vendors.catering = { id: 'included', name: 'Included with resort venue', tier: venueVendor.tier, pricePerHead: 0, features: ['Food & beverages included in venue package'] };
          pkg.allocations.catering = 0;
          return;
        }
        const selected = findVendorByTier(category, targetTier, venuePreferenceParam);
        if (selected) {
          pkg.allocations[category] = calculateMultiDayPrice(selected, category, numGuests, numDays);
          pkg.vendors[category] = selected;
        }
      });
      // Add wedding cake (flat price, optional by tier)
      const cake = findVendorByTier('weddingCake', targetTier, null);
      if (cake) {
        const cakePrice = cake.pricePerHead ? cake.pricePerHead * numGuests : cake.price;
        pkg.allocations.weddingCake = cakePrice;
        pkg.vendors.weddingCake = cake;
      }
      // Add theme addons
      const addons = getThemeAddons(weddingThemeParam, targetTier, numDays);
      if (addons.length > 0) {
        pkg.addons = addons;
        pkg.allocations.addons = addons.reduce((sum, a) => sum + a.totalPrice, 0);
      }
      pkg.total = Object.values(pkg.allocations).reduce((a, b) => a + b, 0);
    };

    // Budget allocation weights per category (% of total budget)
    const categoryWeights = {
      venue:         0.35,  // 35% of budget
      catering:      0.20,  // 20%
      photography:   0.15,  // 15%
      decoration:    0.12,  // 12%
      entertainment: 0.08,  // 8%
      makeup:        0.05,  // 5%
      weddingCake:   0.03,  // 3%
      addons:        0.02   // 2%
    };

    // Helper: allocate a package using real vendors but ensure each category
    // gets AT LEAST its weighted share of the target budget.
    // Vendor is picked by tier, but the displayed price is the MAX of
    // (real vendor price) and (category weight × target budget).
    // This ensures higher-budget packages always show higher prices per category.
    const allocateWithBudgetFloor = (pkg, targetTier, targetBudget) => {
      // First pick real vendors
      allocatePackage(pkg, targetTier);
      // Then enforce budget floor per SERVICE category (NOT venue)
      // Venue always uses real vendor price — it represents a specific property
      Object.keys(pkg.allocations).forEach(cat => {
        if (cat === 'venue' || cat === 'addons') {
          pkg.allocations[cat] = Math.round(pkg.allocations[cat]);
          return;
        }
        // Skip budget floor for catering when included with resort
        if (cat === 'catering' && pkg.allocations[cat] === 0) return;
        const weight = categoryWeights[cat] || 0.02;
        // Redistribute venue's share among service categories
        const serviceWeight = weight / (1 - (categoryWeights.venue || 0.35) - (categoryWeights.addons || 0.02));
        const serviceBudget = targetBudget - (pkg.allocations.venue || 0) - (pkg.allocations.addons || 0);
        const budgetFloor = Math.round(serviceWeight * serviceBudget);
        pkg.allocations[cat] = Math.max(pkg.allocations[cat], budgetFloor);
        pkg.allocations[cat] = Math.round(pkg.allocations[cat]);
      });
      pkg.total = Object.values(pkg.allocations).reduce((a, b) => a + b, 0);
    };

    // Best Value: essential-tier vendors, target 80% of budget
    const bestValueTarget = Math.round(numBudget * 0.80);
    allocateWithBudgetFloor(bestValue, bestValueTier, bestValueTarget);
    bestValue.savings = numBudget - bestValue.total;
    if (bestValue.savings < 0) bestValue.overBudget = true;

    // Signature: signature-tier vendors, target 100% of budget
    allocateWithBudgetFloor(balanced, budgetTier, numBudget);
    balanced.savings = numBudget - balanced.total;
    if (balanced.savings < 0) balanced.overBudget = true;

    // Prestige: prestige-tier vendors, target 150% of budget
    const prestigeTarget = Math.round(numBudget * 1.50);
    allocateWithBudgetFloor(premium, premiumTier, prestigeTarget);
    premium.savings = numBudget - premium.total;
    if (premium.savings < 0) premium.overBudget = true;

    packages.push(bestValue, balanced, premium);
    
    return {
      budget: numBudget,
      guests: numGuests,
      packages,
      generatedAt: new Date().toISOString(),
      aiInsights: generateAIInsights(numBudget, numGuests, packages)
    };
  };

  const generateAIInsights = (budget, guests, packages) => {
    const perGuestBudget = budget / guests;
    const insights = [];
    
    if (perGuestBudget < 150) {
      insights.push({ type: 'tip', text: 'For intimate celebrations, consider a villa venue for a more personalized experience.' });
    }
    if (perGuestBudget > 300) {
      insights.push({ type: 'upgrade', text: 'Your budget allows for premium upgrades—consider live entertainment or a cinematic film.' });
    }
    if (guests > 100) {
      insights.push({ type: 'logistics', text: 'With 100+ guests, we recommend venues with dedicated event coordinators.' });
    }
    if (budget >= 25000) {
      insights.push({ type: 'vip', text: 'You qualify for our VIP planning service with a dedicated wedding concierge.' });
    }
    insights.push({ type: 'savings', text: `Book 6+ months in advance for up to 15% savings on venue costs.` });
    
    return insights;
  };

  const runBudgetOptimizer = () => {
    setOptimizerStep(2); // Show analyzing animation
    
    // Simulate AI processing
    setTimeout(() => {
      const result = generateOptimizedPackages(budgetInput, guestCountInput, optimizerPriorities, venuePreference, numberOfDays, weddingTheme);
      setOptimizedPlan(result);
      setOptimizerStep(3); // Show results
    }, 2000);
  };
  
  // Approximate base pricing for live estimate (from real vendor data)
  const pricing = {
    venue: {
      beachfront: 9855,   // The Laguna
      garden: 6129,       // Jeeva Saba
      villa: 11554,       // The Stones Villas
      cliff: 2148         // Anantara Uluwatu
    },
    catering: {
      standard: 20,       // Bali Indian Food
      premium: 40,        // BCC
      luxury: 47           // Bali Miracle
    },
    decoration: {
      standard: 4647,     // BALIVIP Essential
      premium: 6390,      // BALIVIP Prestige
      luxury: 10456       // BALIVIP Ultra
    },
    photography: {
      standard: 1353,     // Sriwijayastory combo
      premium: 2383,      // IMAJ combo
      luxury: 4266        // Bali Pixtura
    },
    entertainment: {
      standard: 441,      // DJ Bian
      premium: 941,       // Lucky Aces
      luxury: 2118        // Luxe Voir
    }
  };

  const tiers = {
    essential: {
      label: 'Essential',
      desc: 'Thoughtfully curated core services for intimate celebrations.',
      multiplier: 1.0
    },
    signature: {
      label: 'Signature',
      desc: 'Balanced experience with elevated details and seamless flow.',
      multiplier: 1.4
    },
    prestige: {
      label: 'Prestige',
      desc: 'Luxury finishes, premium vendors, and bespoke touches throughout.',
      multiplier: 2.0
    },
    ultra: {
      label: 'Ultra',
      desc: 'No-limits luxury with exclusive access and world-class service.',
      multiplier: 3.0
    }
  };

  // Theme palette keyed off venue and mood
  const theme = React.useMemo(() => {
    const base = {
      garden: { primary: '#6ed0b6', secondary: '#88e0c8', accent: '#dff8ef' },
      beachfront: { primary: '#7aa6ff', secondary: '#9bc0ff', accent: '#e7f0ff' },
      villa: { primary: '#b19cff', secondary: '#c6b6ff', accent: '#efeaff' },
      cliff: { primary: '#f0b86e', secondary: '#f5c88e', accent: '#fff3e4' },
      default: { primary: '#7aa6ff', secondary: '#9bc0ff', accent: '#e7f0ff' }
    };
    const venueKey = estimate.venue || 'default';
    const colors = base[venueKey] || base.default;
    // Mood tweak: if timeless-elegance, soften saturation slightly
    const isTimeless = (mood || '').includes('timeless');
    return isTimeless
      ? { primary: colors.primary, secondary: colors.secondary, accent: colors.accent, overlay: 'rgba(0,0,0,0.55)' }
      : { primary: colors.primary, secondary: colors.secondary, accent: colors.accent, overlay: 'rgba(0,0,0,0.5)' };
  }, [estimate.venue, mood]);

  React.useEffect(() => {
    const venueCost = pricing.venue[estimate.venue] || 0;
    const cateringCost = (pricing.catering[estimate.catering] || 0) * estimate.guests;
    const decorationCost = pricing.decoration[estimate.decoration] || 0;
    const photographyCost = pricing.photography[estimate.photography] || 0;
    const entertainmentCost = pricing.entertainment[estimate.entertainment] || 0;
    
    const base = venueCost + cateringCost + decorationCost + photographyCost + entertainmentCost;
    const multiplier = tiers[tier]?.multiplier || 1;
    setTotalCost(Math.round(base * multiplier));
  }, [estimate]);

  useEffect(() => {
    const venueCost = pricing.venue[estimate.venue] || 0;
    const cateringCost = (pricing.catering[estimate.catering] || 0) * estimate.guests;
    const decorationCost = pricing.decoration[estimate.decoration] || 0;
    const photographyCost = pricing.photography[estimate.photography] || 0;
    const entertainmentCost = pricing.entertainment[estimate.entertainment] || 0;
    const base = venueCost + cateringCost + decorationCost + photographyCost + entertainmentCost;
    const multiplier = tiers[tier]?.multiplier || 1;
    setTotalCost(Math.round(base * multiplier));
  }, [tier]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (loading) {
      setPulseAnimation(true);
    } else {
      setPulseAnimation(false);
    }
  }, [loading]);

  // Scroll-triggered animations: add 'in-view' when sections enter viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.2 }
    );

    sectionsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Parallax background subtle motion on scroll
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY || 0;
      if (parallaxRef.current) {
        parallaxRef.current.style.transform = `translateY(${y * 0.05}px)`;
        parallaxRef.current.style.opacity = `${Math.max(0.35, 1 - y / 1200)}`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll to show just the last question and response
  useEffect(() => {
    // Only scroll if there are more than the initial 2 messages
    if (messages.length > 2 && messagesContainerRef.current && messagesEndRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center' // Center the last message, which keeps input visible
        });
      }, 100);
    }
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    setMessages(msgs => [...msgs, { sender: 'user', text: input }]);
    const userInput = input;
    setInput('');
    setLoading(true);
    setIsTyping(true);

    try {
      const res = await fetch('/api/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userInput, extractEstimate: true })
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      
      setIsTyping(false);
      setMessages(msgs => [...msgs, { sender: 'bot', text: data.reply }]);
      
      if (data.estimate) {
        const newEstimate = { ...estimate };
        let updated = false;
        
        if (data.estimate.guests !== null) {
          newEstimate.guests = data.estimate.guests;
          updated = true;
        }
        if (data.estimate.venue !== null) {
          newEstimate.venue = data.estimate.venue;
          updated = true;
        }
        if (data.estimate.catering !== null) {
          newEstimate.catering = data.estimate.catering;
          updated = true;
        }
        if (data.estimate.decoration !== null) {
          newEstimate.decoration = data.estimate.decoration;
          updated = true;
        }
        if (data.estimate.photography !== null) {
          newEstimate.photography = data.estimate.photography;
          updated = true;
        }
        if (data.estimate.entertainment !== null) {
          newEstimate.entertainment = data.estimate.entertainment;
          updated = true;
        }
        
        if (updated) {
          setEstimate(newEstimate);
          console.log('Updated estimate from AI:', newEstimate);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setIsTyping(false);
      // Only show error if we did not already add a bot reply for this request
      setMessages(msgs => {
        const last = msgs[msgs.length - 1];
        if (last && last.sender === 'bot') return msgs; // avoid duplicate error after success
        return [...msgs, { sender: 'bot', text: 'Sorry, there was an error.' }];
      });
    }
    setLoading(false);
  };

  // Local quick actions for this planner
  const handleQuickAction = (action) => {
    if (action === 'swap-venue') {
      const venues = ['beachfront', 'garden', 'villa', 'cliff'];
      const idx = venues.indexOf(estimate.venue);
      const next = venues[(idx + 1) % venues.length];
      setEstimate(prev => ({ ...prev, venue: next }));
      setHistory(h => [...h, `QuickAction: Venue → ${next}`].slice(-8));
      // Provide immediate chat feedback
      setMessages(msgs => [...msgs, { sender: 'bot', text: `Swapped venue to ${next}. Your estimate updated.` }]);
    }
    if (action === 'refine-budget') {
      const shares = [
        { key: 'decoration', cost: pricing.decoration[estimate.decoration] || 0, tiers: ['standard', 'premium', 'luxury'] },
        { key: 'photography', cost: pricing.photography[estimate.photography] || 0, tiers: ['standard', 'premium', 'luxury'] },
        { key: 'entertainment', cost: pricing.entertainment[estimate.entertainment] || 0, tiers: ['standard', 'premium', 'luxury'] },
        { key: 'catering', cost: (pricing.catering[estimate.catering] || 0) * estimate.guests, tiers: ['standard', 'premium', 'luxury'] },
      ].sort((a,b)=>b.cost-a.cost);
      // Try to apply up to two downgrades for stronger effect
      let changes = [];
      for (let i = 0; i < shares.length && changes.length < 2; i++) {
        const item = shares[i];
        const idx = item.tiers.indexOf(estimate[item.key]);
        if (idx > 0) {
          const to = item.tiers[idx - 1];
          changes.push({ key: item.key, to });
        }
      }
      if (changes.length) {
        setEstimate(prev => {
          const next = { ...prev };
          changes.forEach(c => { next[c.key] = c.to; });
          return next;
        });
        const msg = changes.map(c => `${c.key} → ${c.to}`).join(', ');
        setHistory(h => [...h, `QuickAction: ${msg}`].slice(-8));
        setMessages(msgs => [...msgs, { sender: 'bot', text: `Refined budget: ${msg}. Your estimate updated.` }]);
      } else {
        setMessages(msgs => [...msgs, { sender: 'bot', text: 'You are already at the lowest tiers for major items. Try reducing guest count, choosing a different venue, or downgrading another category.' }]);
      }
    }
  };

  // Quick send helper for suggestion chips
  const sendQuick = async (message) => {
    if (!message || !message.trim()) return;
    // Mirror sendMessage logic but without form event
    setMessages(msgs => [...msgs, { sender: 'user', text: message }]);
    setInput('');
    setLoading(true);
    setIsTyping(true);
    try {
      const res = await fetch('/api/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, extractEstimate: true })
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setMessages(msgs => [...msgs, { sender: 'bot', text: data.reply }]);
      if (data.estimate) updateEstimate({ ...data.estimate }, 'AI');
    } catch (err) {
      console.error('Error sending quick message:', err);
      setMessages(msgs => {
        const last = msgs[msgs.length - 1];
        if (last && last.sender === 'bot') return msgs;
        return [...msgs, { sender: 'bot', text: 'Sorry, there was an error.' }];
      });
    }
    setLoading(false);
    setIsTyping(false);
  };

  // When must-have picks are complete, auto-compose and send to chatbot once
  useEffect(() => {
    if (mustHaveComplete && !mustHaveSent) {
  const composed = `Here are our must-haves: budget=${mustHave.budget}, venue=${mustHave.venue}, guests=${mustHave.guests}, catering=${mustHave.catering}, tier=${mustHave.tier}. Please prioritize budget and tailor the plan and refine the estimate accordingly.`;
      // Mirror sendQuick and update local states
      setMessages(msgs => [...msgs, { sender: 'user', text: composed }]);
      setLoading(true);
      setIsTyping(true);
      (async () => {
        try {
          const res = await fetch('/api/chatbot', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: composed, extractEstimate: true })
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          // Replace chatbot reply with a concise acknowledgment aligned to the UX
          const ack = `We’ve applied your selections and prioritized your budget. We’re crafting the best-fit plan now—your live estimate below reflects this and will adapt as you fine‑tune.`;
          setMessages(msgs => [...msgs, { sender: 'bot', text: ack }]);
          if (data.estimate) {
            // Merge estimate updates from AI
            setEstimate(prev => ({
              ...prev,
              guests: data.estimate.guests ?? prev.guests,
              venue: data.estimate.venue ?? prev.venue,
              catering: data.estimate.catering ?? prev.catering,
              decoration: data.estimate.decoration ?? prev.decoration,
              photography: data.estimate.photography ?? prev.photography,
              entertainment: data.estimate.entertainment ?? prev.entertainment,
            }));
            if (data.estimate.tier) setTier(data.estimate.tier);
          }
        } catch (err) {
          console.error('Error sending must-have message:', err);
          setMessages(msgs => [...msgs, { sender: 'bot', text: 'Sorry, there was an error applying your must-haves.' }]);
        }
        setLoading(false);
        setIsTyping(false);
        setMustHaveSent(true);
        // Scroll to the latest chat message after auto-send completes
        setTimeout(() => {
          try {
            const el = messagesEndRef.current;
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch {}
        }, 100);
      })();
    }
  }, [mustHaveComplete, mustHaveSent, mustHave]);

  // Generate proposal content from current selections
  const openProposal = () => {
    const lines = [];
    lines.push(`Bali Destination Wedding Proposal`);
    lines.push('');
    lines.push(`Vision & Vibe: ${mood.replace('-', ' ')}`);
    lines.push(`Guests: ${estimate.guests}`);
    lines.push(`Venue: ${estimate.venue}`);
    lines.push(`Experience Tier: ${tiers[tier]?.label || tier}`);
    lines.push('');
    lines.push('Selected Services:');
    lines.push(`• Catering: ${estimate.catering}`);
    lines.push(`• Decoration: ${estimate.decoration}`);
    lines.push(`• Photography: ${estimate.photography}`);
    lines.push(`• Entertainment: ${estimate.entertainment}`);
    lines.push('');
    lines.push(`Estimated Investment: $${totalCost.toLocaleString()}`);
    lines.push('');
    lines.push('Notes: Pricing is indicative and may vary by vendor selection, seasonality, and specific venue policies. A detailed line-item quote will be prepared after discovery call.');
    setProposalText(lines.join('\n'));
    setShowProposal(true);
    setProposalGenerated(true);
  };

  const downloadProposal = () => {
    const blob = new Blob([proposalText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'BaliWeddingProposal.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Filter venues based on simple criteria
  const filteredVenues = venues.filter(v => {
    const vibeOk = venueFilters.vibe === 'any' || v.vibe === venueFilters.vibe;
    const capacityOk = v.capacity >= venueFilters.minGuests && v.capacity <= venueFilters.maxGuests;
    // Tier filter is illustrative; map tiers to price bands
    const tierOk = venueFilters.tier === 'any' || (
      venueFilters.tier === 'essential' ? v.basePrice <= 2200 :
      venueFilters.tier === 'signature' ? v.basePrice <= 3000 :
      v.basePrice > 3000
    );
    const rainOk = !venueFilters.rainPlan || v.labels.includes('rainy season friendly') || v.notes.some(n => /backup/i.test(n));
    const accessOk = !venueFilters.accessible || v.notes.some(n => /ADA|accessible/i.test(n));
    return vibeOk && capacityOk && tierOk && rainOk && accessOk;
  });

  return (
    <>
      <div style={{ 
        minHeight: '100vh', 
  background: 'radial-gradient(1200px 600px at 10% 10%, rgba(59,130,246,0.15), transparent),\
        radial-gradient(800px 400px at 90% 0%, rgba(255,255,255,0.06), transparent),\
        linear-gradient(180deg, #0c0c0c 0%, #141414 50%, #0a0a0a 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Fixed top progress/status header (shows after user starts must-haves) */}
        {hasStarted && (<div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backdropFilter: 'blur(10px) saturate(150%)',
          background: 'rgba(20,20,20,0.6)',
          borderBottom: '1px solid rgba(59,130,246,0.25)'
        }}>
          {/* Thin thinking indicator bar when chatbot is processing */}
          {loading && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', overflow: 'hidden' }}>
              <div style={{
                width: '30%', height: '100%',
                background: 'linear-gradient(90deg, rgba(59,130,246,0.9), rgba(34,197,94,0.8))',
                boxShadow: '0 0 12px rgba(59,130,246,0.6)',
                animation: 'headerLoader 1200ms ease-in-out infinite'
              }} />
            </div>
          )}
          <Container fluid className="py-2">
            <Row className="align-items-center">
              <Col md={8} className="text-start">
                <div className="d-flex align-items-center mb-2" style={{ gap: '0.75rem' }}>
                  <div style={{
                    color: '#ffffff', fontWeight: 700, letterSpacing: '1px', fontSize: '0.8rem', opacity: 0.9
                  }}>Planning Progress</div>
                  <Badge bg="" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
                    {stepsCompleted}/{steps.length}
                  </Badge>
                </div>
                <div className="d-flex flex-wrap" style={{ gap: '0.4rem' }}>
                  {steps.map((s, i) => (
                    <div key={s.key} className="d-flex align-items-center" style={{
                      background: s.done ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.08)',
                      border: `1px solid ${s.done ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.12)'}`,
                      color: '#fff', padding: '0.35rem 0.6rem', borderRadius: '10px', fontSize: '0.8rem'
                    }}>
                      <span style={{ marginRight: '0.4rem' }}>{s.done ? '✓' : i+1}</span>
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2">
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '6px', overflow: 'hidden' }}>
                    <div style={{ width: `${progressPct}%`, height: '100%', background: 'linear-gradient(90deg, rgba(59,130,246,0.9), rgba(34,197,94,0.8))' }} />
                  </div>
                  <small style={{ color: 'rgba(255,255,255,0.7)' }}>{progressPct}% complete</small>
                </div>
              </Col>
              <Col md={4} className="text-md-end mt-2 mt-md-0">
                <div className="d-inline-flex" style={{ gap: '0.5rem' }}>
                  <Button size="sm" style={{
                    background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.35)',
                    color: '#fff', borderRadius: '10px', padding: '0.45rem 0.8rem', fontWeight: 600
                  }} disabled={loading} onClick={() => { setConfirmedSelections(true); setShowDetailedPlan(true); }}>Generate Detailed Plan</Button>
                  {/* Confirm button removed per new flow */}
                  <Button size="sm" style={{
                    background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.35)',
                    color: '#fff', borderRadius: '10px', padding: '0.45rem 0.8rem', fontWeight: 600
                  }} disabled={loading} hidden>Generate Proposal</Button>
                  <Button size="sm" style={{
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                    color: '#fff', borderRadius: '10px', padding: '0.45rem 0.8rem', fontWeight: 600
                  }} disabled={loading} onClick={() => setContacted(true)}>Contact</Button>
                </div>
              </Col>
            </Row>
          </Container>
        </div>)}
        {/* Subtle ambient glow (parallax) */}
        <div ref={parallaxRef} style={{
          position: 'absolute',
          top: '10%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '800px',
          height: '800px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 0
        }}></div>

  <Container id="planner-root" fluid className="py-5" style={{ position: 'relative', zIndex: 1, paddingTop: hasStarted ? '96px' : '24px' }}>
          {/* Hero Header */}
          <Row className="mb-5 section-fade-up sj-stagger" ref={(el) => (sectionsRef.current[0] = el)}>
            <Col>
              <div 
                className="text-center sj-fade-up"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(-30px)',
                  transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <div style={{ 
                  fontSize: '0.75rem', 
                  letterSpacing: '6px',
                  textTransform: 'uppercase',
                  marginBottom: '1.5rem',
                  color: 'rgba(255,255,255,0.5)',
                  fontWeight: '500'
                }}>
                  Your Bali Story Begins
                </div>
                <h1 
                  className="mb-4" 
                  style={{ 
                    fontSize: 'clamp(3rem, 8vw, 6rem)',
                    fontWeight: '600',
                    color: '#ffffff',
                    letterSpacing: '-3px',
                    lineHeight: '1',
                    marginBottom: '2rem'
                  }}
                >
                  Design Your Destination Wedding
                </h1>
                <p 
                  className="lead mx-auto" 
                  style={{ 
                    fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
                    maxWidth: '700px',
                    color: 'rgba(255,255,255,0.6)',
                    fontWeight: '300',
                    lineHeight: '1.6'
                  }}
                >
                  Tell us your vision and we’ll craft a living plan—venues, vibe, budget, and all the little moments that make it yours.
                </p>
              </div>
            </Col>
          </Row>

          {/* Decorative separator to introduce next chapter */}
          <div style={{ height: '28px', position: 'relative', marginBottom: '1rem' }}>
            <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 100 10" role="presentation" aria-hidden="true">
              <polygon points="0,10 100,0 100,10 0,10" fill="rgba(255,255,255,0.08)" />
            </svg>
          </div>
          {/* Chapter band with higher contrast */}
          <div className="page-section-soft" style={{ background: 'linear-gradient(180deg, rgba(20,22,26,0.85), rgba(16,18,22,0.95))', borderRadius: '16px', padding: '1rem' }}>
          
          {/* Featured Video Hero */}
          <Row className="mb-5 py-4">
            <Col>
              <div className="text-center mb-4">
                <Badge bg="" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', letterSpacing: '3px', textTransform: 'uppercase', padding: '0.6rem 1.25rem', borderRadius: '20px' }}>
                  Featured Story
                </Badge>
                <h2 className="mt-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 5vw, 3rem)', color: '#fff', letterSpacing: '-1px' }}>
                  Experience the Magic
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.55)', maxWidth: '650px', margin: '1rem auto 0', fontSize: '1.1rem', lineHeight: 1.6 }}>
                  Watch how we bring dream weddings to life in Bali.
                </p>
              </div>
              {/* Large Featured Video */}
              <div 
                className="featured-video-card elevate"
                style={{
                  position: 'relative',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  aspectRatio: '16/9',
                  maxWidth: '1000px',
                  margin: '0 auto',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
                }}
                onClick={() => { setVideoModal('8iekOe7fk_Y'); setVideoInfo({ name: 'A Bali Love Story', quote: 'Our dream wedding captured beautifully' }); }}
              >
                <img
                  src="https://img.youtube.com/vi/8iekOe7fk_Y/maxresdefault.jpg"
                  alt="A Bali Love Story"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={(e) => { e.currentTarget.src = 'https://img.youtube.com/vi/8iekOe7fk_Y/hqdefault.jpg'; }}
                />
                {/* Large Play Button */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.25)',
                  transition: 'background 300ms'
                }} className="play-overlay">
                  <div className="play-button" style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.95)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                    transition: 'transform 300ms, box-shadow 300ms'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="#1a1a1a">
                      <polygon points="6,4 20,12 6,20" />
                    </svg>
                  </div>
                </div>
                {/* Featured Badge */}
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  background: 'linear-gradient(135deg, rgba(212,175,55,0.9), rgba(184,134,11,0.9))',
                  color: '#fff',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
                }}>
                  ★ Featured
                </div>
                {/* Info Overlay */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '3rem 2rem 2rem',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)'
                }}>
                  <div style={{ fontSize: '1.5rem', color: '#fff', fontWeight: '600', marginBottom: '0.5rem' }}>A Bali Love Story</div>
                  <div style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>"Our dream wedding captured beautifully"</div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Testimonials Section */}
          <Row className="mb-5 mt-4" ref={(el) => (sectionsRef.current[5] = el)}>
            <Col>
              <div className="text-center mb-4">
                <Badge bg="" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', letterSpacing: '3px', textTransform: 'uppercase', padding: '0.5rem 1rem', borderRadius: '20px' }}>
                  Real Stories
                </Badge>
                <h2 className="mt-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', color: '#fff', letterSpacing: '-1px' }}>
                  Couples Who Said "I Do" in Bali
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.55)', maxWidth: '600px', margin: '0.75rem auto 0', fontSize: '1rem' }}>
                  Hear from our happy couples and see their magical moments captured forever.
                </p>
              </div>
              <div className="testimony-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                {[
                  { img: '/testimony/490024548_18491430520065487_4079131484132641846_n.jpg', name: 'Sarah & Michael', quote: 'Every detail was perfect—beyond our dreams!' },
                  { img: '/testimony/490069917_18491633890065487_5042687420505289654_n.jpg', name: 'Emma & James', quote: 'The team made everything effortless and magical.' },
                  { img: '/testimony/491461215_18492660544065487_3179943495123266314_n.jpg', name: 'Olivia & Noah', quote: 'Our guests are still talking about it!' },
                  { img: '/testimony/491465730_18494032192065487_4006137379908705721_n.jpg', name: 'Ava & Liam', quote: 'Bali was the perfect backdrop for our love story.' },
                  { img: '/testimony/491501815_18492446203065487_1937371356140247060_n.jpg', name: 'Mia & Ethan', quote: 'Professional, caring, and truly talented.' },
                  { img: '/testimony/494988992_18495802771065487_5084116418053153398_n.jpg', name: 'Isabella & Lucas', quote: 'A fairytale come true—thank you!' },
                  { img: '/testimony/500906462_18500221750065487_3901730153196161666_n.jpg', name: 'Charlotte & Henry', quote: 'We felt like royalty on our special day.' }
                ].map((t, i) => (
                  <div 
                    key={`testimony-${i}`} 
                    className="testimony-card elevate" 
                    style={{
                      position: 'relative',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      aspectRatio: '4/5',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      minHeight: '320px',
                      cursor: 'pointer'
                    }}
                    onClick={() => { setLightboxImg(t.img); setLightboxInfo({ name: t.name, quote: t.quote }); }}
                  >
                    <img 
                      src={t.img} 
                      alt={`${t.name} wedding`} 
                      loading="eager" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
                      onError={(e)=>{ console.log('Image failed:', t.img); e.currentTarget.src=''; e.currentTarget.style.background='rgba(255,255,255,0.1)'; }} 
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: '2rem 1.25rem 1.25rem',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 60%, transparent 100%)'
                    }}>
                      <div style={{ fontSize: '0.95rem', color: '#fff', fontWeight: '600', marginBottom: '0.35rem' }}>{t.name}</div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)', fontStyle: 'italic', lineHeight: 1.4 }}>"{t.quote}"</div>
                    </div>
                    {/* Zoom hint icon */}
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.7,
                      transition: 'opacity 200ms'
                    }} className="zoom-hint">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        <line x1="11" y1="8" x2="11" y2="14"/>
                        <line x1="8" y1="11" x2="14" y2="11"/>
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          {/* Watch More Stories - Secondary Videos */}
          <Row className="mb-5 py-4">
            <Col>
              <div className="text-center mb-4">
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.5rem, 4vw, 2rem)', color: '#fff', letterSpacing: '-0.5px' }}>
                  Watch More Stories
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', maxWidth: '500px', margin: '0.75rem auto 0', fontSize: '1rem' }}>
                  More couples sharing their Bali wedding experience
                </p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', maxWidth: '800px', margin: '0 auto', padding: '0 1rem' }}>
                {[
                  { id: 'J9I3O61nAaY', name: 'Forever Starts Here', quote: 'The beginning of our forever' },
                  { id: 'WieCEd7u558', name: 'Paradise Wedding', quote: 'Bali made our dreams come true' }
                ].map((v, i) => (
                  <div
                    key={`more-video-${i}`}
                    className="video-card elevate"
                    style={{
                      position: 'relative',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      aspectRatio: '16/9',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      cursor: 'pointer'
                    }}
                    onClick={() => { setVideoModal(v.id); setVideoInfo({ name: v.name, quote: v.quote }); }}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`}
                      alt={v.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`; }}
                    />
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0,0,0,0.25)',
                      transition: 'background 300ms'
                    }} className="play-overlay">
                      <div className="play-button" style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.95)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 6px 24px rgba(0,0,0,0.3)',
                        transition: 'transform 300ms, box-shadow 300ms'
                      }}>
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="#1a1a1a">
                          <polygon points="6,4 20,12 6,20" />
                        </svg>
                      </div>
                    </div>
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: '2rem 1.25rem 1.25rem',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 50%, transparent 100%)'
                    }}>
                      <div style={{ fontSize: '1.05rem', color: '#fff', fontWeight: '600', marginBottom: '0.3rem' }}>{v.name}</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.75)', fontStyle: 'italic' }}>"{v.quote}"</div>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          {/* Google Reviews Section */}
          <Row className="mb-5 py-4">
            <Col>
              <div className="text-center mb-4">
                {/* Google Rating Header */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', background: 'rgba(255,255,255,0.05)', padding: '0.75rem 1.5rem', borderRadius: '40px', marginBottom: '1rem' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#fff', fontSize: '1.5rem', fontWeight: '700' }}>4.9</span>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {[1,2,3,4,5].map(i => (
                        <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill="#FBBC05">
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                        </svg>
                      ))}
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem' }}>(76 reviews)</span>
                  </div>
                </div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.5rem, 4vw, 2rem)', color: '#fff', letterSpacing: '-0.5px', marginTop: '0.5rem' }}>
                  What Couples Say on Google
                </h3>
              </div>
              
              {/* Review Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', maxWidth: '1000px', margin: '0 auto', padding: '0 1rem' }}>
                {[
                  {
                    name: 'Denis Vodisek',
                    location: 'Hong Kong',
                    rating: 5,
                    text: 'We are beyond grateful to the entire BALIVIP Wedding team for making our special day truly unforgettable. From the very beginning, they supported us at every single moment, ensuring that every detail was executed to absolute perfection. When we saw all the decorations and our vision come to life on the wedding day, we were completely in awe.',
                    highlight: 'A special and huge thank you to our main planner, Jennifer, who was always on top of everything!'
                  },
                  {
                    name: 'Sara Mardam-Bey',
                    location: 'International',
                    rating: 5,
                    text: 'We cannot begin to express how grateful we are to have worked with the BALI VIP Wedding planning team. They guided us through 18 months of planning, taking care to respond to all of our questions every step of the way. Our wedding was a complicated event with 138 guests flying in from all over the world.',
                    highlight: 'Our guests continued to tell us it was the best wedding they have ever attended!'
                  }
                ].map((review, i) => (
                  <div
                    key={`review-${i}`}
                    className="elevate"
                    style={{
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '20px',
                      padding: '1.75rem',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Quote mark decoration */}
                    <div style={{ position: 'absolute', top: '12px', right: '20px', fontSize: '4rem', color: 'rgba(255,255,255,0.05)', fontFamily: 'Georgia, serif', lineHeight: 1 }}>"</div>
                    
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      {/* Avatar */}
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #4285F4, #34A853)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: '600',
                        fontSize: '1.1rem'
                      }}>
                        {review.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontWeight: '600', fontSize: '1rem' }}>{review.name}</div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>{review.location}</div>
                      </div>
                      {/* Google G icon */}
                      <svg width="20" height="20" viewBox="0 0 24 24" style={{ opacity: 0.6 }}>
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    
                    {/* Stars */}
                    <div style={{ display: 'flex', gap: '3px', marginBottom: '1rem' }}>
                      {[1,2,3,4,5].map(s => (
                        <svg key={s} width="16" height="16" viewBox="0 0 24 24" fill="#FBBC05">
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                        </svg>
                      ))}
                    </div>
                    
                    {/* Review Text */}
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '1rem' }}>
                      {review.text}
                    </p>
                    
                    {/* Highlight Quote */}
                    <div style={{
                      background: 'rgba(66,133,244,0.1)',
                      borderLeft: '3px solid #4285F4',
                      padding: '0.75rem 1rem',
                      borderRadius: '0 8px 8px 0',
                      fontStyle: 'italic',
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '0.9rem'
                    }}>
                      "{review.highlight}"
                    </div>
                  </div>
                ))}
              </div>
              
              {/* View All Link */}
              <div className="text-center mt-4">
                <a
                  href="https://maps.app.goo.gl/pnTxvR4dvmMf4Yw19"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '30px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.03)',
                    transition: 'all 200ms'
                  }}
                  className="elevate"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  View all 76 reviews on Google Maps
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7V17"/>
                  </svg>
                </a>
              </div>
            </Col>
          </Row>

  {/* Why BALIVIP Section - Subtle Contrast */}
  <Row className="mb-4 section-fade-up sj-stagger" ref={(el) => (sectionsRef.current[1] = el)}>
          <Col>
            <div style={{
              borderRadius: '24px',
              background: 'linear-gradient(145deg, rgba(35,35,40,0.95) 0%, rgba(28,28,32,0.98) 100%)',
              border: '1px solid rgba(212,175,55,0.12)',
              padding: '3.5rem 2.5rem',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)'
            }}>
              {/* Decorative corner accents */}
              <div style={{ position: 'absolute', top: 0, left: 0, width: '120px', height: '120px', background: 'radial-gradient(ellipse at top left, rgba(212,175,55,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: 0, right: 0, width: '120px', height: '120px', background: 'radial-gradient(ellipse at bottom right, rgba(212,175,55,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
              
              {/* Subtle top border glow */}
              <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)' }} />
              
              {/* Small decorative element */}
              <div style={{ marginBottom: '1rem' }}>
                <span style={{ 
                  display: 'inline-block',
                  color: '#d4af37',
                  fontSize: '0.75rem',
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  fontWeight: 500
                }}>
                  ✦ The BALIVIP Promise ✦
                </span>
              </div>
              
              <h3 style={{ 
                color: '#fff', 
                fontFamily: "'Playfair Display', serif", 
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                marginBottom: '0.5rem',
                fontWeight: 500,
                letterSpacing: '-0.02em'
              }}>
                Where Dreams Meet
              </h3>
              <h3 style={{ 
                background: 'linear-gradient(135deg, #d4af37 0%, #f4e4ba 50%, #d4af37 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontFamily: "'Playfair Display', serif", 
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                marginBottom: '2rem',
                fontWeight: 600,
                letterSpacing: '-0.02em'
              }}>
                Destination Excellence
              </h3>
              
              {/* Divider */}
              <div style={{ 
                width: '80px', 
                height: '1px', 
                background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent)', 
                margin: '0 auto 2rem'
              }} />
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1.25rem',
                maxWidth: '1000px',
                margin: '0 auto'
              }}>
                {[
                  { icon: '🏝️', text: 'Exclusive Bali Venues', sub: 'Hand-selected sanctuaries' },
                  { icon: '💎', text: 'Luxury, Not Markup', sub: 'Direct partnerships' },
                  { icon: '🤝', text: 'Global Standards', sub: 'Local expertise' },
                  { icon: '📸', text: 'Iconic Photography', sub: 'Award-winning artists' },
                  { icon: '🎯', text: 'Bespoke Design', sub: 'Your vision, perfected' },
                  { icon: '✨', text: 'White-Glove Service', sub: 'Every detail handled' },
                  { icon: '🌺', text: 'Authentic Bali', sub: 'Cultural elegance' },
                  { icon: '💬', text: '24/7 Concierge', sub: 'Always at your side' }
                ].map((item, idx) => (
                  <div 
                    key={idx}
                    className="sj-fade-up"
                    style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '1.25rem 1rem',
                      borderRadius: '16px',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      transition: 'all 0.3s ease',
                      transitionDelay: `${idx * 60}ms`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(180deg, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0.03) 100%)';
                      e.currentTarget.style.borderColor = 'rgba(212,175,55,0.2)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <span style={{ fontSize: '1.5rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>{item.icon}</span>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem', textAlign: 'center' }}>{item.text}</span>
                    <span style={{ color: 'rgba(212,175,55,0.7)', fontSize: '0.75rem', fontWeight: 400, letterSpacing: '0.02em' }}>{item.sub}</span>
                  </div>
                ))}
              </div>
              
              <p style={{ 
                color: 'rgba(255,255,255,0.5)', 
                marginTop: '2.5rem', 
                fontSize: '1rem',
                fontFamily: "'Playfair Display', serif",
                fontStyle: 'italic',
                letterSpacing: '0.02em'
              }}>
                "From intimate elopements to grand celebrations—<br/>
                <span style={{ color: 'rgba(212,175,55,0.8)' }}>we craft moments that last forever.</span>"
              </p>
            </div>
          </Col>
          </Row>

          {/* ===== YOUR DREAM WEDDING CTA ===== */}
          <Row className="mb-5 py-4">
            <Col>
              <div 
                className="elevate"
                onClick={() => setShowBudgetOptimizer(true)}
                style={{
                  background: 'linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(180,150,50,0.08) 50%, rgba(212,175,55,0.12) 100%)',
                  border: '1px solid rgba(212,175,55,0.25)',
                  borderRadius: '24px',
                  padding: '3rem 2.5rem',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 300ms',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
                }}
              >
                {/* Animated gold shimmer overlay */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.08), transparent)',
                  animation: 'shimmer 3s infinite',
                  pointerEvents: 'none'
                }} />
                
                <Row className="align-items-center">
                  <Col lg={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #d4af37, #c9a227)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.75rem',
                        boxShadow: '0 8px 24px rgba(212,175,55,0.3)'
                      }}>
                        💍
                      </div>
                      <div>
                        <Badge bg="" style={{ marginBottom: '0.25rem', fontSize: '0.65rem', background: 'rgba(212,175,55,0.2)', border: '1px solid rgba(212,175,55,0.4)', color: '#d4af37' }}>YOUR JOURNEY BEGINS</Badge>
                        <h3 style={{ color: '#fff', margin: 0, fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.5rem, 3.5vw, 2rem)' }}>
                          Your Dream Wedding Starts Here
                        </h3>
                      </div>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '1.1rem', lineHeight: 1.7, maxWidth: '600px' }}>
                      Ready to turn your vision into reality? Tell us your budget and preferences — 
                      we'll craft personalized packages tailored just for you. No pressure, just possibilities.
                    </p>
                    <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1.25rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                      <span>✨ Instant personalized packages</span>
                      <span>💎 Transparent pricing</span>
                      <span>🌴 Bali's best venues</span>
                    </div>
                  </Col>
                  <Col lg={4} className="text-lg-end mt-4 mt-lg-0">
                    <Button
                      size="lg"
                      onClick={() => setShowBudgetOptimizer(true)}
                      style={{
                        background: 'linear-gradient(135deg, #d4af37 0%, #c9a227 100%)',
                        border: 'none',
                        borderRadius: '30px',
                        padding: '1.1rem 2.5rem',
                        fontSize: '1.15rem',
                        fontWeight: '600',
                        color: '#1a1a1a',
                        boxShadow: '0 8px 32px rgba(212,175,55,0.35)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Let's Begin
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </Button>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          </div>
        
          {/* Selection & Estimate section removed - now using Smart Budget Optimizer CTA above */}

      </Container>
      </div>

      {/* Proposal Modal */}
      <Modal show={showProposal} onHide={() => setShowProposal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Generated Proposal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', background: 'rgba(0,0,0,0.03)', padding: '1rem', borderRadius: '8px' }}>
            {proposalText}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProposal(false)}>Close</Button>
          <Button variant="primary" onClick={downloadProposal}>Download</Button>
        </Modal.Footer>
      </Modal>
      {/* Venue Catalog Modal */}
      <Modal show={showVenueCatalog} onHide={() => setShowVenueCatalog(false)} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Venue Catalog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={3}>
              <Card className="border-0" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <Card.Body>
                  <div className="mb-3"><strong>Filters</strong></div>
                  <Form.Group className="mb-3">
                    <Form.Label>Vibe</Form.Label>
                    <Form.Select value={venueFilters.vibe} onChange={(e)=>setVenueFilters({ ...venueFilters, vibe: e.target.value })}>
                      <option value="any">Any</option>
                      <option value="beach">Beach</option>
                      <option value="cliff">Cliff</option>
                      <option value="garden">Garden</option>
                      <option value="villa">Villa</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Guest Count</Form.Label>
                    <div className="d-flex" style={{ gap: '0.5rem' }}>
                      <Form.Control type="number" value={venueFilters.minGuests} onChange={(e)=>setVenueFilters({ ...venueFilters, minGuests: parseInt(e.target.value||'0') })} />
                      <Form.Control type="number" value={venueFilters.maxGuests} onChange={(e)=>setVenueFilters({ ...venueFilters, maxGuests: parseInt(e.target.value||'0') })} />
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Budget Tier</Form.Label>
                    <Form.Select value={venueFilters.tier} onChange={(e)=>setVenueFilters({ ...venueFilters, tier: e.target.value })}>
                      <option value="any">Any</option>
                      <option value="essential">Essential</option>
                      <option value="signature">Signature</option>
                      <option value="prestige">Prestige</option>
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            <Col md={9}>
              <Row className="g-3">
                {filteredVenues.map(v => (
                  <Col key={v.id} md={6}>
                    <Card className="h-100 border-0" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <Card.Img variant="top" src={v.img} alt={v.name} onError={(e)=>{e.currentTarget.style.display='none';}} />
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <strong>{v.name}</strong>
                            <div className="text-muted" style={{ fontSize: '0.9rem' }}>{v.vibe} • Capacity {v.capacity}</div>
                          </div>
                          <Badge bg="">{v.availability}</Badge>
                        </div>
                        <div className="mb-2">
                          {v.labels.map(l => (
                            <Badge key={l} className="me-1" bg="secondary">{l}</Badge>
                          ))}
                        </div>
                        <div className="mb-2">Pricing band: ${v.basePrice.toLocaleString()}+</div>
                        <ListGroup variant="flush" className="mb-3">
                          {v.notes.map((n,i)=>(<ListGroup.Item key={i}>{n}</ListGroup.Item>))}
                        </ListGroup>
                        <div className="d-flex">
                          <Button size="sm" variant="outline-primary" onClick={()=>setCompareList(prev=> prev.find(x=>x.id===v.id)? prev : [...prev, v])}>Add to Compare</Button>
                          <Button size="sm" className="ms-2" variant="primary" onClick={()=>setEstimate(prev=>({ ...prev, venue: v.vibe }))}>Add to Plan</Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setShowVenueCatalog(false)}>Close</Button>
          <Button variant="primary" disabled={compareList.length<2} onClick={()=>setShowCompare(true)}>Compare Selected ({compareList.length})</Button>
        </Modal.Footer>
      </Modal>

      {/* Detailed Plan Modal */}
      <Modal show={showDetailedPlan} onHide={() => setShowDetailedPlan(false)} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Your Detailed Wedding Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {/* Observe end-of-modal to reveal CTA */}
          {useEffect(() => {
            const anchor = document.getElementById('contact-cta-anchor');
            if (!anchor) return;
            const io = new IntersectionObserver((entries) => {
              const entry = entries[0];
              setShowBottomCTA(entry.isIntersecting);
            }, { root: null, threshold: 0.3 });
            io.observe(anchor);
            return () => io.disconnect();
          }, [showDetailedPlan])}
          {/* Floating sticky total panel (persists on scroll) */}
          <div className="plan-sticky-total d-none d-md-block" style={{ position: 'fixed', top: '88px', right: '4%', width: '420px', maxWidth: '96vw', zIndex: 1056 }}>
            <div className="p-4 text-center" style={{ background: 'rgba(40, 40, 40, 0.78)', borderRadius: '20px', boxShadow: '0 12px 28px rgba(0,0,0,0.45)', border: '1px solid rgba(59,130,246,0.25)', backdropFilter: 'blur(12px)' }}>
              <div className="mb-2" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', letterSpacing: '2px', fontWeight: 600, textTransform: 'uppercase' }}>Total Investment</div>
              <div className="fw-bold" style={{ fontSize: 'clamp(2rem, 5vw, 2.6rem)', letterSpacing: '-0.5px', lineHeight: 1, color: '#ffffff' }}>
                ${totalCost.toLocaleString()}
              </div>
              <small className="d-block mt-2" style={{ color: 'rgba(255,255,255,0.55)' }}>AI-powered estimate</small>
              {(() => {
                const palette = ['#7aa6ff', '#6ed0b6', '#f0b86e', '#b19cff', '#f39bc2'];
                const segments = [
                  { key: 'venue', label: 'Venue', value: pricing.venue[estimate.venue] || 0, color: palette[0] },
                  { key: 'catering', label: 'Catering', value: (pricing.catering[estimate.catering] || 0) * estimate.guests, color: palette[1] },
                  { key: 'decoration', label: 'Decoration', value: pricing.decoration[estimate.decoration] || 0, color: palette[2] },
                  { key: 'photography', label: 'Photography', value: pricing.photography[estimate.photography] || 0, color: palette[3] },
                  { key: 'entertainment', label: 'Entertainment', value: pricing.entertainment[estimate.entertainment] || 0, color: palette[4] }
                ];
                const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
                let cumulative = 0;
                const outerR = 58;
                const innerR = 40;
                const cx = 70;
                const cy = 70;
                return (
                  <div className="mt-3 d-flex flex-column align-items-center">
                    <svg width={150} height={150} viewBox={`0 0 ${cx*2} ${cy*2}`} role="img" aria-label="Estimate breakdown">
                      <circle cx={cx} cy={cy} r={outerR} fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
                      {segments.map((seg) => {
                        const startAngle = (cumulative / total) * 2 * Math.PI;
                        const slice = (seg.value / total) * 2 * Math.PI;
                        const endAngle = startAngle + slice;
                        cumulative += seg.value;
                        const x1o = cx + outerR * Math.cos(startAngle);
                        const y1o = cy + outerR * Math.sin(startAngle);
                        const x2o = cx + outerR * Math.cos(endAngle);
                        const y2o = cy + outerR * Math.sin(endAngle);
                        const x1i = cx + innerR * Math.cos(endAngle);
                        const y1i = cy + innerR * Math.sin(endAngle);
                        const x2i = cx + innerR * Math.cos(startAngle);
                        const y2i = cy + innerR * Math.sin(startAngle);
                        const largeArc = slice > Math.PI ? 1 : 0;
                        return (
                          <path key={seg.key}
                            d={`M ${x1o} ${y1o} A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2o} ${y2o} L ${x1i} ${y1i} A ${innerR} ${innerR} 0 ${largeArc} 0 ${x2i} ${y2i} Z`}
                            fill={seg.color}
                            opacity={0.85}
                          />
                        );
                      })}
                    </svg>
                    <div className="mt-2 w-100" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem' }}>
                      {segments.map(s => {
                        const pct = Math.round((s.value / total) * 100);
                        return (
                          <div key={`legend-${s.key}`} className="d-flex align-items-center" style={{ gap: '0.4rem', color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap' }}>
                            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.color }} />
                            <small>{s.label} — {pct}%</small>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
          {/* Hero storytelling header */}
          <div className="plan-hero" style={{ position: 'relative', overflow: 'hidden', background: `linear-gradient(180deg, ${theme.accent}, rgba(20,20,20,0.9))` }}>
            <div className="d-flex" style={{ gap: '0.75rem', padding: '0.75rem' }}>
              {['/images/venue.jpeg','/images/flowers.jpeg','/images/baliExperience.jpg','/images/hawaii.jpg'].map((src,i)=> (
                <div key={`plan-hero-${i}`} className="plan-hero-tile" style={{ flex: 1, minHeight: '160px', borderRadius: '12px', overflow: 'hidden', background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}44` }}>
                  <img src={src} alt="proposal visual" loading="lazy" onError={(e)=>{e.currentTarget.style.display='none';}} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
            </div>
            <div style={{ position: 'absolute', inset: '0', background: `linear-gradient(180deg, rgba(0,0,0,0) 40%, ${theme.overlay} 100%)` }} />
            <div style={{ position: 'absolute', bottom: '1rem', left: '1.25rem', right: '1.25rem' }}>
              <h3 className="mb-1 plan-hero-title" style={{ color: '#fff', textShadow: '0 2px 12px rgba(0,0,0,0.6)', letterSpacing: '-0.5px' }}>
                {estimate.venue?.charAt(0).toUpperCase() + estimate.venue?.slice(1)} Story • {estimate.guests} Guests
              </h3>
              <div className="text-white-50">{tiers[tier]?.label} Experience — {tiers[tier]?.desc}</div>
            </div>
          </div>
          {/* Decorative separator for contrast (angled band) */}
          <div style={{ height: '28px', position: 'relative' }}>
            <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 100 10" role="presentation" aria-hidden="true">
              <polygon points="0,10 100,0 100,10 0,10" fill={`${theme.secondary}33`} />
            </svg>
          </div>
          {/* Section band: 1. Overview (must-haves) */}
          <div className="plan-section plan-section-soft">
            <Container className="py-5">
              <div className="section-header"><span className="dot" style={{ background: theme.primary }}></span><h4 className="title">Overview</h4><div className="line" style={{ background: `${theme.secondary}66` }}></div></div>
              <Row className="g-4 sj-stagger in-view">
                <Col md={8}>
                  <Card className="border-0 elevate" style={{ borderRadius: '18px', overflow: 'hidden', background: `linear-gradient(135deg, ${theme.primary}22, rgba(255,255,255,0.06))`, border: `1px solid ${theme.secondary}44` }}>
                    <Card.Body>
                      {/* Luxe header row */}
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center" style={{ gap: '0.75rem' }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${theme.primary}33`, border: `1px solid ${theme.secondary}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span role="img" aria-label="ring">💍</span>
                          </div>
                          <div>
                            <div className="fw-semibold" style={{ letterSpacing: '-0.2px' }}>{estimate.venue?.charAt(0).toUpperCase() + estimate.venue?.slice(1)} Celebration</div>
                            <small className="text-muted">{estimate.guests} guests • {estimate.catering} catering</small>
                          </div>
                        </div>
                        <Badge bg="" style={{ background: `${theme.primary}22`, color: '#fff', border: `1px solid ${theme.primary}66`, borderRadius: '999px' }}>{tiers[tier]?.label || tier}</Badge>
                      </div>

                      {/* Metrics strip */}
                      <div className="d-flex flex-wrap" style={{ gap: '1rem' }}>
                        <div className="p-2 px-3" style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, borderRadius: '10px' }}>
                          <small className="text-muted">Venue</small>
                          <div className="fw-semibold" style={{ textTransform: 'capitalize' }}>{estimate.venue}</div>
                        </div>
                        <div className="p-2 px-3" style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, borderRadius: '10px' }}>
                          <small className="text-muted">Catering</small>
                          <div className="fw-semibold" style={{ textTransform: 'capitalize' }}>{estimate.catering}</div>
                        </div>
                        <div className="p-2 px-3" style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, borderRadius: '10px' }}>
                          <small className="text-muted">Tier</small>
                          <div className="fw-semibold">{tiers[tier]?.label || tier}</div>
                        </div>
                        <div className="p-2 px-3" style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, borderRadius: '10px' }}>
                          <small className="text-muted">Guests</small>
                          <div className="fw-semibold">{estimate.guests}</div>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="my-3" style={{ height: 1, background: `linear-gradient(90deg, transparent, ${theme.secondary}55, transparent)` }} />

                      {/* Highlights with icons */}
                      <Row className="g-3">
                        {[
                          { icon: '🏝️', label: 'Scenic setting', desc: 'Curated venue ambiance and seamless coordination' },
                          { icon: '🍽️', label: 'Signature dining', desc: 'Tailored menu and premium service staff' },
                          { icon: '🌸', label: 'Designer décor', desc: 'Floral styling, ambient lighting, refined signage' },
                          { icon: '📸', label: 'Editorial capture', desc: 'Edited gallery and highlight reel' },
                          { icon: '🎵', label: 'Live atmosphere', desc: 'Sound system, MC, dance floor lighting' },
                        ].map((h,i)=> (
                          <Col md={6} key={`ovr-hi-${i}`}>
                            <div className="d-flex" style={{ gap: '0.6rem' }}>
                              <div style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{h.icon}</div>
                              <div>
                                <div className="fw-semibold" style={{ letterSpacing: '-0.2px' }}>{h.label}</div>
                                <small className="text-muted">{h.desc}</small>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 elevate plan-right-card" style={{ background: 'linear-gradient(180deg, rgba(20,22,26,0.92), rgba(18,20,24,0.98))', border: `1px solid ${theme.secondary}55`, borderRadius: '18px', color: '#eef2f7' }}>
                    <Card.Body>
                      <div className="mb-2" style={{ fontSize: '0.8rem', color: 'rgba(238,242,247,0.85)', letterSpacing: '1px', textTransform: 'uppercase' }}>Total Investment</div>
                      <div className="fw-bold mb-1" style={{ fontSize: '2.2rem', letterSpacing: '-0.5px' }}>${totalCost.toLocaleString()}</div>
                      <small className="text-muted d-block mb-3">Inclusive AI-powered estimate</small>
                      {mustHave.budget && (
                        <div className="p-2" style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', border: `1px solid ${theme.secondary}33` }}>
                          <small className="d-block mb-1">Budget target: ${mustHave.budget.toLocaleString()}</small>
                          <small style={{ color: totalCost <= mustHave.budget ? '#16a34a' : '#dc2626' }}>{totalCost <= mustHave.budget ? 'Within target' : `Exceeds by $${(totalCost - mustHave.budget).toLocaleString()}`}</small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>

          {/* 2-6. Individual sections: Venue, Catering, Decoration, Photography, Entertainment */}
          {[
            { key:'venue', title:'Venue', img:'/images/venue.jpeg', figure:(pricing.venue[estimate.venue]||0), value: estimate.venue },
            { key:'catering', title:'Catering', img:'/images/food.webp', figure:((pricing.catering[estimate.catering]||0)*estimate.guests), value: estimate.catering },
            { key:'decoration', title:'Decoration', img:'/images/flowers.jpeg', figure:(pricing.decoration[estimate.decoration]||0), value: estimate.decoration },
            { key:'photography', title:'Photography', img:'/images/robotWedding.jpg', figure:(pricing.photography[estimate.photography]||0), value: estimate.photography },
            { key:'entertainment', title:'Entertainment', img:'/images/hawaii.jpg', figure:(pricing.entertainment[estimate.entertainment]||0), value: estimate.entertainment },
          ].map((sec,i)=> (
            <div key={`band-${sec.key}`} className={`plan-section ${i%2===0? 'plan-section-soft':'plan-section-light'}`}>
              <Container className="py-5">
                <div className="section-header"><span className="dot" style={{ background: theme.primary }}></span><h4 className="title">{sec.title}</h4><div className="line" style={{ background: `${theme.secondary}66` }}></div></div>
                <Row className="g-4 sj-stagger in-view">
                  <Col md={8}>
                    <Card className="border-0 plan-tile" style={{ borderRadius: '16px', overflow: 'hidden', border: `1px solid ${theme.secondary}44`, background: 'rgba(255,255,255,0.06)' }}>
                      <Card.Body>
                        <Row className="g-3">
                          <Col md={7}>
                            <div style={{ height: '220px', position: 'relative', borderRadius:'12px', overflow:'hidden' }}>
                              <img src={sec.img} alt={sec.title} loading="lazy" onError={(e)=>{e.currentTarget.style.display='none';}} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                              <div style={{ position:'absolute', inset:0, background:`linear-gradient(180deg, rgba(0,0,0,0) 50%, ${theme.overlay} 100%)` }} />
                            </div>
                          </Col>
                          <Col md={5}>
                            <div className="d-flex justify-content-between align-items-end">
                              <strong className="text-white">{sec.title}</strong>
                              <span className="text-white-50">${sec.figure.toLocaleString()}</span>
                            </div>
                            <small className="d-block text-white-50" style={{ textTransform:'capitalize' }}>{sec.value} selection</small>
                            <div className="mt-3">
                              <Button size="sm" variant="outline-light" onClick={()=>{
                                // Jump to swap tab logic inline (keep simple: open swaps band at bottom)
                                const el = document.getElementById('swap-options');
                                el?.scrollIntoView({ behavior:'smooth', block:'start' });
                              }}>Adjust options</Button>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 elevate plan-right-card" style={{ background: 'linear-gradient(180deg, rgba(18,20,24,0.96), rgba(12,14,18,0.98))', border: `1px solid ${theme.secondary}66`, borderRadius: '18px', color: '#eef2f7' }}>
                      <Card.Body>
                        {/* Luxe pill heading */}
                        <div className="d-inline-flex align-items-center px-3 py-1 mb-3" style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}44`, borderRadius: '999px', color: 'rgba(238,242,247,0.9)', letterSpacing: '0.6px' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: theme.primary, marginRight: 8 }} />
                          Included
                        </div>
                        {/* Luxe list with icon bullets */}
                        <div className="d-grid" style={{ gap: '0.75rem' }}>
                          {(() => {
                            if (sec.key !== 'venue') return null;
                            const items = [
                              {icon:'🎯', text:'Coordination & setup'},
                              {icon:'🤝', text:'Vendor liaison'},
                              {icon:'🌸', text:'Décor placement'},
                            ];
                            return items.map((item,idx)=> (
                            <div key={`inc-v-${idx}`} className="d-flex align-items-start" style={{ gap:'0.6rem' }}>
                              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.icon}</div>
                              <div className="flex-grow-1">
                                <div style={{ color:'#eef2f7' }}>{item.text}</div>
                                <div style={{ height:1, background:`linear-gradient(90deg, ${theme.secondary}55, transparent)`, marginTop: '0.5rem' }} />
                              </div>
                            </div>
                            ));
                          })()}

                          {(() => {
                            if (sec.key !== 'catering') return null;
                            const items = [
                              {icon:'🍽️', text:'Menu & tasting'},
                              {icon:'👩‍🍳', text:'Service staff'},
                              {icon:'🍷', text:'Tableware basics'},
                            ];
                            return items.map((item,idx)=> (
                            <div key={`inc-c-${idx}`} className="d-flex align-items-start" style={{ gap:'0.6rem' }}>
                              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.icon}</div>
                              <div className="flex-grow-1">
                                <div style={{ color:'#eef2f7' }}>{item.text}</div>
                                <div style={{ height:1, background:`linear-gradient(90deg, ${theme.secondary}55, transparent)`, marginTop: '0.5rem' }} />
                              </div>
                            </div>
                            ));
                          })()}

                          {(() => {
                            if (sec.key !== 'decoration') return null;
                            const items = [
                              {icon:'🌺', text:'Floral styling'},
                              {icon:'✨', text:'Ambient lighting'},
                              {icon:'🪧', text:'Refined signage'},
                            ];
                            return items.map((item,idx)=> (
                            <div key={`inc-d-${idx}`} className="d-flex align-items-start" style={{ gap:'0.6rem' }}>
                              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.icon}</div>
                              <div className="flex-grow-1">
                                <div style={{ color:'#eef2f7' }}>{item.text}</div>
                                <div style={{ height:1, background:`linear-gradient(90deg, ${theme.secondary}55, transparent)`, marginTop: '0.5rem' }} />
                              </div>
                            </div>
                            ));
                          })()}

                          {(() => {
                            if (sec.key !== 'photography') return null;
                            const items = [
                              {icon:'🕒', text:'Coverage hours'},
                              {icon:'🖼️', text:'Edited gallery'},
                              {icon:'🎞️', text:'Highlight reel'},
                            ];
                            return items.map((item,idx)=> (
                            <div key={`inc-p-${idx}`} className="d-flex align-items-start" style={{ gap:'0.6rem' }}>
                              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.icon}</div>
                              <div className="flex-grow-1">
                                <div style={{ color:'#eef2f7' }}>{item.text}</div>
                                <div style={{ height:1, background:`linear-gradient(90deg, ${theme.secondary}55, transparent)`, marginTop: '0.5rem' }} />
                              </div>
                            </div>
                            ));
                          })()}

                          {(() => {
                            if (sec.key !== 'entertainment') return null;
                            const items = [
                              {icon:'🔊', text:'Sound system'},
                              {icon:'🎤', text:'MC coordination'},
                              {icon:'💡', text:'Dance floor lighting'},
                            ];
                            return items.map((item,idx)=> (
                            <div key={`inc-e-${idx}`} className="d-flex align-items-start" style={{ gap:'0.6rem' }}>
                              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}33`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.icon}</div>
                              <div className="flex-grow-1">
                                <div style={{ color:'#eef2f7' }}>{item.text}</div>
                                <div style={{ height:1, background:`linear-gradient(90deg, ${theme.secondary}55, transparent)`, marginTop: '0.5rem' }} />
                              </div>
                            </div>
                            ));
                          })()}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Container>
            </div>
          ))}

          {/* 7. Swap options */}
          <div id="swap-options" className="plan-section plan-section-soft">
            <Container className="py-5">
              <div className="section-header"><span className="dot" style={{ background: theme.primary }}></span><h4 className="title">Swap options</h4><div className="line" style={{ background: `${theme.secondary}66` }}></div></div>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Venue</Form.Label>
                    <Form.Select value={estimate.venue} onChange={(e)=>setEstimate(prev=>({ ...prev, venue: e.target.value }))}>
                      <option value="beachfront">Beachfront</option>
                      <option value="garden">Garden</option>
                      <option value="villa">Villa</option>
                      <option value="cliff">Cliff</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Catering</Form.Label>
                    <Form.Select value={estimate.catering} onChange={(e)=>setEstimate(prev=>({ ...prev, catering: e.target.value }))}>
                      <option value="standard">Standard (${pricing.catering.standard}/guest)</option>
                      <option value="premium">Premium (${pricing.catering.premium}/guest)</option>
                      <option value="luxury">Luxury (${pricing.catering.luxury}/guest)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Decoration</Form.Label>
                    <Form.Select value={estimate.decoration} onChange={(e)=>setEstimate(prev=>({ ...prev, decoration: e.target.value }))}>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                      <option value="luxury">Luxury</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Photography</Form.Label>
                    <Form.Select value={estimate.photography} onChange={(e)=>setEstimate(prev=>({ ...prev, photography: e.target.value }))}>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                      <option value="luxury">Luxury</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Entertainment</Form.Label>
                    <Form.Select value={estimate.entertainment} onChange={(e)=>setEstimate(prev=>({ ...prev, entertainment: e.target.value }))}>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                      <option value="luxury">Luxury</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Guests</Form.Label>
                    <Form.Control type="number" min={20} max={300} value={estimate.guests} onChange={(e)=>setEstimate(prev=>({ ...prev, guests: parseInt(e.target.value||'0') }))} />
                  </Form.Group>
                </Col>
              </Row>
            </Container>
          </div>

          {/* 8. Story / Narrative */}
          <div className="plan-section plan-section-light">
            <Container className="py-5">
              <div className="section-header"><span className="dot" style={{ background: theme.primary }}></span><h4 className="title">Story & Narrative</h4><div className="line" style={{ background: `${theme.secondary}66` }}></div></div>
              <Row className="g-4 sj-stagger in-view">
                <Col md={8}>
                  <Card className="border-0" style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${theme.secondary}44`, borderRadius: '16px' }}>
                    <Card.Body>
                      <p className="text-muted">A {estimate.venue} celebration for {estimate.guests} guests with {estimate.catering} catering. {tiers[tier]?.desc} Experience ties it all together—curated décor, {estimate.photography} photography, and {estimate.entertainment} entertainment for a seamless day.</p>
                      <Row className="g-3">
                        {[ '/images/flowers.jpeg','/images/venue.jpeg','/images/baliExperience.jpg' ].map((src,i)=> (
                          <Col md={4} key={`caption-final-${i}`}>
                            <div className="plan-caption" style={{ borderRadius:'12px', overflow:'hidden', border:`1px solid ${theme.secondary}44`, background: 'rgba(255,255,255,0.06)' }}>
                              <img src={src} alt="caption" loading="lazy" onError={(e)=>{e.currentTarget.style.display='none';}} style={{ width:'100%', height:'140px', objectFit:'cover' }} />
                              <div className="p-2">
                                <small className="text-muted">A moment we envision—soft florals, golden hour, and effortless flow.</small>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 elevate plan-right-card" style={{ background: 'linear-gradient(180deg, rgba(20,22,26,0.92), rgba(18,20,24,0.98))', border: `1px solid ${theme.secondary}55`, borderRadius: '16px', color: '#eef2f7' }}>
                    <Card.Body>
                      <h6 className="mb-2" style={{ color: 'rgba(238,242,247,0.9)' }}>Contact</h6>
                      <p className="text-muted">Ready to proceed? Our team will tailor the final line‑items and schedule a discovery call.</p>
                      <Button className="w-100" variant="primary" onClick={()=>{ setShowDetailedPlan(false); setShowContactModal(true); }}>Contact our team</Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
          {/* Anchor near end to trigger CTA reveal */}
          <div id="contact-cta-anchor" style={{ height: '1px' }} />
          {/* Decorative separator removed with Narrative & Captions section */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setShowDetailedPlan(false)}>Close</Button>
        </Modal.Footer>
        {/* Floating bottom CTA when near end */}
        {showBottomCTA && (
          <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', zIndex: 1060 }}>
            <div className="d-flex align-items-center shadow" style={{ gap: '0.75rem', background: 'rgba(18,20,24,0.95)', border: '1px solid rgba(59,130,246,0.35)', color: '#eef2f7', borderRadius: '999px', padding: '0.5rem 0.8rem' }}>
              <span className="d-none d-md-inline" style={{ color: 'rgba(238,242,247,0.85)' }}>Next step:</span>
              <strong>Contact our team</strong>
              <Button size="sm" variant="primary" onClick={()=>{ setShowDetailedPlan(false); setShowContactModal(true); }}>
                Contact
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Global page bottom CTA removed - using Smart Budget Optimizer instead */}

      {/* Invisible anchor near end of page content to trigger CTA */}
      <div id="page-cta-anchor" style={{ height: '1px' }} />

      {/* Contact Modal */}
      <Modal show={showContactModal} onHide={()=> { setShowContactModal(false); setContactSubmitted(false); }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{contactSubmitted ? 'We’ve got your details' : 'Contact our team'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          <div className="plan-section plan-section-light">
            <Container className="py-4">
              {!contactSubmitted ? (
              <Row className="g-4">
                <Col md={6}>
                  <Card className="border-0 elevate plan-right-card" style={{ background: 'linear-gradient(180deg, rgba(20,22,26,0.92), rgba(18,20,24,0.98))', border: '1px solid rgba(59,130,246,0.35)', borderRadius: '16px', color: '#eef2f7' }}>
                    <Card.Body>
                      <div className="d-inline-flex align-items-center px-3 py-1 mb-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '999px' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#25D366', marginRight: 8 }} />
                        WhatsApp
                      </div>
                      <div className="d-flex align-items-center" style={{ gap: '0.6rem' }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="#25D366">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        <div>
                          <div className="fw-semibold" style={{ letterSpacing: '-0.2px' }}>Chat on WhatsApp</div>
                          <small className="text-muted">+6281337202227</small>
                        </div>
                      </div>
                      <Button className="mt-3" variant="success" onClick={async ()=>{
                        try {
                          const res = await fetch('/api/contact/whatsapp', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              name: contactForm.name,
                              planData: optimizedPlan ? {
                                packageName: selectedPackage?.name || 'Custom',
                                budget: parseInt(budgetInput),
                                total: selectedPackage?.total || 0,
                                guests: guestCountInput,
                                weddingTheme: weddingTheme,
                                numberOfDays: numberOfDays,
                                vendors: selectedPackage?.vendors || {},
                                allocations: selectedPackage?.allocations || {}
                              } : null
                            })
                          });
                          const data = await res.json();
                          window.open(data.url, '_blank');
                        } catch (e) { window.open('https://wa.me/6281337202227', '_blank'); }
                      }}>Open WhatsApp</Button>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-0 elevate" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '16px' }}>
                    <Card.Body>
                      <div className="d-inline-flex align-items-center px-3 py-1 mb-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '999px' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3B82F6', marginRight: 8 }} />
                        Email us
                      </div>
                      <Form>
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: 'rgba(255,255,255,0.85)' }}>Name</Form.Label>
                          <Form.Control type="text" placeholder="Your name" value={contactForm.name} onChange={(e)=> setContactForm(f=>({ ...f, name: e.target.value }))} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: 'rgba(255,255,255,0.85)' }}>Email address</Form.Label>
                          <Form.Control type="email" placeholder="you@example.com" value={contactForm.email} onChange={(e)=> setContactForm(f=>({ ...f, email: e.target.value }))} />
                        </Form.Group>
                        <Button variant="primary" onClick={async ()=>{
                          try {
                            await fetch('/api/contact', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                name: contactForm.name,
                                email: contactForm.email,
                                planData: optimizedPlan ? {
                                  packageName: selectedPackage?.name || 'Custom',
                                  budget: parseInt(budgetInput),
                                  total: selectedPackage?.total || 0,
                                  guests: guestCountInput,
                                  weddingTheme: weddingTheme,
                                  numberOfDays: numberOfDays,
                                  venuePreference: venuePreference,
                                  vendors: selectedPackage?.vendors || {},
                                  allocations: selectedPackage?.allocations || {},
                                  addons: selectedPackage?.addons || []
                                } : null
                              })
                            });
                          } catch (e) { console.error('Contact send error:', e); }
                          setContacted(true); setContactSubmitted(true);
                        }}>Send</Button>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              ) : (
                <Row className="g-4">
                  <Col md={{ span: 8, offset: 2 }}>
                    <Card className="border-0 elevate" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '16px' }}>
                      <Card.Body className="text-center">
                        <div className="mb-2" style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)', letterSpacing: '1px', textTransform: 'uppercase' }}>Details received</div>
                        <h4 className="mb-3" style={{ color: '#fff' }}>{contactForm.name}</h4>
                        <div className="mb-4" style={{ color: 'rgba(255,255,255,0.8)' }}>{contactForm.email}</div>
                        <p className="text-muted">Our team will reach out shortly to schedule a discovery call and finalize plan details.</p>
                        <div className="d-flex justify-content-center" style={{ gap: '0.75rem' }}>
                          <Button variant="primary" onClick={()=> setShowContactModal(false)}>Done</Button>
                          <Button variant="outline-light" onClick={()=> setContactSubmitted(false)}>Edit details</Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </Container>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=> { setShowContactModal(false); setContactSubmitted(false); }}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Lightbox Modal for Testimony Images */}
      <Modal 
        show={!!lightboxImg} 
        onHide={() => setLightboxImg(null)} 
        centered 
        size="xl"
        dialogClassName="lightbox-modal"
        contentClassName="lightbox-content"
      >
        <Modal.Body className="p-0 position-relative" style={{ background: 'transparent' }}>
          <Button 
            variant="link" 
            onClick={() => setLightboxImg(null)}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              zIndex: 10,
              background: 'rgba(0,0,0,0.6)',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              padding: 0
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </Button>
          {lightboxImg && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img 
                src={lightboxImg} 
                alt={lightboxInfo.name} 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '80vh', 
                  objectFit: 'contain',
                  borderRadius: '12px',
                  boxShadow: '0 25px 80px rgba(0,0,0,0.6)'
                }} 
              />
              <div style={{
                marginTop: '1.5rem',
                textAlign: 'center',
                padding: '0 1rem'
              }}>
                <div style={{ fontSize: '1.25rem', color: '#fff', fontWeight: '600', marginBottom: '0.5rem' }}>
                  {lightboxInfo.name}
                </div>
                <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.75)', fontStyle: 'italic' }}>
                  "{lightboxInfo.quote}"
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Video Modal Player */}
      <Modal 
        show={!!videoModal} 
        onHide={() => setVideoModal(null)} 
        centered 
        size="xl"
        dialogClassName="video-modal"
        contentClassName="video-modal-content"
      >
        <Modal.Body className="p-0 position-relative" style={{ background: 'transparent' }}>
          <Button 
            variant="link" 
            onClick={() => setVideoModal(null)}
            style={{
              position: 'absolute',
              top: '-48px',
              right: '0',
              zIndex: 10,
              background: 'rgba(0,0,0,0.6)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              padding: 0
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </Button>
          {videoModal && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ 
                width: '100%', 
                aspectRatio: '16/9', 
                borderRadius: '12px', 
                overflow: 'hidden',
                boxShadow: '0 25px 80px rgba(0,0,0,0.6)',
                background: '#000'
              }}>
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${videoModal}?rel=0&showinfo=0&modestbranding=1&playsinline=1`}
                  title={videoInfo.name}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  loading="lazy"
                  style={{ display: 'block' }}
                />
              </div>
              <div style={{
                marginTop: '1.25rem',
                textAlign: 'center',
                padding: '0 1rem'
              }}>
                <div style={{ fontSize: '1.25rem', color: '#fff', fontWeight: '600', marginBottom: '0.35rem' }}>
                  {videoInfo.name}
                </div>
                <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', marginBottom: '0.75rem' }}>
                  "{videoInfo.quote}"
                </div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                  Video not loading? <a 
                    href={`https://www.youtube.com/watch?v=${videoModal}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#d4af37' }}
                  >Watch on YouTube →</a>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ===== SMART BUDGET OPTIMIZER MODAL ===== */}
      <Modal 
        show={showBudgetOptimizer} 
        onHide={() => { setShowBudgetOptimizer(false); setOptimizerStep(0); setSelectedPackage(null); setSelectedStartingPackage(null); }} 
        centered 
        size="xl"
        dialogClassName="budget-optimizer-modal"
      >
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#fff' }}>
            <span style={{ fontSize: '1.5rem' }}>✨</span>
            <span>AI Wedding Planner</span>
            <Badge bg="info" style={{ fontSize: '0.65rem', fontWeight: '500' }}>AI-Powered</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #0d1117 100%)', padding: '2rem', minHeight: '70vh' }}>
          
          {/* Step 0: Choose Starting Package */}
          {optimizerStep === 0 && (
            <div className="package-selection-step">
              <div className="text-center mb-4">
                <h3 style={{ color: '#fff', fontFamily: "'Playfair Display', serif", marginBottom: '0.5rem' }}>
                  Choose Your Starting Point
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem' }}>
                  Select a package to see detailed options, or build your own with AI
                </p>
              </div>

              <Row className="justify-content-center g-4 mb-4">
                {/* Essential Package */}
                <Col md={4}>
                  <div 
                    onClick={() => {
                      setSelectedStartingPackage('essential');
                      setBudgetInput('15000');
                      setGuestCountInput(30);
                      setVenuePreference('cliff');
                      setGuestsConfirmed(true); setBudgetConfirmed(true); setVenueConfirmed(true);
                      // Trigger fade-in and typewriter effect with representative vendor names
                      const picks = getRepresentativeVendors('essential');
                      const vendorLines = picks.map(p => `${p.emoji} ${p.category.charAt(0).toUpperCase() + p.category.slice(1)}: ${p.name}`).join('\n');
                      const packageDescription = `Great choice! 🌿 The Essential package is perfect for intimate celebrations:\n\n${vendorLines}\n\nI can go ahead and give you an estimate, or tell me how you'd like to tailor and customize your wedding! 💬`;
                      setOptimizerChat([]);
                      setTypewriterFullText(packageDescription);
                      setTypewriterText('');
                      setChatFadeIn(true);
                      setShowConfirmation(false);
                      setOptimizerStep(1);
                      setTimeout(() => setIsTypewriterActive(true), 300);
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '20px',
                      padding: '2rem',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.5)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <div className="text-center">
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🌿</div>
                      <h4 style={{ color: '#fff', fontFamily: "'Playfair Display', serif", marginBottom: '0.25rem' }}>Essential</h4>
                      <div style={{ color: '#d4af37', fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem' }}>from $15,000</div>
                      <ul style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', textAlign: 'left', listStyle: 'none', padding: 0, marginBottom: '0.5rem' }}>
                        <li style={{ marginBottom: '0.4rem' }}>🏝 Up to 20 guests</li>
                        <li style={{ marginBottom: '0.4rem' }}>⛰ Cliff-front venue</li>
                        <li style={{ marginBottom: '0.4rem' }}>📸 Photo & video (8 hrs)</li>
                        <li style={{ marginBottom: '0.4rem' }}>💐 Full venue styling</li>
                        <li style={{ marginBottom: '0.4rem' }}>🎵 DJ entertainment</li>
                        <li style={{ marginBottom: '0.4rem' }}>💄 Bridal makeup & hairdo</li>
                      </ul>
                    </div>
                  </div>
                </Col>

                {/* Classic Package */}
                <Col md={4}>
                  <div 
                    onClick={() => {
                      setSelectedStartingPackage('classic');
                      setBudgetInput('40000');
                      setGuestCountInput(50);
                      setVenuePreference('beachfront');
                      setGuestsConfirmed(true); setBudgetConfirmed(true); setVenueConfirmed(true);
                      // Trigger fade-in and typewriter effect with representative vendor names
                      const picksC = getRepresentativeVendors('classic');
                      const vendorLinesC = picksC.map(p => `${p.emoji} ${p.category.charAt(0).toUpperCase() + p.category.slice(1)}: ${p.name}`).join('\n');
                      const packageDescriptionC = `Excellent choice! 💫 The Signature package — our most popular — includes:\n\n${vendorLinesC}\n\nI can go ahead and give you an estimate, or tell me how you'd like to tailor and customize your wedding! 💬`;
                      setOptimizerChat([]);
                      setTypewriterFullText(packageDescriptionC);
                      setTypewriterText('');
                      setChatFadeIn(true);
                      setShowConfirmation(false);
                      setOptimizerStep(1);
                      setTimeout(() => setIsTypewriterActive(true), 300);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                      border: '2px solid rgba(212,175,55,0.4)',
                      borderRadius: '20px',
                      padding: '2rem',
                      height: '100%',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.8)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <Badge 
                      style={{ 
                        position: 'absolute', 
                        top: '-10px', 
                        left: '50%', 
                        transform: 'translateX(-50%)',
                        background: 'linear-gradient(135deg, #d4af37 0%, #b8963e 100%)',
                        color: '#000',
                        padding: '0.35rem 1rem',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}
                    >
                      MOST POPULAR
                    </Badge>
                    <div className="text-center">
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💫</div>
                      <h4 style={{ color: '#fff', fontFamily: "'Playfair Display', serif", marginBottom: '0.25rem' }}>Signature</h4>
                      <div style={{ color: '#d4af37', fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem' }}>from $40,000</div>
                      <ul style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', textAlign: 'left', listStyle: 'none', padding: 0, marginBottom: '0.5rem' }}>
                        <li style={{ marginBottom: '0.4rem' }}>🏝 Up to 50 guests</li>
                        <li style={{ marginBottom: '0.4rem' }}>🏖 Beachfront, cliff-top, or rice field villa</li>
                        <li style={{ marginBottom: '0.4rem' }}>📸 Full-day photo & video</li>
                        <li style={{ marginBottom: '0.4rem' }}>💐 Premium floral styling</li>
                        <li style={{ marginBottom: '0.4rem' }}>🎵 Live acoustic band + DJ</li>
                        <li style={{ marginBottom: '0.4rem' }}>💄 Bridal makeup package + trial</li>
                        <li style={{ marginBottom: '0.4rem' }}>🎂 Custom wedding cake</li>
                        <li style={{ marginBottom: '0.4rem' }}>🚗 Guest transportation</li>
                      </ul>
                    </div>
                  </div>
                </Col>

                {/* Luxury Package */}
                <Col md={4}>
                  <div 
                    onClick={() => {
                      setSelectedStartingPackage('luxury');
                      setBudgetInput('75000');
                      setGuestCountInput(100);
                      setVenuePreference('cliff');
                      setGuestsConfirmed(true); setBudgetConfirmed(true); setVenueConfirmed(true);
                      // Trigger fade-in and typewriter effect with representative vendor names
                      const picksL = getRepresentativeVendors('luxury');
                      const vendorLinesL = picksL.map(p => `${p.emoji} ${p.category.charAt(0).toUpperCase() + p.category.slice(1)}: ${p.name}`).join('\n');
                      const packageDescriptionL = `Wonderful choice! 👑 The Prestige package — our ultimate experience — includes:\n\n${vendorLinesL}\n\nI can go ahead and give you an estimate, or tell me how you'd like to tailor and customize your wedding! 💬`;
                      setOptimizerChat([]);
                      setTypewriterFullText(packageDescriptionL);
                      setTypewriterText('');
                      setChatFadeIn(true);
                      setShowConfirmation(false);
                      setOptimizerStep(1);
                      setTimeout(() => setIsTypewriterActive(true), 300);
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '20px',
                      padding: '2rem',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.5)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <div className="text-center">
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👑</div>
                      <h4 style={{ color: '#fff', fontFamily: "'Playfair Display', serif", marginBottom: '0.25rem' }}>Prestige</h4>
                      <div style={{ color: '#d4af37', fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem' }}>from $75,000</div>
                      <ul style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', textAlign: 'left', listStyle: 'none', padding: 0, marginBottom: '0.5rem' }}>
                        <li style={{ marginBottom: '0.4rem' }}>🏝 Up to 200+ guests</li>
                        <li style={{ marginBottom: '0.4rem' }}>🌊 5-star resort or private estate</li>
                        <li style={{ marginBottom: '0.4rem' }}>📸 Cinematic photo & video team</li>
                        <li style={{ marginBottom: '0.4rem' }}>💐 Luxury floral installations</li>
                        <li style={{ marginBottom: '0.4rem' }}>🎵 Live band + premium DJ</li>
                        <li style={{ marginBottom: '0.4rem' }}>🔥 Traditional dance & fire shows</li>
                        <li style={{ marginBottom: '0.4rem' }}>💄 Celebrity makeup artist</li>
                        <li style={{ marginBottom: '0.4rem' }}>🎂 Designer wedding cake</li>
                      </ul>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '2rem 0' }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }}></div>
                <span style={{ color: 'rgba(255,255,255,0.4)', padding: '0 1.5rem', fontSize: '0.9rem' }}>or</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }}></div>
              </div>

              {/* Start from Scratch */}
              <div className="text-center">
                <Button
                  variant="outline-light"
                  onClick={() => {
                    setSelectedStartingPackage(null);
                    setOptimizerChat([
                      { role: 'assistant', content: "Hi! I'm your AI wedding planner ✨ Tell me about your dream Bali wedding — the vibe, guest count, budget, anything that excites you!" }
                    ]);
                    setShowConfirmation(false);
                    setOptimizerStep(1);
                  }}
                  style={{
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'rgba(255,255,255,0.8)',
                    borderRadius: '30px',
                    padding: '0.75rem 2rem',
                    fontSize: '1rem'
                  }}
                >
                  💬 Build my own with AI
                </Button>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                  Tell us your vision and let AI create a custom package
                </p>
              </div>
            </div>
          )}

          {/* Step 1: Chat-First AI Interface */}
          {optimizerStep === 1 && (
            <div 
              className="optimizer-chat-step"
              style={{
                animation: chatFadeIn ? 'chatFadeIn 0.5s ease-out forwards' : 'none',
                opacity: chatFadeIn ? 0 : 1
              }}
              onAnimationEnd={() => setChatFadeIn(false)}
            >
              <style>
                {`
                  @keyframes chatFadeIn {
                    from {
                      opacity: 0;
                      transform: translateY(20px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                  @keyframes cursorBlink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0; }
                  }
                `}
              </style>
              <Row className="justify-content-center">
                <Col lg={8} xl={7}>
                  {/* Back Button */}
                  <Button
                    variant="link"
                    onClick={() => {
                      setOptimizerStep(0);
                      setIsTypewriterActive(false);
                      setTypewriterText('');
                      setTypewriterFullText('');
                    }}
                    style={{ color: 'rgba(255,255,255,0.5)', padding: 0, marginBottom: '1rem', fontSize: '0.9rem', textDecoration: 'none' }}
                  >
                    ← Back to packages
                  </Button>
                  
                  {/* Chat Header */}
                  <div className="text-center mb-4">
                    <div style={{ 
                      width: '80px', 
                      height: '80px', 
                      margin: '0 auto 1rem',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(139,92,246,0.2))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2.5rem'
                    }}>
                      ✨
                    </div>
                    <h3 style={{ color: '#fff', fontFamily: "'Playfair Display', serif", marginBottom: '0.5rem' }}>
                      {selectedStartingPackage ? `Customizing ${selectedStartingPackage.charAt(0).toUpperCase() + selectedStartingPackage.slice(1)} Package` : 'Your AI Wedding Planner'}
                    </h3>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                      {selectedStartingPackage ? 'Tell me how you\'d like to personalize this package' : 'Tell me about your dream wedding and I\'ll create personalized packages'}
                    </p>
                  </div>

                  {/* Chat Messages */}
                  <div 
                    ref={optimizerChatRef}
                    style={{ 
                      background: 'rgba(255,255,255,0.02)', 
                      borderRadius: '20px', 
                      padding: '1.5rem',
                      border: '1px solid rgba(255,255,255,0.06)',
                      marginBottom: '1rem',
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                    {/* Typewriter effect for package descriptions */}
                    {isTypewriterActive && typewriterText && (
                      <div 
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          marginBottom: '1rem'
                        }}
                      >
                        <div style={{
                          maxWidth: '85%',
                          padding: '1rem 1.25rem',
                          borderRadius: '20px 20px 20px 4px',
                          background: 'rgba(255,255,255,0.05)',
                          color: '#fff',
                          fontSize: '0.95rem',
                          lineHeight: '1.5',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {typewriterText}
                          <span style={{
                            display: 'inline-block',
                            width: '2px',
                            height: '1.1em',
                            background: '#d4af37',
                            marginLeft: '2px',
                            verticalAlign: 'text-bottom',
                            animation: 'cursorBlink 0.8s infinite'
                          }} />
                        </div>
                      </div>
                    )}
                    {/* Regular chat messages */}
                    {!isTypewriterActive && optimizerChat.map((msg, idx) => (
                      <div 
                        key={idx}
                        style={{
                          display: 'flex',
                          justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                          marginBottom: '1rem'
                        }}
                      >
                        <div style={{
                          maxWidth: '85%',
                          padding: '1rem 1.25rem',
                          borderRadius: msg.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                          background: msg.role === 'user' 
                            ? 'linear-gradient(135deg, #d4af37 0%, #b8963e 100%)' 
                            : 'rgba(255,255,255,0.05)',
                          color: msg.role === 'user' ? '#000' : '#fff',
                          fontSize: '0.95rem',
                          lineHeight: '1.5',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    {isOptimizerChatLoading && (
                      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem' }}>
                        <div style={{
                          padding: '1rem 1.5rem',
                          borderRadius: '20px 20px 20px 4px',
                          background: 'rgba(255,255,255,0.05)',
                          color: 'rgba(255,255,255,0.6)'
                        }}>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Thinking...
                        </div>
                      </div>
                    )}
                    
                    {/* Confirmation Card */}
                    {showConfirmation && pendingPreferences && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start', 
                        marginBottom: '1rem',
                        marginTop: '0.5rem'
                      }}>
                        <div style={{
                          maxWidth: '90%',
                          padding: '1.25rem 1.5rem',
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(184,150,62,0.1) 100%)',
                          border: '1px solid rgba(212,175,55,0.3)',
                        }}>
                          <div style={{ 
                            fontSize: '0.9rem', 
                            color: '#d4af37', 
                            marginBottom: '0.75rem',
                            fontWeight: '600'
                          }}>
                            ✨ Here's what I understood:
                          </div>
                          
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '0.5rem',
                            marginBottom: '1rem' 
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              color: 'rgba(255,255,255,0.9)',
                              fontSize: '0.9rem'
                            }}>
                              <span style={{ color: '#4ade80' }}>✓</span>
                              <span style={{ textTransform: 'capitalize' }}>
                                {pendingPreferences.venue === 'any' ? 'Open to any venue style' : `${pendingPreferences.venue} venue`}
                              </span>
                            </div>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              color: 'rgba(255,255,255,0.9)',
                              fontSize: '0.9rem'
                            }}>
                              <span style={{ color: '#4ade80' }}>✓</span>
                              <span>{pendingPreferences.guests} guests</span>
                            </div>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              color: 'rgba(255,255,255,0.9)',
                              fontSize: '0.9rem'
                            }}>
                              <span style={{ color: '#4ade80' }}>✓</span>
                              <span>${Number(pendingPreferences.budget).toLocaleString()} budget</span>
                            </div>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              color: 'rgba(255,255,255,0.9)',
                              fontSize: '0.9rem'
                            }}>
                              <span style={{ color: '#4ade80' }}>✓</span>
                              <span>{pendingPreferences.days && pendingPreferences.days > 1 ? `${pendingPreferences.days}-day celebration` : 'Single-day celebration'}</span>
                            </div>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              color: 'rgba(255,255,255,0.9)',
                              fontSize: '0.9rem'
                            }}>
                              <span style={{ color: '#4ade80' }}>✓</span>
                              <span>{pendingPreferences.theme && pendingPreferences.theme !== 'Western' ? `${pendingPreferences.theme} themed wedding` : 'Classic Western style'}</span>
                            </div>
                            {pendingPreferences.priorities && Object.entries(pendingPreferences.priorities).some(([k, v]) => v > 5) && (
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem',
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '0.9rem'
                              }}>
                                <span style={{ color: '#4ade80' }}>✓</span>
                                <span>Focus on {Object.entries(pendingPreferences.priorities)
                                  .filter(([k, v]) => v > 5)
                                  .map(([k]) => k.toLowerCase())
                                  .join(', ')}</span>
                              </div>
                            )}
                          </div>
                          
                          <div style={{ 
                            display: 'flex', 
                            gap: '0.75rem', 
                            flexWrap: 'wrap'
                          }}>
                            <Button
                              onClick={() => {
                                setShowConfirmation(false);
                                setOptimizerStep(2);
                                setTimeout(() => {
                                  const result = generateOptimizedPackages(
                                    pendingPreferences.budget,
                                    pendingPreferences.guests,
                                    pendingPreferences.priorities || optimizerPriorities,
                                    pendingPreferences.venue,
                                    pendingPreferences.days || numberOfDays,
                                    pendingPreferences.theme || weddingTheme
                                  );
                                  setOptimizedPlan(result);
                                  setOptimizerStep(3);
                                }, 2000);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #d4af37 0%, #b8963e 100%)',
                                border: 'none',
                                color: '#000',
                                fontWeight: '600',
                                padding: '0.6rem 1.25rem',
                                borderRadius: '25px',
                                fontSize: '0.85rem'
                              }}
                            >
                              ✓ Looks right, generate!
                            </Button>
                            <Button
                              variant="outline-light"
                              onClick={() => {
                                setShowConfirmation(false);
                                // Reset confirmed flags so chatbot re-asks (4 required fields)
                                setGuestsConfirmed(false); setBudgetConfirmed(false); setVenueConfirmed(false);
                                setThemeConfirmed(false);
                                // Reset chat to fresh start (flush LLM context)
                                setOptimizerChat([
                                  { role: 'assistant', content: "No problem! Let's start fresh. Tell me what you'd like to change about your wedding plans." }
                                ]);
                              }}
                              style={{
                                borderColor: 'rgba(255,255,255,0.2)',
                                color: 'rgba(255,255,255,0.8)',
                                padding: '0.6rem 1.25rem',
                                borderRadius: '25px',
                                fontSize: '0.85rem'
                              }}
                            >
                              ✎ Let me adjust
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Chat Input */}
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!optimizerChatInput.trim() || isOptimizerChatLoading) return;
                    
                    const userMessage = optimizerChatInput.trim();
                    
                    // Check if user is confirming a pre-selected package
                    // More flexible matching to handle typos and enthusiasm (yess, yeahhh, suree, etc.)
                    const normalizedMsg = userMessage.toLowerCase().trim();
                    const isAffirmative = /^(ye+s+|ye+a+h*|ye+p+|yu+p+|su+re+|o+k+a*y*|sounds? good|perfect|let'?s? do it|go ahead|looks? good|absolutely|definitely|love it|i'?m in|let'?s? go|do it|i do|ready|excited|can'?t wait)!*$/i.test(normalizedMsg);
                    
                    if (selectedStartingPackage && isAffirmative) {
                      // User confirmed the package — we have guests, budget, venue from card
                      // Only missing: cultural theme. Ask for it.
                      const yesResponse = "Love it! ✨ One last thing — do you have a cultural or thematic style in mind? We specialize in:\n\n🕊 Western (Classic)\n🪷 Indian\n🌺 Balinese\n🏮 Chinese\n⛩ Japanese\n🎎 Korean\n🌮 Mexican\n🕌 Middle Eastern\n🌾 Bohemian\n\nOr if you'd like a fusion of styles, we can do that too!";
                      setOptimizerChat(prev => [...prev, { role: 'user', content: userMessage }]);
                      setTypewriterFullText(yesResponse);
                      setTypewriterText('');
                      setTimeout(() => setIsTypewriterActive(true), 300);
                      setOptimizerChatInput('');
                      // Clear starting package so next message goes through normal chatbot flow
                      setSelectedStartingPackage(null);
                      return;
                    }
                    
                    // Check if user is declining a pre-selected package
                    const isNegative = /^(no+|na+h+|not? really|nope|ne+ver|i don'?t think so|no thanks|not? for me|pass|change|different|something else)!*$/i.test(normalizedMsg);
                    
                    if (selectedStartingPackage && isNegative) {
                      // User declined the package — reset preferences but KEEP conversation history
                      setSelectedStartingPackage(null);
                      setBudgetInput('25000'); setGuestCountInput(80); setVenuePreference('beachfront');
                      setNumberOfDays(1); setWeddingTheme('Western');
                      setGuestsConfirmed(false); setBudgetConfirmed(false); setVenueConfirmed(false);
                      setThemeConfirmed(false);
                      // Append user's "no" to existing chat, then show response with typewriter
                      const noResponse = "No problem at all! Let's build something perfect for you from scratch. ✨\n\nTell me about your dream Bali wedding — how many guests, what venue type (beachfront, cliff front, cliff top, ocean view, or rice field), your budget, and any cultural theme you'd like?";
                      setOptimizerChat(prev => [...prev, { role: 'user', content: userMessage }]);
                      setTypewriterFullText(noResponse);
                      setTypewriterText('');
                      setTimeout(() => setIsTypewriterActive(true), 300);
                      setOptimizerChatInput('');
                      return;
                    }
                    
                    // If user wants to tailor/customize — show customization menu (works with or without a package selected)
                    const wantsToCustomize = /\b(tailor|customize|customise|personalize|personalise|adjust|modify|tweak)/i.test(normalizedMsg);
                    if (wantsToCustomize) {
                      if (selectedStartingPackage) setSelectedStartingPackage(null);
                      const customizeResponse = "Of course! Here's what you can customize:\n\n🏝 Venue\n🍽 Catering\n📸 Photography & Video\n💐 Decoration & Styling\n🎵 Entertainment & Music\n💄 Makeup\n🎂 Wedding Cake\n💰 Budget\n👥 Guest Count\n\nJust tell me which one you'd like to change!";
                      setOptimizerChat(prev => [...prev, { role: 'user', content: userMessage }]);
                      setTypewriterFullText(customizeResponse);
                      setTypewriterText('');
                      setTimeout(() => setIsTypewriterActive(true), 300);
                      setOptimizerChatInput('');
                      return;
                    }
                    
                    // If user says "all" / "everything" — show ALL vendor options
                    const wantsAll = /^(all|everything|all of them|show me all|every category|all categories)\s*!*$/i.test(normalizedMsg);
                    if (wantsAll) {
                      if (selectedStartingPackage) setSelectedStartingPackage(null);
                      const allOptions = `Here are all your options:\n\n🏝 VENUES:\n🏖 Beachfront: The Laguna ($9,855–$12,306) · The Mulia ($21,671–$40,516) · Amankila ($58,000–$163,500) · Khayangan Estate ($28,962)\n⛰ Cliff: Anantara ($2,148–$3,483) · Pandawa ($1,795–$6,813) · AYANA ($11,896–$29,713) · Stones ($17,273) · Kempinski ($31,220–$53,978) · Ungasan ($29,936–$44,933) · Alila ($10,600–$28,031)\n🌾 Rice Field: Jeeva Saba ($6,129)\n\n🍽 CATERING:\nBali Indian Food $12–21/guest · Bali Catering Co $35–44/guest · Amazing Bali $38–47/guest · Lumbung $28–50/guest · Bali Miracle $37–47/guest\n\n📸 PHOTO & VIDEO:\nSriwijayastory $647–2,295 · Venema $2,236–3,178 · Flipmax $941–1,589 · IMAJ $1,854–2,383 · Diktat $2,295–2,942 · Bali Pixtura $3,413–4,766 · AXIOO $2,059–3,531\n\n🎵 ENTERTAINMENT:\nDJ Bian $382–441 · DJ Arif/Mike $588–618 · Moana $306–765 · Lucky Aces $941–1,089 · DJ Jimmy Sax $1,400 · Luxe Voir $2,118–2,795 · Joshua Setiawan $2,059–2,295\n\nTell me which category or specific vendor you'd like to change!`;
                      setOptimizerChat(prev => [...prev, { role: 'user', content: userMessage }]);
                      setTypewriterFullText(allOptions);
                      setTypewriterText('');
                      setTimeout(() => setIsTypewriterActive(true), 300);
                      setOptimizerChatInput('');
                      return;
                    }
                    
                    // If user wants to start over / from scratch — reset everything
                    const wantsStartOver = /\b(start from scratch|start over|start fresh|begin again|reset|redo|from the beginning|new plan)/i.test(normalizedMsg);
                    if (wantsStartOver) {
                      if (selectedStartingPackage) setSelectedStartingPackage(null);
                      setSelectedVenueId(null);
                      setBudgetInput('25000'); setGuestCountInput(80); setVenuePreference('beachfront');
                      setNumberOfDays(1); setWeddingTheme('Western');
                      setGuestsConfirmed(false); setBudgetConfirmed(false); setVenueConfirmed(false); setThemeConfirmed(false);
                      const freshResponse = "No problem! Let's start fresh. ✨\n\nTell me about your dream Bali wedding — how many guests, what venue type (beachfront, cliff front, cliff top, ocean view, or rice field), your budget, and any cultural theme you'd like?";
                      setOptimizerChat(prev => [...prev, { role: 'user', content: userMessage }]);
                      setTypewriterFullText(freshResponse);
                      setTypewriterText('');
                      setTimeout(() => setIsTypewriterActive(true), 300);
                      setOptimizerChatInput('');
                      return;
                    }
                    
                    // If user didn't confirm, they want to customize - clear the starting package
                    const wasAwaitingConfirmation = !!selectedStartingPackage;
                    if (selectedStartingPackage) {
                      setSelectedStartingPackage(null);
                    }
                    
                    // Build updated history BEFORE setState (since setState is async)
                    const updatedHistory = [...optimizerChat, { role: 'user', content: userMessage }];
                    setOptimizerChat(updatedHistory);
                    setOptimizerChatInput('');
                    setIsOptimizerChatLoading(true);
                    
                    // Detect CHANGE REQUESTS for any category — pass to backend so LLM handles it contextually
                    const changePatterns = [
                      { key: 'venue', regex: /\b(change|switch|different|swap|update|new)\b.*(venue|location|setting|place)/i },
                      { key: 'venue', regex: /\b(venue|location|setting|place)\b.*(change|switch|different|swap|update|new)/i },
                      { key: 'budget', regex: /\b(change|switch|different|update|new|adjust)\b.*budget/i },
                      { key: 'guests', regex: /\b(change|switch|different|update|new|adjust)\b.*(guest|people|pax)/i },
                      { key: 'theme', regex: /\b(change|switch|different|update|new)\b.*(theme|style|cultural)/i },
                      { key: 'catering', regex: /\b(change|switch|different|swap|update|new)\b.*(cater|food|menu|dining|cuisine)/i },
                      { key: 'catering', regex: /\b(cater|food|menu|dining|cuisine)\b.*(change|switch|different|swap|update|new)/i },
                      { key: 'photography', regex: /\b(change|switch|different|swap|update|new)\b.*(photo|video|film|camera)/i },
                      { key: 'photography', regex: /\b(photo|video|film|camera)\b.*(change|switch|different|swap|update|new)/i },
                      { key: 'entertainment', regex: /\b(change|switch|different|swap|update|new)\b.*(entertain|music|dj|band|dance)/i },
                      { key: 'entertainment', regex: /\b(entertain|music|dj|band|dance)\b.*(change|switch|different|swap|update|new)/i },
                      { key: 'decoration', regex: /\b(change|switch|different|swap|update|new)\b.*(decor|flower|floral|styling)/i },
                      { key: 'decoration', regex: /\b(decor|flower|floral|styling)\b.*(change|switch|different|swap|update|new)/i },
                      { key: 'makeup', regex: /\b(change|switch|different|swap|update|new)\b.*(makeup|hair|bridal look|mua)/i },
                      { key: 'makeup', regex: /\b(makeup|hair|bridal look|mua)\b.*(change|switch|different|swap|update|new)/i },
                      { key: 'cake', regex: /\b(change|switch|different|swap|update|new)\b.*(cake|dessert)/i },
                    ];
                    let detectedChangeCategory = null;
                    for (const { key, regex } of changePatterns) {
                      if (regex.test(userMessage)) { detectedChangeCategory = key; break; }
                    }
                    
                    // Also detect "what are my options for X" / "show me X options" / "list X"
                    const listPatterns = [
                      { key: 'venue', regex: /\b(show|list|what).*(venue|location)/i },
                      { key: 'catering', regex: /\b(show|list|what).*(cater|food|menu)/i },
                      { key: 'photography', regex: /\b(show|list|what).*(photo|video|film)/i },
                      { key: 'entertainment', regex: /\b(show|list|what).*(entertain|music|dj|band)/i },
                      { key: 'decoration', regex: /\b(show|list|what).*(decor|flower|floral)/i },
                      { key: 'makeup', regex: /\b(show|list|what).*(makeup|hair|mua)/i },
                      { key: 'cake', regex: /\b(show|list|what).*(cake|dessert)/i },
                    ];
                    if (!detectedChangeCategory) {
                      for (const { key, regex } of listPatterns) {
                        if (regex.test(userMessage)) { detectedChangeCategory = key; break; }
                      }
                    }
                    
                    // Clear required-field flags for venue/budget/guests/theme changes
                    if (detectedChangeCategory === 'venue') { setVenueConfirmed(false); setVenuePreference('any'); setSelectedVenueId(null); }
                    if (detectedChangeCategory === 'budget') { setBudgetConfirmed(false); }
                    if (detectedChangeCategory === 'guests') { setGuestsConfirmed(false); }
                    if (detectedChangeCategory === 'theme') { setThemeConfirmed(false); }
                    
                    // Pre-scan user message BEFORE sending API call
                    // 4 required: guests, venue type, budget, cultural theme
                    // Days defaults to 1 (single day) unless user says multi-day
                    let effectiveGuests = (guestsConfirmed && detectedChangeCategory !== 'guests') ? guestCountInput : null;
                    let effectiveBudget = (budgetConfirmed && detectedChangeCategory !== 'budget') ? parseInt(budgetInput) : null;
                    let effectiveVenue = (venueConfirmed && detectedChangeCategory !== 'venue') ? venuePreference : null;
                    let effectiveTheme = (themeConfirmed && detectedChangeCategory !== 'theme') ? weddingTheme : null;
                    let effectiveDays = numberOfDays || 1; // always default to 1
                    
                    // Pre-scan for guests (e.g., "40 guests", "100 people")
                    const guestMatch = userMessage.match(/(\d+)\s*(guests?|people|pax)/i);
                    if (guestMatch) {
                      const g = Math.min(300, Math.max(20, parseInt(guestMatch[1])));
                      effectiveGuests = g; setGuestCountInput(g); setGuestsConfirmed(true);
                    }
                    // Pre-scan for budget (e.g., "$40k", "40k", "$40,000", "40000", "$50K budget")
                    const budgetMatch = userMessage.match(/\$\s*(\d+)\s*k\b/i) || userMessage.match(/\b(\d+)\s*k\b/i) || userMessage.match(/\$\s*([\d,]+)/i) || userMessage.match(/(\d{5,})\s*(budget|dollars|usd)?/i);
                    if (budgetMatch) {
                      let b = budgetMatch[1].replace(/,/g, '');
                      b = parseInt(b);
                      if (b < 1000) b = b * 1000; // "40k" → 40000
                      effectiveBudget = b; setBudgetInput(b.toString()); setBudgetConfirmed(true);
                    }
                    // Pre-scan for venue type (beachfront, cliff resort, cliff villa, ocean view, rice field)
                    const venueMap = [
                      [/\bbeach(front)?\b|\bseaside\b|\bresort\b/i, 'beachfront'],
                      [/\bcliff\s*front\b|\bclifffront\b|\bcliff\s*resort\b/i, 'cliff front'],
                      [/\bcliff\s*villa\b|\bprivate\s*estate\b/i, 'cliff villa'],
                      [/\bcliff\s*top\b|\bclifftop\b/i, 'cliff top'],
                      [/\bvilla\b/i, 'cliff villa'],
                      [/\bocean\s*view\b|\bsea\s*view\b|\bocean\b/i, 'ocean view'],
                      [/\brice\s*(field|paddy)\b|\bgarden\b/i, 'rice field']
                    ];
                    for (const [regex, venue] of venueMap) {
                      if (regex.test(userMessage)) { effectiveVenue = venue; setVenuePreference(venue); setVenueConfirmed(true); break; }
                    }
                    // Pre-scan for theme
                    if (!effectiveTheme) {
                      const themePatterns = [
                        [/\bindian\b|\bhindu\b|\bsouth asian\b/i, 'Indian'],
                        [/\bbalinese\b|\bbali style\b|\bbali\b/i, 'Balinese'],
                        [/\bchinese\b/i, 'Chinese'],
                        [/\bjapanese\b/i, 'Japanese'],
                        [/\bkorean\b/i, 'Korean'],
                        [/\bmexican\b|\blatin\b/i, 'Mexican'],
                        [/\bmiddle eastern\b/i, 'Middle Eastern'],
                        [/\bboho\b|\bbohemian\b|\brusstic\b/i, 'Bohemian'],
                        [/\bwestern\b|\bclassic\b|\btraditional\b|\bnormal\b|\bregular\b/i, 'Western']
                      ];
                      for (const [regex, theme] of themePatterns) {
                        if (regex.test(userMessage)) { effectiveTheme = theme; setWeddingTheme(theme); setThemeConfirmed(true); break; }
                      }
                    }
                    // Pre-scan for days (only if user explicitly mentions multi-day)
                    let daysChanged = false;
                    if (/\b(multi.?day|multiple\s*days?|few days|2 days|two days|2-day|weekend)\b/i.test(userMessage)) { effectiveDays = 2; setNumberOfDays(2); daysChanged = true; }
                    else if (/\b(3 days|three days|3-day|long weekend)\b/i.test(userMessage)) { effectiveDays = 3; setNumberOfDays(3); daysChanged = true; }
                    else if (/\b(4 days|four days|4-day)\b/i.test(userMessage)) { effectiveDays = 4; setNumberOfDays(4); daysChanged = true; }
                    
                    // If user ONLY mentioned days (no other new field in this message), acknowledge it on frontend
                    if (daysChanged && !guestMatch && !budgetMatch) {
                      const daysAck = `Noted — a ${effectiveDays}-day celebration! 🎉 Multi-day pricing will be applied to your estimate.`;
                      // Don't return — let it continue to LLM so other missing fields get asked
                      // But prepend acknowledgment to chat
                      setOptimizerChat(prev => [...prev, { role: 'assistant', content: daysAck }]);
                    }

                    try {
                      const response = await fetch('/api/chatbot/wedding-chat', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                          message: userMessage,
                          conversationHistory: updatedHistory,
                          awaitingPackageConfirmation: wasAwaitingConfirmation,
                          changeCategory: detectedChangeCategory || null,
                          knownPreferences: {
                            guests: effectiveGuests,
                            budget: effectiveBudget,
                            venue: effectiveVenue,
                            days: effectiveDays,
                            theme: effectiveTheme
                          }
                        })
                      });
                      const data = await response.json();
                      
                      // Handle error responses
                      if (!response.ok || !data.reply) {
                        setOptimizerChat(prev => [...prev, { role: 'assistant', content: data.error || "I'd love to hear more! Tell me about your dream wedding vision." }]);
                        setIsOptimizerChatLoading(false);
                        return;
                      }
                      
                      // Add AI response to chat
                      setOptimizerChat(prev => [...prev, { role: 'assistant', content: data.reply }]);
                      
                      // Detect specific venue name ONLY from the user's current message
                      // Never scan LLM replies or history — those may mention venues the user didn't choose
                      const allVenues = vendorDatabase.venue || [];
                      const msgLower = userMessage.toLowerCase();
                      const matchedVenue = allVenues.find(v => {
                        const nameParts = v.name.toLowerCase().split(/[\s–—-]+/);
                        return nameParts.some(part => part.length > 3 && msgLower.includes(part));
                      });
                      if (matchedVenue) {
                        setSelectedVenueId(matchedVenue.id);
                        const reverseVibeMap = { 'beachfront': 'beachfront', 'cliff': 'cliff top', 'garden': 'rice field' };
                        const mappedType = reverseVibeMap[matchedVenue.vibe] || matchedVenue.vibe;
                        setVenuePreference(mappedType);
                        setVenueConfirmed(true);
                      }
                      
                      // Apply extracted preferences
                      if (data.preferences) {
                        setAiSuggestions(data.preferences);
                        // Only update venue type if no specific venue was picked by name
                        if (data.preferences.venue && data.preferences.venue !== 'any' && !selectedVenueId && !matchedVenue) {
                          setVenuePreference(data.preferences.venue);
                          setVenueConfirmed(true);
                        }
                        if (data.preferences.guests) {
                          setGuestCountInput(Math.min(300, Math.max(20, data.preferences.guests)));
                          setGuestsConfirmed(true);
                        }
                        if (data.preferences.budget) {
                          setBudgetInput(data.preferences.budget.toString());
                          setBudgetConfirmed(true);
                        }
                        if (data.preferences.days && data.preferences.days > 0) {
                          setNumberOfDays(Math.min(5, Math.max(1, data.preferences.days)));
                        }
                        if (data.preferences.theme && data.preferences.theme !== null) {
                          setWeddingTheme(data.preferences.theme);
                          setThemeConfirmed(true);
                        }
                        if (data.preferences.priorities) {
                          setOptimizerPriorities(prev => ({ ...prev, ...data.preferences.priorities }));
                        }
                        setDreamDescription(data.preferences.summary || userMessage);
                      }
                      
                      // Auto-generate packages if ready - show confirmation first
                      if (data.readyToGenerate && data.preferences) {
                        setPendingPreferences({
                          venue: data.preferences.venue || venuePreference || 'beachfront',
                          guests: data.preferences.guests || guestCountInput || 50,
                          budget: data.preferences.budget || budgetInput || 25000,
                          priorities: data.preferences.priorities || optimizerPriorities,
                          summary: data.preferences.summary || '',
                          days: data.preferences.days || numberOfDays || 1, // defaults to 1
                          theme: data.preferences.theme || weddingTheme || 'Western'
                        });
                        setShowConfirmation(true);
                      }
                    } catch (error) {
                      console.error('Chat error:', error);
                      setOptimizerChat(prev => [...prev, { 
                        role: 'assistant', 
                        content: "I'd love to hear more! What's your dream Bali wedding like?" 
                      }]);
                    }
                    setIsOptimizerChatLoading(false);
                  }}>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <Form.Control
                        type="text"
                        placeholder={showConfirmation ? "Click 'Let me adjust' to continue chatting..." : "Describe your dream wedding... (e.g., sunset cliff ceremony, 50 guests, $30k budget)"}
                        value={optimizerChatInput}
                        onChange={(e) => setOptimizerChatInput(e.target.value)}
                        disabled={isOptimizerChatLoading || showConfirmation}
                        style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '25px',
                          padding: '1rem 1.5rem',
                          color: '#fff',
                          fontSize: '1rem'
                        }}
                      />
                      <Button
                        type="submit"
                        disabled={!optimizerChatInput.trim() || isOptimizerChatLoading || showConfirmation}
                        style={{
                          background: 'linear-gradient(135deg, #d4af37 0%, #b8963e 100%)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '52px',
                          height: '52px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.25rem',
                          flexShrink: 0
                        }}
                      >
                        →
                      </Button>
                    </div>
                  </form>

                  {/* Manual Mode Toggle */}
                  <div className="text-center mt-4">
                    <Button
                      variant="link"
                      onClick={() => {
                        // Show manual sliders in a collapsed section
                        const manualSection = document.getElementById('manual-inputs');
                        if (manualSection) {
                          manualSection.style.display = manualSection.style.display === 'none' ? 'block' : 'none';
                        }
                      }}
                      style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', textDecoration: 'none' }}
                    >
                      Or adjust settings manually ↓
                    </Button>
                  </div>

                  {/* Collapsible Manual Inputs */}
                  <div id="manual-inputs" style={{ display: 'none', marginTop: '1.5rem' }}>
                    {/* Budget Slider */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '16px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>💰 Budget</span>
                        <span style={{ color: '#d4af37', fontWeight: '700' }}>${(parseInt(budgetInput) || 25000).toLocaleString()}</span>
                      </div>
                      <Form.Range min={5000} max={100000} step={1000} value={parseInt(budgetInput) || 25000} onChange={(e) => setBudgetInput(e.target.value)} />
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                        {[15000, 25000, 40000, 60000, 100000].map(amt => (
                          <Button
                            key={amt}
                            variant="outline-light"
                            size="sm"
                            onClick={() => setBudgetInput(amt.toString())}
                            style={{ 
                              borderRadius: '20px', 
                              fontSize: '0.75rem',
                              padding: '0.25rem 0.75rem',
                              background: parseInt(budgetInput) === amt ? 'rgba(212,175,55,0.2)' : 'transparent',
                              borderColor: parseInt(budgetInput) === amt ? '#d4af37' : 'rgba(255,255,255,0.2)',
                              color: parseInt(budgetInput) === amt ? '#d4af37' : 'rgba(255,255,255,0.6)'
                            }}
                          >
                            ${amt/1000}k
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Guest Slider */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '16px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>👥 Guests</span>
                        <span style={{ color: '#fff', fontWeight: '700' }}>{guestCountInput}</span>
                      </div>
                      <Form.Range min={20} max={300} value={guestCountInput} onChange={(e) => setGuestCountInput(parseInt(e.target.value))} />
                    </div>

                    {/* Venue Preference Cards */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '16px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '1rem' }}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>🏝️ Venue Preference</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.5rem' }}>
                        {[
                          { id: 'any', label: 'Any Style', icon: '✨', desc: 'Let AI decide' },
                          { id: 'beachfront', label: 'Beachfront', icon: '🏖️', desc: 'Ocean views' },
                          { id: 'garden', label: 'Garden', icon: '🌿', desc: 'Lush & intimate' },
                          { id: 'villa', label: 'Villa', icon: '🏛️', desc: 'Private estate' },
                          { id: 'cliff', label: 'Cliff', icon: '⛰️', desc: 'Dramatic views' }
                        ].map(v => (
                          <div
                            key={v.id}
                            onClick={() => setVenuePreference(v.id)}
                            style={{
                              background: venuePreference === v.id 
                                ? 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(184,150,62,0.15))' 
                                : 'rgba(255,255,255,0.03)',
                              border: venuePreference === v.id 
                                ? '2px solid #d4af37' 
                                : '1px solid rgba(255,255,255,0.08)',
                              borderRadius: '12px',
                              padding: '0.75rem',
                              textAlign: 'center',
                              cursor: 'pointer',
                              transition: 'all 200ms ease'
                            }}
                          >
                            <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{v.icon}</div>
                            <div style={{ color: venuePreference === v.id ? '#d4af37' : '#fff', fontWeight: '600', fontSize: '0.8rem' }}>{v.label}</div>
                            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', marginTop: '0.15rem' }}>{v.desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Wedding Theme / Culture */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '16px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '1rem' }}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>🌏 Cultural Theme <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}>(optional)</span></div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '0.5rem' }}>
                        {[
                          { id: 'Western', label: 'Western', icon: '💒' },
                          { id: 'Indian', label: 'Indian', icon: '🪷' },
                          { id: 'Balinese', label: 'Balinese', icon: '🌺' },
                          { id: 'Chinese', label: 'Chinese', icon: '🏮' },
                          { id: 'Japanese', label: 'Japanese', icon: '🎎' },
                          { id: 'Korean', label: 'Korean', icon: '🇰🇷' },
                          { id: 'Mexican', label: 'Mexican', icon: '🌮' },
                          { id: 'Bohemian', label: 'Bohemian', icon: '🌻' }
                        ].map(t => (
                          <div
                            key={t.id}
                            onClick={() => { setWeddingTheme(t.id); setThemeConfirmed(true); }}
                            style={{
                              background: weddingTheme === t.id 
                                ? 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(184,150,62,0.15))' 
                                : 'rgba(255,255,255,0.03)',
                              border: weddingTheme === t.id 
                                ? '2px solid #d4af37' 
                                : '1px solid rgba(255,255,255,0.08)',
                              borderRadius: '12px',
                              padding: '0.6rem 0.4rem',
                              textAlign: 'center',
                              cursor: 'pointer',
                              transition: 'all 200ms ease'
                            }}
                          >
                            <div style={{ fontSize: '1.3rem', marginBottom: '0.15rem' }}>{t.icon}</div>
                            <div style={{ color: weddingTheme === t.id ? '#d4af37' : '#fff', fontWeight: '600', fontSize: '0.7rem' }}>{t.label}</div>
                          </div>
                        ))}
                      </div>
                      {weddingTheme !== 'Western' && (
                        <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: 'rgba(212,175,55,0.1)', borderRadius: '8px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                          ✨ We'll include cultural add-ons like {weddingTheme === 'Indian' ? 'Mehndi, Baraat & Sangeet' : weddingTheme === 'Balinese' ? 'Blessing Ceremony & Legong Dance' : weddingTheme === 'Chinese' ? 'Tea Ceremony & Lion Dance' : weddingTheme === 'Japanese' ? 'Sake Ceremony' : weddingTheme === 'Korean' ? 'Paebaek Ceremony' : weddingTheme === 'Mexican' ? 'Mariachi Band' : 'boho-themed décor'} in your packages
                        </div>
                      )}
                    </div>

                    {/* Priority Sliders */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '16px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '1rem' }}>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '1rem' }}>🎯 Your Priorities</div>
                      {[
                        { key: 'venue', label: 'Venue & Location', icon: '🏝️' },
                        { key: 'catering', label: 'Food & Drinks', icon: '🍽️' },
                        { key: 'photography', label: 'Photo & Video', icon: '📸' },
                        { key: 'decoration', label: 'Décor & Florals', icon: '💐' },
                        { key: 'entertainment', label: 'Entertainment', icon: '🎵' }
                      ].map(p => (
                        <div key={p.key} style={{ marginBottom: '0.75rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>{p.icon} {p.label}</span>
                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                              {optimizerPriorities[p.key] === 1 ? 'Low' : optimizerPriorities[p.key] === 2 ? 'Medium' : optimizerPriorities[p.key] === 3 ? 'High' : optimizerPriorities[p.key] === 4 ? 'Very High' : 'Essential'}
                            </span>
                          </div>
                          <Form.Range
                            min={1}
                            max={5}
                            value={optimizerPriorities[p.key]}
                            onChange={(e) => setOptimizerPriorities(prev => ({ ...prev, [p.key]: parseInt(e.target.value) }))}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Generate Button */}
                    <div className="text-center mt-3">
                      <Button
                        onClick={runBudgetOptimizer}
                        disabled={!budgetInput || parseFloat(budgetInput) < 5000}
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          border: 'none',
                          borderRadius: '25px',
                          padding: '0.75rem 2rem',
                          fontWeight: '600'
                        }}
                      >
                        ✨ Generate Packages
                      </Button>
                      {budgetInput && parseFloat(budgetInput) < 5000 && (
                        <p style={{ color: '#ef4444', marginTop: '0.75rem', fontSize: '0.8rem' }}>
                          Minimum budget is $5,000
                        </p>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Step 2: Analyzing Animation */}
          {optimizerStep === 2 && (
            <div className="text-center py-5">
              <div style={{ marginBottom: '2rem' }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  margin: '0 auto 2rem',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(59,130,246,0.2))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse 2s ease-in-out infinite'
                }}>
                  <span style={{ fontSize: '3rem' }}>🧠</span>
                </div>
                <h3 style={{ color: '#fff', marginBottom: '1rem' }}>AI is Optimizing Your Budget...</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)' }}>Analyzing vendors, comparing prices, and crafting personalized packages</p>
              </div>
              <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                <ProgressBar 
                  animated 
                  now={100} 
                  style={{ height: '8px', background: 'rgba(255,255,255,0.1)' }} 
                  variant="success"
                />
              </div>
              <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', gap: '2rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                <span>✓ Analyzing 50+ vendors</span>
                <span>✓ Comparing price tiers</span>
                <span>✓ Optimizing allocation</span>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {optimizerStep === 3 && optimizedPlan && (
            <div className="optimizer-results">
              {/* Header */}
              <div className="text-center mb-4">
                <Badge bg="success" style={{ fontSize: '0.75rem', marginBottom: '0.75rem' }}>✨ Optimization Complete</Badge>
                <h3 style={{ color: '#fff', fontFamily: "'Playfair Display', serif" }}>
                  Your Personalized Wedding Packages
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                  Budget: <strong style={{ color: '#10b981' }}>${optimizedPlan.budget.toLocaleString()}</strong> • 
                  Guests: <strong style={{ color: '#10b981' }}>{optimizedPlan.guests}</strong>
                </p>
                <div style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '10px', padding: '0.75rem 1.25rem', maxWidth: '520px', margin: '0 auto' }}>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
                    ⚠️ <strong style={{ color: '#d4af37' }}>Estimate Only</strong> — Prices shown are approximate and based on current vendor rates. For accurate real-time pricing and availability, please <span style={{ color: '#d4af37', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowContactModal(true)}>contact our team</span>.
                  </p>
                </div>
              </div>

              {/* Package Cards */}
              <Row className="g-4 mb-4">
                {optimizedPlan.packages.map((pkg, idx) => (
                  <Col key={pkg.id} lg={4}>
                    <Card 
                      onClick={() => setSelectedPackage(pkg)}
                      style={{ 
                        background: selectedPackage?.id === pkg.id 
                          ? `linear-gradient(135deg, ${pkg.color}20, ${pkg.color}10)` 
                          : 'rgba(255,255,255,0.03)',
                        border: selectedPackage?.id === pkg.id 
                          ? `2px solid ${pkg.color}` 
                          : '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        transition: 'all 300ms',
                        height: '100%'
                      }}
                      className="elevate"
                    >
                      <Card.Body style={{ padding: '1.5rem' }}>
                        {/* Package Header */}
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>{pkg.icon}</div>
                          <h4 style={{ color: '#fff', marginBottom: '0.25rem' }}>{pkg.name}</h4>
                          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginBottom: '1rem' }}>{pkg.tagline}</p>
                          <div style={{ 
                            fontSize: '2rem', 
                            fontWeight: '700', 
                            color: pkg.color,
                            marginBottom: '0.25rem'
                          }}>
                            ${pkg.total.toLocaleString()}
                          </div>
                          {pkg.total <= optimizedPlan.budget ? (
                            <Badge bg="success" style={{ fontSize: '0.7rem' }}>
                              ${pkg.savings.toLocaleString()} under budget
                            </Badge>
                          ) : (
                            <Badge bg="warning" style={{ fontSize: '0.7rem', color: '#000' }}>
                              ${(pkg.total - optimizedPlan.budget).toLocaleString()} over budget
                            </Badge>
                          )}
                        </div>

                        {/* Category Breakdown */}
                        <div style={{ marginBottom: '1rem' }}>
                          {Object.entries(pkg.vendors).map(([cat, vendor]) => (
                            <div key={cat} style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              padding: '0.5rem 0',
                              borderBottom: '1px solid rgba(255,255,255,0.05)'
                            }}>
                              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', textTransform: 'capitalize' }}>
                                {cat}
                              </span>
                              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                                ${pkg.allocations[cat].toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Cultural Add-ons */}
                        {pkg.addons && pkg.addons.length > 0 && (
                          <div style={{ marginBottom: '0.75rem' }}>
                            <div style={{ 
                              color: '#d4af37', 
                              fontSize: '0.75rem', 
                              fontWeight: '600', 
                              marginBottom: '0.35rem',
                              paddingTop: '0.35rem'
                            }}>
                              🌏 Cultural Add-ons
                            </div>
                            {pkg.addons.map(addon => (
                              <div key={addon.id} style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                padding: '0.3rem 0',
                                borderBottom: '1px solid rgba(255,255,255,0.03)'
                              }}>
                                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                                  {addon.name}
                                </span>
                                <span style={{ color: 'rgba(212,175,55,0.7)', fontSize: '0.8rem' }}>
                                  ${addon.totalPrice.toLocaleString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Visual Budget Bar */}
                        <div style={{ marginTop: '1rem' }}>
                          <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                            {Object.entries(pkg.allocations).map(([cat, amount], i) => {
                              const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];
                              const pct = (amount / pkg.total) * 100;
                              return (
                                <div 
                                  key={cat}
                                  style={{ 
                                    width: `${pct}%`, 
                                    background: colors[i % colors.length],
                                    transition: 'width 500ms'
                                  }}
                                  title={`${cat}: ${pct.toFixed(0)}%`}
                                />
                              );
                            })}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                            {Object.entries(pkg.allocations).map(([cat], i) => {
                              const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];
                              return (
                                <span key={cat} style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: colors[i % colors.length] }}></span>
                                  {cat}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* Selected Package Details */}
              {selectedPackage && (
                <div style={{ 
                  background: 'rgba(255,255,255,0.03)', 
                  borderRadius: '20px', 
                  padding: '2rem',
                  border: `1px solid ${selectedPackage.color}30`,
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h4 style={{ color: '#fff', margin: 0 }}>
                      {selectedPackage.icon} {selectedPackage.name} — Detailed Breakdown
                    </h4>
                    <Badge bg="" style={{ background: selectedPackage.color, color: '#fff' }}>
                      ${selectedPackage.total.toLocaleString()}
                    </Badge>
                  </div>

                  <Row className="g-3">
                    {Object.entries(selectedPackage.vendors).map(([category, vendor]) => (
                      <Col key={category} md={6} lg={4}>
                        <div style={{
                          background: 'rgba(255,255,255,0.03)',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          border: '1px solid rgba(255,255,255,0.06)',
                          height: '100%'
                        }}>
                          {/* Vendor Image Preview */}
                          <div style={{
                            height: '120px',
                            background: `linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 100%), url(${vendor.image || '/images/venue.jpeg'})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            position: 'relative'
                          }}>
                            <Badge 
                              bg={vendor.tier === 'prestige' ? 'warning' : vendor.tier === 'signature' ? 'info' : 'secondary'} 
                              style={{ 
                                fontSize: '0.65rem',
                                position: 'absolute',
                                top: '0.75rem',
                                right: '0.75rem'
                              }}
                            >
                              {vendor.tier}
                            </Badge>
                            <div style={{
                              position: 'absolute',
                              bottom: '0.75rem',
                              left: '0.75rem',
                              right: '0.75rem'
                            }}>
                              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                {category}
                              </div>
                              <div style={{ color: '#fff', fontWeight: '600', fontSize: '1rem' }}>
                                {vendor.name}
                              </div>
                            </div>
                          </div>
                          
                          {/* Vendor Details */}
                          <div style={{ padding: '1rem' }}>
                            <div style={{ color: selectedPackage.color, fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.75rem' }}>
                              ${selectedPackage.allocations[category].toLocaleString()}
                            </div>
                            <ul style={{ margin: 0, padding: '0 0 0 1rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                              {vendor.features.slice(0, 3).map((f, i) => (
                                <li key={i} style={{ marginBottom: '0.25rem' }}>{f}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* AI Insights */}
              {optimizedPlan.aiInsights && optimizedPlan.aiInsights.length > 0 && (
                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(139,92,246,0.1))', 
                  borderRadius: '16px', 
                  padding: '1.5rem',
                  border: '1px solid rgba(59,130,246,0.2)',
                  marginBottom: '1.5rem'
                }}>
                  <h5 style={{ color: '#fff', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>🤖</span> AI Insights
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {optimizedPlan.aiInsights.map((insight, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                        <span style={{ 
                          fontSize: '0.9rem',
                          opacity: 0.8
                        }}>
                          {insight.type === 'tip' ? '💡' : insight.type === 'upgrade' ? '⬆️' : insight.type === 'vip' ? '👑' : insight.type === 'savings' ? '💰' : 'ℹ️'}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>{insight.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="text-center" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-light"
                  onClick={() => { 
                    setOptimizerStep(0); 
                    setSelectedPackage(null);
                    setSelectedStartingPackage(null);
                  }}
                  style={{ borderRadius: '30px', padding: '0.75rem 2rem' }}
                >
                  ← Start Over
                </Button>
                {selectedPackage && (
                  <Button
                    onClick={() => {
                      // Save the complete wedding plan to localStorage
                      const weddingPlan = {
                        packageName: selectedPackage.name,
                        packageIcon: selectedPackage.icon,
                        packageColor: selectedPackage.color,
                        budget: optimizedPlan.budget,
                        total: selectedPackage.total,
                        guests: optimizedPlan.guests,
                        createdAt: new Date().toISOString(),
                        vendors: selectedPackage.vendors,
                        allocations: selectedPackage.allocations,
                        aiInsights: optimizedPlan.aiInsights,
                        // Store dream description for future AI visualization
                        dreamDescription: dreamDescription || null,
                        aiSuggestions: aiSuggestions || null,
                        imagePrompt: aiSuggestions?.imagePrompt || null,
                        weddingStyle: aiSuggestions?.weddingStyle || null,
                        // Store chat history for editing
                        optimizerChat: optimizerChat,
                        venuePreference: venuePreference,
                        budgetInput: budgetInput,
                        guestCountInput: guestCountInput,
                        optimizerPriorities: optimizerPriorities,
                        numberOfDays: numberOfDays,
                        weddingTheme: weddingTheme,
                        addons: selectedPackage.addons || [],
                        vendorDatabase: vendorDatabase
                      };
                      localStorage.setItem('weddingPlan', JSON.stringify(weddingPlan));
                      
                      // Also update the main estimator
                      const v = selectedPackage.vendors;
                      setEstimate(prev => ({
                        ...prev,
                        venue: v.venue?.vibe || prev.venue,
                        guests: optimizedPlan.guests,
                        catering: v.catering?.tier === 'prestige' ? 'luxury' : v.catering?.tier === 'signature' ? 'premium' : 'standard',
                        decoration: v.decoration?.tier === 'prestige' ? 'luxury' : v.decoration?.tier === 'signature' ? 'premium' : 'standard',
                        photography: v.photography?.tier === 'prestige' ? 'luxury' : v.photography?.tier === 'signature' ? 'premium' : 'standard',
                        entertainment: v.entertainment?.tier === 'prestige' ? 'luxury' : v.entertainment?.tier === 'signature' ? 'premium' : 'standard'
                      }));
                      
                      setShowBudgetOptimizer(false);
                      setOptimizerStep(1);
                      setSelectedPackage(null);
                      
                      // Navigate to the wedding plan page
                      navigate('/my-wedding-plan');
                    }}
                    style={{
                      background: `linear-gradient(135deg, ${selectedPackage.color} 0%, ${selectedPackage.color}cc 100%)`,
                      border: 'none',
                      borderRadius: '30px',
                      padding: '0.75rem 2rem',
                      fontWeight: '600'
                    }}
                  >
                    ✓ View Full Plan
                  </Button>
                )}
                <Button
                  variant="link"
                  onClick={() => setShowContactModal(true)}
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  💬 Speak to a Planner
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Compare Venues Modal */}
      <Modal show={showCompare} onHide={()=>setShowCompare(false)} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Compare Venues</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            {compareList.slice(0,2).map(v => (
              <Col key={v.id} md={6}>
                <Card className="border-0" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <Card.Img variant="top" src={v.img} alt={v.name} onError={(e)=>{e.currentTarget.style.display='none';}} />
                  <Card.Body>
                    <h5>{v.name}</h5>
                    <div className="text-muted mb-2">{v.vibe} • Capacity {v.capacity} • Availability {v.availability}</div>
                    <div className="mb-2">Pricing band: ${v.basePrice.toLocaleString()}+</div>
                    <div className="mb-2">Best for: {v.labels.join(', ')}</div>
                    <ListGroup variant="flush" className="mb-3">
                      {v.notes.map((n,i)=>(<ListGroup.Item key={i}>{n}</ListGroup.Item>))}
                    </ListGroup>
                    <Button variant="primary" onClick={()=>{ setEstimate(prev=>({ ...prev, venue: v.vibe })); setShowCompare(false); setShowVenueCatalog(false); }}>Choose This Venue</Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setShowCompare(false)}>Close</Button>
          <Button variant="outline-danger" onClick={()=>setCompareList([])}>Clear Selection</Button>
        </Modal.Footer>
      </Modal>

      {/* Packages & Add-ons Modal */}
      <Modal show={showPackages} onHide={()=>setShowPackages(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Packages & Add-ons</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="mb-3">Tiers</h5>
          <ListGroup className="mb-4">
            <ListGroup.Item><strong>Essential</strong> — Core services for intimate celebrations. Includes venue coordination, standard decor, standard photography, and base entertainment.</ListGroup.Item>
            <ListGroup.Item><strong>Signature</strong> — Elevated details and flow. Adds premium decor, premium photography, expanded coordination, and curated vendor options.</ListGroup.Item>
            <ListGroup.Item><strong>Prestige</strong> — Luxury finishes and bespoke touches. Includes luxury decor, cinematic photography, specialty entertainment, and concierge coordination.</ListGroup.Item>
          </ListGroup>
          <h5 className="mb-3">Add-ons</h5>
          <ListGroup className="mb-4">
            <ListGroup.Item>Rain plan tent & backup power</ListGroup.Item>
            <ListGroup.Item>Guest transport (airport/venue shuttles)</ListGroup.Item>
            <ListGroup.Item>Live band or DJ upgrades</ListGroup.Item>
            <ListGroup.Item>Floral styling & personalized décor</ListGroup.Item>
          </ListGroup>
          <h5 className="mb-3">Fee Structure</h5>
          <ListGroup>
            <ListGroup.Item>Planner service fee</ListGroup.Item>
            <ListGroup.Item>Vendor fees</ListGroup.Item>
            <ListGroup.Item>Taxes & service charges</ListGroup.Item>
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setShowPackages(false)}>Close</Button>
          {/* Generate Proposal removed per feedback */}
        </Modal.Footer>
      </Modal>

      {/* Footer */}
      <footer style={{
        background: 'linear-gradient(180deg, #0a0c10 0%, #0f1218 100%)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '3rem 2rem 6rem',
        color: 'rgba(255,255,255,0.5)'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col md={4} className="text-center text-md-start mb-3 mb-md-0">
              <div style={{ 
                fontFamily: "'Playfair Display', serif", 
                fontSize: '1.5rem', 
                color: '#d4af37',
                letterSpacing: '2px'
              }}>
                BALIVIP
              </div>
              <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                Luxury Destination Weddings
              </div>
            </Col>
            <Col md={4} className="text-center mb-3 mb-md-0">
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', fontSize: '0.85rem' }}>
                <a href="/about" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }} 
                   onMouseEnter={(e) => e.target.style.color = '#d4af37'} 
                   onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.5)'}>
                  About
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); setShowContactModal(true); }} 
                   style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}
                   onMouseEnter={(e) => e.target.style.color = '#d4af37'} 
                   onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.5)'}>
                  Contact
                </a>
              </div>
            </Col>
            <Col md={4} className="text-center text-md-end">
              <div style={{ fontSize: '0.8rem' }}>
                © {new Date().getFullYear()} BALIVIP Wedding. All rights reserved.
              </div>
              <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'rgba(255,255,255,0.35)' }}>
                Crafted with ♡ in Bali
              </div>
            </Col>
          </Row>
        </Container>
      </footer>
      
      {/* Floating Sticky CTA Bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(180deg, rgba(20,22,26,0.95) 0%, rgba(10,12,16,0.98) 100%)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        padding: '1rem 2rem',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.75rem',
          color: 'rgba(255,255,255,0.7)',
          fontSize: '0.9rem',
          marginRight: '1rem'
        }}>
          <span style={{ color: '#d4af37' }}>✨</span>
          <span className="d-none d-md-inline">Ready to plan your dream Bali wedding?</span>
        </div>
        <Button
          onClick={() => setShowBudgetOptimizer(true)}
          style={{
            background: 'linear-gradient(135deg, #d4af37 0%, #b8963e 100%)',
            border: 'none',
            color: '#000',
            fontWeight: '600',
            padding: '0.65rem 1.5rem',
            borderRadius: '30px',
            fontSize: '0.9rem'
          }}
        >
          Start Planning
        </Button>
        <Button
          variant="outline-light"
          onClick={() => setShowContactModal(true)}
          style={{
            borderColor: 'rgba(255,255,255,0.25)',
            color: '#fff',
            fontWeight: '500',
            padding: '0.65rem 1.5rem',
            borderRadius: '30px',
            fontSize: '0.9rem'
          }}
        >
          Contact Us
        </Button>
        <Button
          variant="link"
          onClick={() => window.open('https://wa.me/6281337202227', '_blank')}
          style={{
            color: '#25D366',
            padding: '0.65rem 1rem',
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center'
          }}
          title="WhatsApp"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#25D366">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </Button>
      </div>

      {/* Add padding to account for sticky bar */}
      <div style={{ height: '80px' }} />

      <style>{`
        /* Section bands for clear contrast */
  /* Softer, higher-contrast neutral band (less bright than white) */
  .plan-section-soft { background: linear-gradient(180deg, #1a1f27, #202732); color:#e6ecf3; }
  .plan-section-soft .card, .plan-section-soft .card-body, .plan-section-soft .list-group-item { background: transparent; color:#e6ecf3; }
  .plan-section-soft .text-muted { color: #c9d3dc !important; }
        .plan-section-light { background: linear-gradient(180deg, #121212, #1a1a1a); color:#ffffff; }
        .section-header { display:flex; align-items:center; gap:0.5rem; margin-bottom:1rem; }
        .section-header .dot { width:10px; height:10px; border-radius:999px; display:inline-block; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
        .section-header .title { color:#fff; margin:0; font-weight:700; letter-spacing:-0.3px; }
        .section-header .line { flex:1; height:1px; opacity:0.6; border-radius:999px; }
  /* Plan hero subtle entrance */
        .plan-hero { animation: slideUp 700ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        .plan-hero-tile { transition: transform 500ms ease; }
        .plan-hero-tile:hover { transform: translateY(-3px); }
        .plan-hero-title { animation: fadeUp 800ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        .plan-tile { animation: fadeUp 700ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        .plan-caption { animation: fadeUp 700ms cubic-bezier(0.22, 1, 0.36, 1) both; }
  /* Premium typography accents */
  .modal-title { letter-spacing: -0.4px; font-weight: 700; }
  /* Elegant separators and elevated cards */
  .elevate { transition: box-shadow 300ms ease, transform 300ms ease; }
  .elevate:hover { transform: translateY(-2px); box-shadow: 0 16px 40px rgba(0,0,0,0.35); }
        @keyframes headerLoader {
          0% { transform: translateX(-50%); opacity: 0.8; }
          50% { transform: translateX(150%); opacity: 1; }
          100% { transform: translateX(300%); opacity: 0.8; }
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        /* Chip busy pulse animation */
        .chip-busy {
          position: relative;
        }
        .chip-busy::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 999px;
          box-shadow: 0 0 0 0 rgba(59,130,246,0.4);
          animation: chipPulse 1200ms ease-out infinite;
        }
        @keyframes chipPulse {
          0% { box-shadow: 0 0 0 0 rgba(59,130,246,0.0); }
          50% { box-shadow: 0 0 0 6px rgba(59,130,246,0.15); }
          100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.0); }
        }
        /* Steve Jobs-style sleek fade-ins */
        .sj-fade-up {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 900ms cubic-bezier(0.23, 1, 0.32, 1), transform 900ms cubic-bezier(0.23, 1, 0.32, 1);
        }
        .in-view .sj-fade-up,
        .sj-fade-up.in-view {
          opacity: 1;
          transform: translateY(0);
        }
        .sj-stagger > * {
          opacity: 0;
          transform: translateY(18px);
        }
        .sj-stagger.in-view > * {
          opacity: 1;
          transform: translateY(0);
          transition: opacity 700ms cubic-bezier(0.23, 1, 0.32, 1), transform 700ms cubic-bezier(0.23, 1, 0.32, 1);
        }
        .sj-stagger.in-view > *:nth-child(1) { transition-delay: 60ms; }
        .sj-stagger.in-view > *:nth-child(2) { transition-delay: 120ms; }
        .sj-stagger.in-view > *:nth-child(3) { transition-delay: 180ms; }
        .sj-stagger.in-view > *:nth-child(4) { transition-delay: 240ms; }
        .sj-stagger.in-view > *:nth-child(5) { transition-delay: 300ms; }
        .sj-stagger.in-view > *:nth-child(6) { transition-delay: 360ms; }
        .sj-stagger.in-view > *:nth-child(7) { transition-delay: 420ms; }

        @media (prefers-reduced-motion: reduce) {
          .sj-fade-up, .sj-stagger > * { transition: none; transform: none; }
          .gallery-strip { animation: none; }
        }
        /* Testimony card styles */
        .testimony-card {
          transition: transform 400ms cubic-bezier(0.22,1,0.36,1), box-shadow 400ms ease;
        }
        .testimony-card:hover {
          transform: translateY(-6px) scale(1.015);
          box-shadow: 0 20px 50px rgba(0,0,0,0.4);
        }
        .testimony-card:hover .zoom-hint {
          opacity: 1;
        }
        .testimony-card img {
          transition: transform 600ms cubic-bezier(0.22,1,0.36,1), filter 400ms ease;
          filter: brightness(0.92) saturate(1.1);
        }
        .testimony-card:hover img {
          transform: scale(1.05);
          filter: brightness(1) saturate(1.15);
        }
        .testimony-grid {
          padding: 0 0.5rem;
        }
        @media (min-width: 992px) {
          .testimony-grid { grid-template-columns: repeat(4, 1fr); }
        }
        @media (max-width: 575px) {
          .testimony-grid { grid-template-columns: 1fr 1fr; gap: 0.75rem; }
          .testimony-card { aspect-ratio: 3/4; }
        }
        /* Lightbox modal styles */
        .lightbox-modal .modal-dialog {
          max-width: 95vw;
        }
        .lightbox-modal .modal-content,
        .lightbox-content {
          background: rgba(0,0,0,0.95) !important;
          border: none !important;
          border-radius: 16px;
        }
        .lightbox-modal .modal-body {
          padding: 2rem;
        }
        /* Video card styles */
        .video-card {
          transition: transform 400ms cubic-bezier(0.22,1,0.36,1), box-shadow 400ms ease;
        }
        .video-card:hover {
          transform: translateY(-6px) scale(1.02);
          box-shadow: 0 24px 60px rgba(0,0,0,0.5);
        }
        .video-card img {
          transition: transform 600ms cubic-bezier(0.22,1,0.36,1), filter 400ms ease;
          filter: brightness(0.85);
        }
        .video-card:hover img {
          transform: scale(1.05);
          filter: brightness(0.95);
        }
        .video-card:hover .play-overlay {
          background: rgba(0,0,0,0.2);
        }
        .video-card:hover .play-button {
          transform: scale(1.1);
          box-shadow: 0 12px 40px rgba(0,0,0,0.4);
        }
        .video-grid {
          padding: 0 0.5rem;
        }
        @media (min-width: 992px) {
          .video-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (min-width: 576px) and (max-width: 991px) {
          .video-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 575px) {
          .video-grid { grid-template-columns: 1fr; }
        }
        /* Video modal styles */
        .video-modal .modal-dialog {
          max-width: 90vw;
        }
        .video-modal .modal-content,
        .video-modal-content {
          background: transparent !important;
          border: none !important;
        }
        .video-modal .modal-body {
          padding: 0;
        }
        /* Gallery visuals */
        .gallery-wrapper { overflow: hidden; }
        .gallery-strip { white-space: nowrap; will-change: transform; animation: galleryScroll 28s linear infinite; }
        .gallery-strip.reverse { animation-direction: reverse; }
        .gallery-item { position: relative; width: 240px; height: 160px; border-radius: 14px; overflow: hidden; border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.06); }
        .gallery-item img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 600ms cubic-bezier(0.22, 1, 0.36, 1), filter 600ms ease; filter: saturate(1.05) contrast(1.05); }
        .gallery-item:hover img { transform: scale(1.06); filter: saturate(1.15) contrast(1.08); }
        @keyframes galleryScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }

        .gallery-card { position: relative; border-radius: 16px; overflow: hidden; border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.06); }
        .gallery-card img { width: 100%; height: 100%; object-fit: cover; display: block; aspect-ratio: 4/3; transition: transform 500ms ease; }
        .gallery-card:hover img { transform: scale(1.03); }

        .fade-tile { opacity: 0; transform: translateY(20px); animation: fadeUp 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        .fade-tile:nth-child(1) { animation-delay: 0.1s; }
        .fade-tile:nth-child(2) { animation-delay: 0.2s; }
        .fade-tile:nth-child(3) { animation-delay: 0.3s; }
        .fade-tile:nth-child(4) { animation-delay: 0.4s; }
        .fade-tile:nth-child(5) { animation-delay: 0.5s; }
        .fade-tile:nth-child(6) { animation-delay: 0.6s; }
        @keyframes fadeUp { to { opacity: 1; transform: translateY(0); } }
        /* Apple-like smooth section reveal */
        .section-fade-up {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 800ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .section-fade-up.in-view {
          opacity: 1;
          transform: translateY(0);
        }

          /* When a reveal group enters view, animate its children with stagger */
          .reveal-group.in-view .item-reveal {
            opacity: 1;
            animation-play-state: running;
          }
          .item-reveal {
            opacity: 0;
            animation-play-state: paused;
          }

        /* Card hover elevate */
        .elevate:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 40px rgba(0,0,0,0.35);
          transition: transform 300ms ease, box-shadow 300ms ease;
        }

        /* Subtle opacity & scale for mood tiles */
        .mood-tile {
          transition: transform 300ms ease, opacity 300ms ease;
        }
        .mood-tile:hover {
          transform: scale(1.03);
          opacity: 0.95;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes messageSlide {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.5;
          }
        }
        
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.1);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
        }
      `}</style>
    </>
  );
};

export default InteractivePlanner;
